import React, { useState, useEffect, useRef } from 'react';
import { CalendarDays, X, CheckCircle2, FileEdit, RefreshCw, Lock, Unlock } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useParams, useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import TrDateInput from '../components/TrDateInput';
import AciklamaTurButonlari from '../components/AciklamaTurButonlari';
import { useTranslation } from 'react-i18next';
import { getAktifDil } from '../i18n/i18n';

interface ImalatKategoriSD {
  imalatKatID: number;
  kategoriAdi: string;
  alanTipi: 'SerbestMetin' | 'TekSecim' | 'CokSecim' | 'Sayi' | 'Tarih';
  secenekler?: string;
  aciklama?: string;
  siraNo: number;
}

interface ImalatNotuSD {
  imalatNotID: number;
  imalatKatID: number;
  deger: string;
  aciklama?: string;
  ekleyenAdi?: string;
  eklenmeTarihi: string;
}

interface SozlesmeDetay {
  sozlesmeID: number;
  sozlesmeTakipNo: string;
  sozlesmeTarihi: string;
  rezerveBitisTarihi?: string;
  sozlesmeDurumu: string;
  sozlesmeFiyati: number;
  listeFiyati?: number;
  planlananOdeme: number;
  ortalamaVade?: number;
  ozelSartlar?: string;
  aktifMi: boolean;
  eklemeTarihi: string;
  cari?: {
    cariID: number;
    takipNo: string;
    adiSoyadi: string;
    firmaAdi?: string;
    telefon: string;
    telefon2?: string;
    email?: string;
    adres?: string;
    tc?: string;
    cariVD?: string;
    cariVN?: string;
    turu: string;
  };
  daire?: {
    daireID: number;
    daireNo: string;
    takipNo: string;
    netM2?: number;
    brutM2?: number;
    listeFiyati?: number;
    aciklama?: string;
    blok?: {
      blokID: number;
      blokAdi: string;
      proje?: {
        projeID: number;
        projeAdi: string;
        takipNo?: string;
      };
    };
    kat?: {
      katID: number;
      katAdi: string;
    };
    cephe?: {
      cepheID: number;
      cepheAdi: string;
    };
    manzara?: {
      manzaraID: number;
      manzaraAdi: string;
    };
    daireTip?: {
      daireTipID: number;
      tip: string;
    };
  };
}

interface SunumBelge {
  sunumBelgeId: number;
  takipNo: string;
  aciklama: string;
  sunumBelgeTuru: string;
  sunumBelgeAdi: string;
}

const API_BASE_URL = (axiosInstance.defaults.baseURL ?? '').replace(/\/$/, '');

interface Sablon {
  dosyaID: number;
  dosyaAdi: string;
  takipNo: string;
  eklenmeTarihi: string;
  aciklama: string;
  dosyaTuru: number;
  aktifMi: boolean;
}

interface Odeme {
  odemeID: number;
  cariID: number;
  islemTarihi: string;
  vadeTarihi?: string;
  tahsilatTarihi?: string;
  miktar: number;
  paraBirimiID: number;
  islemTakipNo: string;
  odemeTuru: string;
  odemeYonu: string;
  odemeDurumu: string;
  aciklama?: string;
  aktifMi: boolean;
  sozlesmeOdemesi: boolean;
  eklemeTarihi: string;
  kasaID?: number;
  paraBirimi: {
    paraBirimiID: number;
    birim: string;
    carpan: number;
  };
  kasa?: {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string;
  };
}

interface OdemePlanTaksiti {
  taksitID: number;
  sozlesmeID: number;
  cariID: number;
  vadeTarihi: string;
  miktar: number;
  odemeTuruDetay?: string;
  odemeTuru?: string;
  odemeDurumu: string; // 'Bekliyor' | 'Odendi' | 'Iptal'
  aciklama?: string;
  siraNo: number;
  gercekOdemeID?: number;
  tahsilatTarihi?: string;
  kasaID?: number;
  kasaAdi?: string;
  aktifMi: boolean;
}

interface HazirPlanSablonDetay {
  detayId: string;
  odemeTuru: string;
  vadeAyEndeksi: number;
  hesaplamaYontemi: string;
  deger: number;
}

interface HazirPlanSablon {
  sablonId: string;
  sablonAdi: string;
  pesinatTipi: string;
  pesinatDegeri: number;
  vadeTipi: string;
  maksimumTaksitSayisi: number;
  kalanBakiyeDagilimTipi: string;
  kurusYuvarlamaTipi: string;
  aciklama?: string;
  isActive: boolean;
  detaylar: HazirPlanSablonDetay[];
}

interface HazirPlanRow {
  odemeTuru: string;
  vadeTarihiStr: string;
  miktar: number;
  aciklama: string;
  kilitli?: boolean;
}

const HAZIR_PLAN_ODEME_TURU_LABEL: Record<string, string> = {
  Pesinat: 'Peşinat', Taksit: 'Taksit', AraOdeme: 'Ara Ödeme', Teslimat: 'Teslimat',
};

const toTRDate = (iso: string): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};
const fromTRDate = (tr: string): string => {
  if (!tr || tr.length < 10) return '';
  const [d, m, y] = tr.split('.');
  return `${y}-${m}-${d}`;
};
const maskTRDate = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
};

// --- Makbuz (tahsilat/ödeme) yazdırma tasarımı — lacivert/turuncu kurumsal renk şeması (Teklif PDF ile tutarlı) ---
const MAKBUZ_RENK_LACIVERT = '#1a2440';
const MAKBUZ_RENK_TURUNCU = '#e67d21';
const MAKBUZ_ICON_CALENDAR = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>';
const MAKBUZ_ICON_COINS = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="9" r="6.5"/><path d="M14.5 9a6.5 6.5 0 1 1 0 9.9" fill="none"/><circle cx="9" cy="9" r="2.2" fill="currentColor" stroke="none" opacity="0.35"/></svg>';
const MAKBUZ_ICON_WALLET = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/><path d="M2 9V6a2 2 0 0 1 2-2h13"/><path d="M21 13h-4a1.5 1.5 0 0 0 0 3h4a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1Z"/></svg>';
const MAKBUZ_ICON_PERSON = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/></svg>';
const MAKBUZ_ICON_DOC = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>';

const MAKBUZ_CSS = `
  @page { size: A5 landscape; margin: 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11pt; color: ${MAKBUZ_RENK_LACIVERT}; background: #fff; }
  .makbuz { width: 100%; }

  .header { display: grid; grid-template-columns: 1fr 1.1fr 1fr; align-items: start; gap: 10px; padding-bottom: 10px; margin-bottom: 14px; border-bottom: 1px solid #e2e8f0; }
  .header-left .firma-adi { font-size: 14pt; font-weight: 800; color: ${MAKBUZ_RENK_LACIVERT}; letter-spacing: -0.3px; }
  .header-left .firma-alt { font-size: 8pt; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.6px; margin-top: 3px; }
  .meta-line { display: flex; align-items: center; gap: 5px; font-size: 7.5pt; color: #94a3b8; margin-top: 8px; }
  .meta-line svg { flex-shrink: 0; }

  .header-center { text-align: center; }
  .header-center .title { font-size: 12.5pt; font-weight: 800; color: ${MAKBUZ_RENK_LACIVERT}; letter-spacing: 0.3px; text-transform: uppercase; }
  .header-center .title-underline { width: 40px; height: 3px; background: ${MAKBUZ_RENK_TURUNCU}; border-radius: 2px; margin: 6px auto; }
  .header-center .title-sub { font-size: 8pt; color: #94a3b8; font-weight: 600; }

  .header-right { text-align: right; }
  .header-right .label { font-size: 7.5pt; color: #94a3b8; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
  .header-right .value { font-size: 12pt; font-weight: 800; color: ${MAKBUZ_RENK_LACIVERT}; margin-top: 2px; }
  .header-right .meta-line { justify-content: flex-end; }

  .tutar-card { display: flex; align-items: center; justify-content: space-between; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 18px; margin-bottom: 14px; }
  .tutar-left, .tutar-right { display: flex; align-items: center; gap: 12px; }
  .icon-circle { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .icon-circle.orange { background: #fdecdc; color: ${MAKBUZ_RENK_TURUNCU}; }
  .icon-circle.gray { background: #eef1f5; color: #64748b; }
  .tutar-left .lbl { font-size: 8pt; color: #64748b; font-weight: 600; }
  .tutar-left .tutar { font-size: 19pt; font-weight: 900; color: ${MAKBUZ_RENK_LACIVERT}; letter-spacing: -0.5px; margin-top: 2px; }
  .tutar-right { text-align: right; }
  .tutar-right .lbl { font-size: 8pt; color: #64748b; font-weight: 600; }
  .tutar-right .odeme-turu { font-size: 12.5pt; font-weight: 800; color: ${MAKBUZ_RENK_LACIVERT}; margin-top: 2px; }
  .tutar-right .vade { font-size: 8pt; color: #94a3b8; margin-top: 2px; }

  .body-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 14px; }
  .info-block { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; }
  .info-block .block-title { display: flex; align-items: center; gap: 6px; font-size: 8pt; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px; color: ${MAKBUZ_RENK_LACIVERT}; }
  .info-block .icon-sm { width: 20px; height: 20px; border-radius: 50%; background: #fdecdc; color: ${MAKBUZ_RENK_TURUNCU}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .info-block .title-underline { width: 26px; height: 2px; background: ${MAKBUZ_RENK_TURUNCU}; border-radius: 2px; margin: 6px 0 8px; }
  .info-row { display: flex; gap: 6px; margin-bottom: 4px; font-size: 9pt; }
  .info-row .lbl { color: #64748b; min-width: 92px; flex-shrink: 0; }
  .info-row .sep { color: #cbd5e1; }
  .info-row .val { color: ${MAKBUZ_RENK_LACIVERT}; font-weight: 600; }

  .imza-row { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 6px; }
  .imza-box { display: flex; flex-direction: column; align-items: center; gap: 6px; }
  .imza-line { width: 100%; height: 26px; border-bottom: 1px solid #cbd5e1; }
  .imza-label { font-size: 7.5pt; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }

  .not { font-size: 7.5pt; color: #94a3b8; text-align: center; border-top: 1px dashed #e2e8f0; padding-top: 8px; margin-top: 10px; }

  @media print { .no-print { display: none !important; } }
  .print-btn { position: fixed; top: 16px; right: 16px; padding: 8px 20px; background: ${MAKBUZ_RENK_TURUNCU}; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
`;

const makbuzHeaderHtml = (p: { firmaAdi?: string; baslik: string; makbuzNo: string; olusturmaTarihSaat: string; tarih: string }): string => `
  <div class="header">
    <div class="header-left">
      <div class="firma-adi">${p.firmaAdi || 'YapiCRM'}</div>
      <div class="firma-alt">${p.baslik}</div>
      <div class="meta-line">${MAKBUZ_ICON_CALENDAR}<span>${p.olusturmaTarihSaat}</span></div>
    </div>
    <div class="header-center">
      <div class="title">${p.baslik}</div>
      <div class="title-underline"></div>
      <div class="title-sub">${p.makbuzNo}</div>
    </div>
    <div class="header-right">
      <div class="label">Makbuz No</div>
      <div class="value">${p.makbuzNo}</div>
      <div class="meta-line">${MAKBUZ_ICON_CALENDAR}<span>${p.tarih}</span></div>
    </div>
  </div>
`;

const SozlesmeDetay: React.FC = () => {
  const { takipNo } = useParams<{ takipNo: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sozlesme, setSozlesme] = useState<SozlesmeDetay | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showBelgelerModal, setShowBelgelerModal] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<{url: string, type: string, name: string} | null>(null);
  const [sablonlar, setSablonlar] = useState<Sablon[]>([]);
  const [belgelerLoading, setBelgelerLoading] = useState<boolean>(false);
  const [odemeler, setOdemeler] = useState<Odeme[]>([]);
  const [taksitler, setTaksitler] = useState<OdemePlanTaksiti[]>([]);
  const [taksitlerLoading, setTaksitlerLoading] = useState<boolean>(false);
  const [odemelerLoading, setOdemelerLoading] = useState<boolean>(false);
  const [showOdemeModal, setShowOdemeModal] = useState<boolean>(false);
  const [showTaksitEkleModal, setShowTaksitEkleModal] = useState<boolean>(false);
  const [taksitEkleFormData, setTaksitEkleFormData] = useState({ vadeTarihi: '', odemeTuru: 'Nakit', miktar: '', aciklama: '' });
  const [taksitEkleSaving, setTaksitEkleSaving] = useState<boolean>(false);
  const [showTahsilatIslemlerMenu, setShowTahsilatIslemlerMenu] = useState<boolean>(false);
  const [showVadePerformansModal, setShowVadePerformansModal] = useState<boolean>(false);
  const [showTopluPlanlaModal, setShowTopluPlanlaModal] = useState<boolean>(false);
  const [showEditOdemeModal, setShowEditOdemeModal] = useState<boolean>(false);
  const [editingOdeme, setEditingOdeme] = useState<Odeme | null>(null);
  const [kasalar, setKasalar] = useState<any[]>([]);
  const [odemeFormData, setOdemeFormData] = useState({
    miktar: '',
    islemTarihi: '',
    vadeTarihi: '',
    odemeTuru: '',
    odemeDurumu: '',
    kasaID: '',
    aciklama: ''
  });
  const [editOdemeFormData, setEditOdemeFormData] = useState({
    miktar: '',
    vadeTarihi: '',
    tahsilatTarihi: '',
    odemeDurumu: '',
    odemeTuru: '',
    kasaID: '',
    aciklama: ''
  });
  const [topluPlanlaFormData, setTopluPlanlaFormData] = useState({
    taksitlendirilecekTutar: '',
    baslamaTarihi: '',
    taksitSayisi: '',
    odemeTuru: '',
    aciklama: ''
  });
  const [topluPlanlaRows, setTopluPlanlaRows] = useState<HazirPlanRow[]>([]);
  const [topluPlanlaTemizleMevcut, setTopluPlanlaTemizleMevcut] = useState(false);
  const [baslamaTarihiDisplay, setBaslamaTarihiDisplay] = useState('');
  const [saving, setSaving] = useState<boolean>(false);
  const [showIptalModal, setShowIptalModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [updateFormData, setUpdateFormData] = useState({
    sozlesmeDurumu: '',
    sozlesmeTarihi: '',
    rezerveBitisTarihi: '',
    sozlesmeFiyati: '',
    ozelSartlar: ''
  });
  const [updateSozlesmeTarihiDisplay, setUpdateSozlesmeTarihiDisplay] = useState('');
  const [updateRezerveTarihiDisplay, setUpdateRezerveTarihiDisplay] = useState('');
  const [showSozlesmeEditModal, setShowSozlesmeEditModal] = useState<boolean>(false);
  const [showActionsMenu, setShowActionsMenu] = useState<boolean>(false);
  const [showHazirSozlesme, setShowHazirSozlesme] = useState<boolean>(false);
  const [hazirSozlesmeKurumsal, setHazirSozlesmeKurumsal] = useState<any>(null);
  const [hazirSozlesmeLoading, setHazirSozlesmeLoading] = useState<boolean>(false);
  const hazirSozlesmeRef = useRef<HTMLDivElement>(null);
  const [showTeklif, setShowTeklif] = useState<boolean>(false);
  const [teklifKurumsal, setTeklifKurumsal] = useState<any>(null);
  const [teklifLogoBase64, setTeklifLogoBase64] = useState<string>('');
  const teklifRef = useRef<HTMLDivElement>(null);
  const [daireOzellikler, setDaireOzellikler] = useState<Array<{ozellik: string; deger: string}>>([]);
  const [showDaireDetayModal, setShowDaireDetayModal] = useState<boolean>(false);
  const [projeOzellikler, setProjeOzellikler] = useState<Array<{ozellik: string; deger: string}>>([]);
  const [galeriGorseller, setGaleriGorseller] = useState<SunumBelge[]>([]);
  const [galeriBelgeler, setGaleriBelgeler] = useState<SunumBelge[]>([]);
  const [galeriVideolar, setGaleriVideolar] = useState<SunumBelge[]>([]);
  const [galeriSlideIndex, setGaleriSlideIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState<SunumBelge | null>(null);
  const [sozlesmeEditFormData, setSozlesmeEditFormData] = useState({
    sozlesmeDurumu: '',
    sozlesmeTarihi: '',
    rezerveBitisTarihi: '',
    sozlesmeFiyati: '',
    ozelSartlar: ''
  });
  const [dokumanSablonlari, setDokumanSablonlari] = useState<any[]>([]);
  const [showTaksitDetayModal, setShowTaksitDetayModal] = useState<boolean>(false);
  const [selectedTaksitForDetay, setSelectedTaksitForDetay] = useState<OdemePlanTaksiti | null>(null);
  const [taksitDetayForm, setTaksitDetayForm] = useState({ miktar: '', miktarDisplay: '', vadeTarihi: '', odemeDurumu: 'Bekliyor', aciklama: '', tahsilatTarihi: '', kasaID: '', odemeTuru: 'Nakit' });
  const [taksitDetaySaving, setTaksitDetaySaving] = useState<boolean>(false);
  const [showSablonModal, setShowSablonModal] = useState(false);
  const [showOdemePlaniRapor, setShowOdemePlaniRapor] = useState(false);
  const [selectedOdemeler, setSelectedOdemeler] = useState<Set<number>>(new Set());
  const [topluIptalLoading, setTopluIptalLoading] = useState(false);
  const [showTahsilatYapModal, setShowTahsilatYapModal] = useState<boolean>(false);
  const [tahsilatYapTaksit, setTahsilatYapTaksit] = useState<OdemePlanTaksiti | null>(null);
  const [tahsilatYapFormData, setTahsilatYapFormData] = useState({ tarih: '', kasaID: '', odemeTuru: 'Nakit', aciklama: '' });

  // Hazır Ödeme Planı modal state
  const [showHazirPlanModal, setShowHazirPlanModal] = useState(false);
  const [hazirPlanSablonlar, setHazirPlanSablonlar] = useState<HazirPlanSablon[]>([]);
  const [hazirPlanLoading, setHazirPlanLoading] = useState(false);
  const [selectedHazirSablon, setSelectedHazirSablon] = useState<HazirPlanSablon | null>(null);
  const [hazirPlanMiktar, setHazirPlanMiktar] = useState('');
  const [hazirPlanRows, setHazirPlanRows] = useState<HazirPlanRow[]>([]);
  const [hazirPlanOdemeTuru, setHazirPlanOdemeTuru] = useState('Nakit');
  const [hazirPlanSaving, setHazirPlanSaving] = useState(false);
  const [hazirPlanTemizleMevcut, setHazirPlanTemizleMevcut] = useState(false);

  // İmalat Notları modal state
  const [showImalatNotlariModal, setShowImalatNotlariModal] = useState(false);
  const [showHareketlerModal, setShowHareketlerModal] = useState(false);
  const [hareketler, setHareketler] = useState<any[]>([]);
  const [hareketlerLoading, setHareketlerLoading] = useState(false);
  const [imalatKategorilerSD, setImalatKategorilerSD] = useState<ImalatKategoriSD[]>([]);
  const [imalatNotlariSD, setImalatNotlariSD] = useState<ImalatNotuSD[]>([]);
  const [imalatNotlariLoading, setImalatNotlariLoading] = useState(false);
  const [imalatNotForm, setImalatNotForm] = useState<{ katID: number; deger: string; secimler: string[]; aciklama: string } | null>(null);
  const [imalatNotEditID, setImalatNotEditID] = useState<number | null>(null);

  useEffect(() => {
    if (takipNo) {
      fetchSozlesmeDetay();
      fetchOdemeler();
      fetchKasalar();
      fetchDokumanSablonlari();
    }
  }, [takipNo]);

  useEffect(() => {
    if (sozlesme?.sozlesmeID) {
      fetchTaksitler(sozlesme.sozlesmeID);
    }
  }, [sozlesme?.sozlesmeID]);

  useEffect(() => {
    if (sozlesme?.daire?.takipNo) {
      axiosInstance.get(`/api/DaireOzellikler/daire/${sozlesme.daire.takipNo}`)
        .then(res => setDaireOzellikler(res.data))
        .catch(() => setDaireOzellikler([]));
    }
  }, [sozlesme?.daire?.takipNo]);

  useEffect(() => {
    if (sozlesme?.daire?.blok?.proje?.takipNo) {
      axiosInstance.get(`/api/ProjeOzellikler/proje/${sozlesme.daire.blok.proje.takipNo}`)
        .then(res => setProjeOzellikler(res.data))
        .catch(() => setProjeOzellikler([]));
    }
  }, [sozlesme?.daire?.blok?.proje?.takipNo]);

  useEffect(() => {
    const daireTakip = sozlesme?.daire?.takipNo;
    const projeTakip = sozlesme?.daire?.blok?.proje?.takipNo;
    if (!daireTakip) return;
    axiosInstance.get(`/api/SunumBelgeleri/takip/${daireTakip}`)
      .then(res => {
        const daireBelgeler: SunumBelge[] = res.data || [];
        if (projeTakip) {
          axiosInstance.get(`/api/SunumBelgeleri/takip/${projeTakip}`)
            .then(res2 => {
              const all = [...daireBelgeler, ...(res2.data || [])];
              setGaleriGorseller(all.filter(b => b.sunumBelgeTuru === 'Görsel'));
              setGaleriBelgeler(all.filter(b => b.sunumBelgeTuru === 'Belge'));
              setGaleriVideolar(all.filter(b => b.sunumBelgeTuru === 'Video'));
              setGaleriSlideIndex(0);
            }).catch(() => {
              setGaleriGorseller(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel'));
              setGaleriBelgeler(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Belge'));
              setGaleriVideolar(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Video'));
              setGaleriSlideIndex(0);
            });
        } else {
          setGaleriGorseller(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel'));
          setGaleriBelgeler(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Belge'));
          setGaleriVideolar(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Video'));
          setGaleriSlideIndex(0);
        }
      }).catch(() => {
        setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]);
      });
  }, [sozlesme?.daire?.takipNo, sozlesme?.daire?.blok?.proje?.takipNo]);

  const fetchKasalar = async () => {
    try {
      const response = await axiosInstance.get('/api/Kasalar');
      setKasalar(response.data);
    } catch (error) {
      console.error('Kasalar yüklenirken hata oluştu:', error);
    }
  };

  const fetchDokumanSablonlari = async () => {
    try {
      const response = await axiosInstance.get('/api/DokumanSablonlari');
      setDokumanSablonlari(response.data.filter((s: any) => s.aktifMi));
    } catch {}
  };

  const handleSablonSec = (sablon: any) => {
    setUpdateFormData(prev => ({ ...prev, ozelSartlar: sablon.icerik || '' }));
    setShowSablonModal(false);
    toast.success(t('msg.islemBasarili'));
  };

  const fetchSozlesmeDetay = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/Sozlesmeler/takip/${takipNo}`);
      setSozlesme(response.data);
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      console.error('Sözleşme detay yükleme hatası:', error);
      navigate('/sozlesmeler');
    } finally {
      setLoading(false);
    }
  };

  const fetchOdemeler = async () => {
    try {
      setOdemelerLoading(true);
      // Aynı sözleşme takip numarasına ait tüm ödemeleri getir
      const response = await axiosInstance.get(`/api/Odemeler?aktifMi=true`);
      // Sözleşme takip numarasına göre filtrele ve vade tarihine göre sırala
      const filteredOdemeler = response.data
        .filter((odeme: any) => odeme.islemTakipNo === takipNo)
        .sort((a: any, b: any) => {
          // Vade tarihi olmayanları sona at
          if (!a.vadeTarihi && !b.vadeTarihi) return 0;
          if (!a.vadeTarihi) return 1;
          if (!b.vadeTarihi) return -1;
          
          // Vade tarihine göre artan sıralama (en eski vade önce)
          return new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime();
        });
      setOdemeler(filteredOdemeler);
    } catch (error) {
      console.error('Ödemeler yüklenirken hata:', error);
      setOdemeler([]);
    } finally {
      setOdemelerLoading(false);
    }
  };

  const fetchTaksitler = async (sozlesmeID: number) => {
    try {
      setTaksitlerLoading(true);
      const res = await axiosInstance.get(`/api/OdemePlanTaksitleri?sozlesmeID=${sozlesmeID}`);
      setTaksitler(res.data);
    } catch (error) {
      console.error('Taksitler yüklenirken hata:', error);
      setTaksitler([]);
    } finally {
      setTaksitlerLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' ₺';
  };

  // Toplam ödeme tutarını hesapla
  const calculateTotalPayments = () => {
    return odemeler.reduce((total, odeme) => {
      // Sadece tahsilat türündeki ödemeleri topla
      if (odeme.odemeYonu === 'Tahsilat') {
        return total + odeme.miktar;
      }
      return total - odeme.miktar; // Ödeme türündekiler çıkarılır
    }, 0);
  };

  // Kalan ödeme tutarını hesapla
  const calculateRemainingAmount = () => {
    const odenenTutar = calculateOdenenTutar();
    return (sozlesme?.sozlesmeFiyati || 0) - odenenTutar;
  };

  // Ortalama vade hesapla (sözleşme tarihinden itibaren ağırlıklı ortalama gün sayısı)
  // Tüm aktif taksitler (iptal edilmeyenler) dahil edilir
  const calculateOrtalamaVade = () => {
    if (!sozlesme?.sozlesmeTarihi || taksitler.length === 0) return 0;

    const sozlesmeTarihi = new Date(sozlesme.sozlesmeTarihi);
    let toplamAgirlikliGun = 0;
    let toplamTutar = 0;

    taksitler
      .filter(item => item.odemeDurumu !== 'Iptal' && item.vadeTarihi)
      .forEach(taksit => {
        const vadeTarihi = new Date(taksit.vadeTarihi);
        const gunFarki = Math.floor((vadeTarihi.getTime() - sozlesmeTarihi.getTime()) / (1000 * 60 * 60 * 24));
        toplamAgirlikliGun += gunFarki * taksit.miktar;
        toplamTutar += taksit.miktar;
      });

    return toplamTutar > 0 ? Math.round(toplamAgirlikliGun / toplamTutar) : 0;
  };

  // İşlemler sayfasından direkt girilen tahsilatlar (OdemePlanTaksitleri dışı)
  const getDirectTahsilatlar = () => {
    const gercekOdemeIDs = new Set(taksitler.map(item => item.gercekOdemeID).filter(Boolean));
    return odemeler.filter(
      o => o.sozlesmeOdemesi && o.odemeYonu === 'Tahsilat' && o.odemeDurumu === 'Odendi' && !gercekOdemeIDs.has(o.odemeID)
    );
  };

  // Ödendi durumundaki ödeme tutarını hesapla (taksitler + direkt tahsilatlar)
  const calculateOdenenTutar = () => {
    const taksitOdenen = taksitler
      .filter(item => item.odemeDurumu === 'Odendi')
      .reduce((total, t) => total + t.miktar, 0);
    const direktToplam = getDirectTahsilatlar().reduce((total, o) => total + o.miktar, 0);
    return taksitOdenen + direktToplam;
  };

  // Bekliyor durumundaki ödeme tutarını hesapla
  const calculateBekleyenTutar = () => {
    return taksitler
      .filter(item => item.odemeDurumu === 'Bekliyor')
      .reduce((total, t) => total + t.miktar, 0);
  };

  // Planlanmamış tutar: sözleşme fiyatı - tüm aktif taksitler - direkt tahsilatlar
  const calculatePlanlanmamisTutar = () => {
    const planliToplamTutar = calculatePlanlananTutar();
    return (sozlesme?.sozlesmeFiyati || 0) - planliToplamTutar;
  };

  // Tahsilatlar bölümünde görünen tüm kalemlerin (iptal edilmeyen taksitler + doğrudan
  // girilen tahsilatlar) toplamı — sözleşme için fiilen planlanmış tutar.
  const calculatePlanlananTutar = () => {
    const planliToplamTutar = taksitler
      .filter(item => item.odemeDurumu !== 'Iptal')
      .reduce((total, t) => total + t.miktar, 0);
    const direktToplam = getDirectTahsilatlar().reduce((total, o) => total + o.miktar, 0);
    return planliToplamTutar + direktToplam;
  };

  // Ödeme planını yazdırma ekranına gönder
  const generatePaymentPlanPDF = () => {
    if (!sozlesme) return;

    // Taksitleri filtrele ve sırala (iptal edilmeyenler = hem ödendi hem bekliyor)
    const aktifOdemeler = taksitler
      .filter(item => item.odemeDurumu !== 'Iptal')
      .sort((a, b) => (a.vadeTarihi ? new Date(a.vadeTarihi).getTime() : 0) - (b.vadeTarihi ? new Date(b.vadeTarihi).getTime() : 0));
    const direktTahsilatlarPDF = getDirectTahsilatlar();
    const direktTahsilatlarPDFRows = direktTahsilatlarPDF.map(o => {
      const tarih = o.tahsilatTarihi
        ? new Date(o.tahsilatTarihi).toLocaleDateString(getAktifDil() || 'tr')
        : o.islemTarihi ? new Date(o.islemTarihi).toLocaleDateString(getAktifDil() || 'tr') : '-';
      return '<tr>'
        + '<td class="text-center">' + tarih + '</td>'
        + '<td class="text-right">' + o.miktar.toLocaleString(getAktifDil() || 'tr') + ' ₺</td>'
        + '<td class="text-center">' + (o.odemeTuru || '-') + '</td>'
        + '<td class="text-center" style="color:#15803d;font-weight:bold">' + t('durum.odendi', 'Ödendi') + '</td>'
        + '<td>' + (o.aciklama || '-') + '</td>'
        + '</tr>';
    }).join('');

    const odenenToplam = aktifOdemeler.filter(item => item.odemeDurumu === 'Odendi').reduce((s, t) => s + t.miktar, 0)
      + direktTahsilatlarPDF.reduce((s, o) => s + o.miktar, 0);
    const bekleyenToplam = aktifOdemeler.filter(item => item.odemeDurumu === 'Bekliyor').reduce((s, t) => s + t.miktar, 0);
    const kalanOdeme = sozlesme.sozlesmeFiyati - odenenToplam;

    // HTML içeriği oluştur
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${t('sozlesme.odemePlani')} - ${sozlesme.cari?.adiSoyadi}</title>
        <style>
          @media print {
            @page { 
              size: A4; 
              margin: 1cm; 
            }
            body { 
              margin: 0; 
              font-family: Arial, sans-serif;
              font-size: 11pt;
            }
            .no-print { display: none; }
          }
          
          body {
            font-family: Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #333;
          }
          
          .header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 2px solid #333;
          }
          
          .header h1 {
            font-size: 20pt;
            margin: 0 0 10px 0;
            font-weight: bold;
          }
          
          .two-column-layout {
            display: flex;
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .column {
            flex: 1;
          }
          
          .info-section {
            margin-bottom: 20px;
          }
          
          .info-section h2 {
            font-size: 14pt;
            font-weight: bold;
            margin: 0 0 10px 0;
            padding: 5px 0;
            border-bottom: 1px solid #666;
          }
          
          .info-row {
            margin: 5px 0;
          }
          
          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 120px;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          
          th {
            background-color: #333;
            color: white;
            padding: 8px;
            text-align: left;
            font-weight: bold;
          }
          
          td {
            padding: 8px;
            border-bottom: 1px solid #ddd;
          }
          
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          
          .text-right {
            text-align: right;
          }
          
          .text-center {
            text-align: center;
          }
          
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 10px 20px;
            background-color: #007bff;
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 14pt;
            z-index: 1000;
          }
          
          .print-button:hover {
            background-color: #0056b3;
          }
          
          @media screen {
            body {
              max-width: 21cm;
              margin: 1cm auto;
              padding: 20px;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
            }
          }
        </style>
      </head>
      <body>
        <button class="print-button no-print" onclick="window.print()">
          <i class="fas fa-print"></i> ${t('btn.yazdir')}
        </button>
        
        <div class="header">
          <h1>${t('sozlesme.odemePlani', 'Ödeme Planı')}</h1>
          <p>${t('rapor.tarih', 'Rapor Tarihi')}: ${new Date().toLocaleDateString(getAktifDil() || 'tr')}</p>
        </div>

        <div class="two-column-layout">
          <div class="column">
            <div class="info-section">
              <h2>${t('musteri.musteriTab', 'Müşteri')}</h2>
              <div class="info-row">
                <span class="info-label">${t('label.musteri', 'Müşteri')}:</span>
                <span>${sozlesme.cari?.adiSoyadi || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">${t('label.telefon', 'Telefon')}:</span>
                <span>${sozlesme.cari?.telefon || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">${t('label.eposta', 'E-Posta')}:</span>
                <span>${sozlesme.cari?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          <div class="column">
            <div class="info-section">
              <h2>${t('sozlesmeDetay.daireBilgileri', 'Daire Bilgileri')}</h2>
              <div class="info-row">
                <span class="info-label">${t('label.proje', 'Proje')}:</span>
                <span>${sozlesme.daire?.blok?.proje?.projeAdi || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">${t('label.blok', 'Blok')}:</span>
                <span>${sozlesme.daire?.blok?.blokAdi || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">${t('label.daire', 'Daire')}:</span>
                <span>${sozlesme.daire?.daireNo || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">${t('label.kat', 'Kat')}:</span>
                <span>${sozlesme.daire?.kat?.katAdi || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">${t('label.satisFiyati', 'Satış Fiyatı')}:</span>
                <span>${sozlesme.sozlesmeFiyati.toLocaleString(getAktifDil() || 'tr')} ₺</span>
              </div>
            </div>
          </div>
        </div>

        ${(aktifOdemeler.length > 0 || direktTahsilatlarPDF.length > 0) ? `
        <div class="info-section">
          <table>
            <thead>
              <tr>
                <th class="text-center">${t('sozlesmeDetay.vadeTarihi', 'Vade Tarihi')}</th>
                <th class="text-right">${t('sozlesmeDetay.miktarTL', 'Miktar')}</th>
                <th class="text-center">${t('sozlesme.odemeTuru', 'Ödeme Türü')}</th>
                <th class="text-center">${t('label.durum', 'Durum')}</th>
                <th>${t('label.aciklama', 'Açıklama')}</th>
              </tr>
            </thead>
            <tbody>
              ${aktifOdemeler.map(taksit => `
                <tr>
                  <td class="text-center">${taksit.vadeTarihi ? new Date(taksit.vadeTarihi).toLocaleDateString(getAktifDil() || 'tr') : '-'}</td>
                  <td class="text-right">${taksit.miktar.toLocaleString(getAktifDil() || 'tr')} ₺</td>
                  <td class="text-center">${taksit.odemeTuru || taksit.odemeTuruDetay || '-'}</td>
                  <td class="text-center" style="color:${taksit.odemeDurumu === 'Odendi' ? '#15803d' : '#a16207'};font-weight:bold">${taksit.odemeDurumu === 'Odendi' ? t('durum.odendi', 'Ödendi') : t('durum.bekliyor', 'Bekliyor')}</td>
                  <td>${taksit.aciklama || '-'}</td>
                </tr>
              `).join('')}
              ${direktTahsilatlarPDFRows}
            </tbody>
            <tfoot>
              <tr style="background:#f0fdf4;">
                <td colspan="3" style="padding:8px;font-weight:bold;color:#15803d;">${t('sozlesme.odendiToplam', 'Ödenen Toplam')}</td>
                <td colspan="2" style="padding:8px;text-align:right;font-weight:bold;color:#15803d;">${odenenToplam.toLocaleString(getAktifDil() || 'tr')} ₺</td>
              </tr>
              <tr style="background:#fefce8;">
                <td colspan="3" style="padding:8px;font-weight:bold;color:#a16207;">${t('makbuz.bekleyenToplam', 'Bekleyen Toplam')}</td>
                <td colspan="2" style="padding:8px;text-align:right;font-weight:bold;color:#a16207;">${bekleyenToplam.toLocaleString(getAktifDil() || 'tr')} ₺</td>
              </tr>
            </tfoot>
          </table>
        </div>
        ` : ''}

        <div style="margin-top: 40px; text-align: center; font-size: 9pt; color: #666;">
          <p>${t('makbuz.dipnot', '{{tarih}} tarihinde düzenlenmiştir.', { tarih: new Date().toLocaleDateString(getAktifDil() || 'tr') })}</p>
        </div>
      </body>
      </html>
    `;

    // Yeni pencere aç ve yazdır
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Sayfa yüklendikten sonra yazdırma dialog'unu aç
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };
      
      toast.success(t('msg.islemBasarili'));
    } else {
      toast.error(t('msg.islemHatasi'));
    }
  };

  // Teklif önizleme
  const generateTeklifPDF = async () => {
    if (!sozlesme) return;
    try {
      let kurumsal: any = null;
      try { const r = await axiosInstance.get('/api/Kurumsal/1'); kurumsal = r.data; } catch {}

      // Logo base64
      let logoBase64 = '';
      if (kurumsal?.logo) {
        try {
          const logoRes = await axiosInstance.get(`/userFiles/${kurumsal.logo}`, { responseType: 'blob', baseURL: '' });
          logoBase64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(logoRes.data);
          });
        } catch {
          try {
            const logoRes2 = await fetch(`${API_BASE_URL}/userFiles/${kurumsal.logo}`);
            const blob = await logoRes2.blob();
            logoBase64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            });
          } catch {}
        }
      }

      setTeklifKurumsal(kurumsal);
      setTeklifLogoBase64(logoBase64);
      setShowTeklif(true);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const downloadTeklifPDF = async () => {
    if (!teklifRef.current) return;
    toast.info(t('msg.bilgi'), { autoClose: 2000 });
    try {
      const el = teklifRef.current;
      const prevBoxShadow = el.style.boxShadow;
      const prevBorder = el.style.border;
      const prevBorderRadius = el.style.borderRadius;
      el.style.boxShadow = 'none';
      el.style.border = 'none';
      el.style.borderRadius = '0';
      const canvas = await html2canvas(el, { scale: 2.5, useCORS: true, backgroundColor: '#ffffff', logging: false });
      el.style.boxShadow = prevBoxShadow;
      el.style.border = prevBorder;
      el.style.borderRadius = prevBorderRadius;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const printableW = pageWidth - margin * 2;
      const printableH = pageHeight - margin * 2;
      const pxPerMm = canvas.width / printableW;
      const sliceHeightPx = Math.floor(printableH * pxPerMm);
      const totalPages = Math.ceil(canvas.height / sliceHeightPx);
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        const srcY = page * sliceHeightPx;
        const srcH = Math.min(sliceHeightPx, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        }
        pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', margin, margin, printableW, srcH / pxPerMm);
      }
      pdf.save(`Teklif_${sozlesme?.cari?.adiSoyadi || sozlesme?.sozlesmeTakipNo || 'belge'}.pdf`);
      toast.success('PDF indirildi!');
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleEditOdeme = (odeme: Odeme) => {
    setEditingOdeme(odeme);
    
    // Form alanlarını doldurmak için date format'larını düzenle (timezone offset'ini hesaba kat)
    const formatDateForInput = (dateString: string) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEditOdemeFormData({
      miktar: odeme.miktar.toString(),
      vadeTarihi: odeme.vadeTarihi ? formatDateForInput(odeme.vadeTarihi) : '',
      tahsilatTarihi: odeme.tahsilatTarihi ? formatDateForInput(odeme.tahsilatTarihi) : '',
      odemeDurumu: odeme.odemeDurumu,
      odemeTuru: odeme.odemeTuru,
      kasaID: odeme.kasaID ? odeme.kasaID.toString() : '',
      aciklama: odeme.aciklama || ''
    });
    
    setShowEditOdemeModal(true);
  };

  const handleSaveEditOdeme = async () => {
    if (!editingOdeme) return;

    try {
      // Form validasyonu
      if (!editOdemeFormData.miktar || !editOdemeFormData.vadeTarihi || !editOdemeFormData.odemeDurumu) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }

      // Ödeme durumu "Ödendi" ise kasa seçimi zorunlu
      if (editOdemeFormData.odemeDurumu === 'Odendi' && !editOdemeFormData.kasaID) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }

      setSaving(true);

      // Enum değerlerini integer'a çevir
      const getOdemeTuruValue = (turu: string) => {
        switch(turu) {
          case 'Nakit': return 0;
          case 'Senet': return 1;
          case 'Cek': return 2;
          case 'Takas': return 3;
          default: return 0;
        }
      };

      const getOdemeYonuValue = (yonu: string) => {
        switch(yonu) {
          case 'Tahsilat': return 0;
          case 'Odeme': return 1;
          default: return 0;
        }
      };

      const getOdemeDurumuValue = (durum: string) => {
        switch(durum) {
          case 'Bekliyor': return 0;
          case 'Odendi': return 1;
          default: return 0;
        }
      };

      const updateData = {
        odemeID: editingOdeme.odemeID,
        cariID: editingOdeme.cariID,
        islemTarihi: editingOdeme.islemTarihi,
        vadeTarihi: editOdemeFormData.vadeTarihi,
        tahsilatTarihi: editOdemeFormData.tahsilatTarihi || editOdemeFormData.vadeTarihi,
        miktar: parseFloat(editOdemeFormData.miktar),
        paraBirimiID: editingOdeme.paraBirimiID,
        islemTakipNo: editingOdeme.islemTakipNo,
        odemeTuru: getOdemeTuruValue(editingOdeme.odemeTuru),
        odemeYonu: getOdemeYonuValue(editingOdeme.odemeYonu),
        islemTipi: getOdemeYonuValue(editingOdeme.odemeYonu) === 0 ? 2 : 3,
        odemeDurumu: getOdemeDurumuValue(editOdemeFormData.odemeDurumu),
        kasaID: editOdemeFormData.kasaID ? parseInt(editOdemeFormData.kasaID) : null,
        aciklama: editOdemeFormData.aciklama,
        aktifMi: editingOdeme.aktifMi,
        sozlesmeOdemesi: editingOdeme.sozlesmeOdemesi,
        eklemeTarihi: editingOdeme.eklemeTarihi
      };

      console.log('Gönderilen güncelleme verisi:', updateData);
      await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, updateData);
      
      toast.success(t('msg.islemBasarili'));
      setShowEditOdemeModal(false);
      setEditingOdeme(null);
      
      // Ödemeleri yeniden yükle
      await fetchOdemeler();
      
      // Toplam ödeme tutarını yeniden hesapla ve sözleşmeyi güncelle
      const yeniToplam = calculateTotalPayments();
      await updateSozlesmePlanlananOdeme(yeniToplam);
      
    } catch (error: any) {
      toast.error(t('msg.islemHatasi'));
      console.error('Ödeme güncelleme hatası:', error);
      console.error('Hata detayı:', error?.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelOdeme = async () => {
    if (!editingOdeme) return;

    try {
      setSaving(true);

      // Enum değerlerini integer'a çevir
      const getOdemeTuruValue = (turu: string) => {
        switch(turu) {
          case 'Nakit': return 0;
          case 'Senet': return 1;
          case 'Cek': return 2;
          case 'Takas': return 3;
          default: return 0;
        }
      };

      const getOdemeYonuValue = (yonu: string) => {
        switch(yonu) {
          case 'Tahsilat': return 0;
          case 'Odeme': return 1;
          default: return 0;
        }
      };

      const getOdemeDurumuValue = (durum: string) => {
        switch(durum) {
          case 'Bekliyor': return 0;
          case 'Odendi': return 1;
          default: return 0;
        }
      };

      const updateData = {
        odemeID: editingOdeme.odemeID,
        cariID: editingOdeme.cariID,
        islemTarihi: editingOdeme.islemTarihi,
        vadeTarihi: editingOdeme.vadeTarihi,
        tahsilatTarihi: editingOdeme.tahsilatTarihi,
        miktar: editingOdeme.miktar,
        paraBirimiID: editingOdeme.paraBirimiID,
        islemTakipNo: editingOdeme.islemTakipNo,
        odemeTuru: getOdemeTuruValue(editingOdeme.odemeTuru),
        odemeYonu: getOdemeYonuValue(editingOdeme.odemeYonu),
        odemeDurumu: getOdemeDurumuValue(editingOdeme.odemeDurumu),
        aciklama: editingOdeme.aciklama,
        aktifMi: false, // Ödemeyi iptal et
        sozlesmeOdemesi: editingOdeme.sozlesmeOdemesi,
        eklemeTarihi: editingOdeme.eklemeTarihi
      };

      await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, updateData);
      
      toast.success(t('msg.islemBasarili'));
      setShowEditOdemeModal(false);
      setEditingOdeme(null);
      
      // Ödemeleri yeniden yükle
      await fetchOdemeler();
      
      // Toplam ödeme tutarını yeniden hesapla ve sözleşmeyi güncelle
      const yeniToplam = calculateTotalPayments();
      await updateSozlesmePlanlananOdeme(yeniToplam);
      
    } catch (error: any) {
      toast.error(t('msg.islemHatasi'));
      console.error('Ödeme iptal hatası:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTopluIptal = async () => {
    if (selectedOdemeler.size === 0 || !sozlesme?.sozlesmeID) return;
    if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;

    try {
      setTopluIptalLoading(true);
      const iptalEdilecekler = taksitler.filter(item => selectedOdemeler.has(item.taksitID) && item.odemeDurumu === 'Bekliyor');

      await Promise.all(iptalEdilecekler.map(item =>
        axiosInstance.put(`/api/OdemePlanTaksitleri/${item.taksitID}/iptal`)
      ));

      toast.success(t('msg.islemBasarili'));
      setSelectedOdemeler(new Set());
      await fetchTaksitler(sozlesme.sozlesmeID);
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      console.error('Toplu iptal hatası:', error);
    } finally {
      setTopluIptalLoading(false);
    }
  };

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleOdemeFormChange = (field: string, value: string) => {
    setOdemeFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Kilitli olmayan (sistemin serbestçe dağıtım yapabildiği) satırlara, toplam tutardan
  // kilitli satırların düşülmesiyle kalan miktarı eşit şekilde dağıtır (küsurat son satıra kalır).
  const redistributeTopluRows = (rows: HazirPlanRow[], toplamTutar: number): HazirPlanRow[] => {
    const unlockedIdx = rows.reduce<number[]>((acc, r, i) => { if (!r.kilitli) acc.push(i); return acc; }, []);
    if (unlockedIdx.length === 0) return rows;
    const lockedSum = rows.filter(r => r.kilitli).reduce((s, r) => s + r.miktar, 0);
    const kalan = toplamTutar - lockedSum;
    const pay = Math.floor(kalan / unlockedIdx.length);
    const sonPay = kalan - pay * (unlockedIdx.length - 1);
    return rows.map((r, i) => {
      const pos = unlockedIdx.indexOf(i);
      if (pos === -1) return r;
      return { ...r, miktar: pos === unlockedIdx.length - 1 ? sonPay : pay };
    });
  };

  const generateTopluPlanlaRows = (data: typeof topluPlanlaFormData, prevRows: HazirPlanRow[] = []): HazirPlanRow[] => {
    const taksitSayisi = parseInt(data.taksitSayisi);
    const toplamTutar = parseFloat(data.taksitlendirilecekTutar);
    if (!taksitSayisi || taksitSayisi <= 0 || !toplamTutar || toplamTutar <= 0 || !data.baslamaTarihi) return [];

    const fmtTR = (d: Date): string =>
      `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;

    const baslamaTarihi = new Date(data.baslamaTarihi);
    const rows: HazirPlanRow[] = [];
    for (let i = 0; i < taksitSayisi; i++) {
      const prev = prevRows[i];
      if (prev && prev.kilitli) {
        rows.push({ ...prev });
        continue;
      }
      const vade = new Date(baslamaTarihi);
      vade.setMonth(vade.getMonth() + i);
      rows.push({
        odemeTuru: data.odemeTuru || 'Nakit',
        vadeTarihiStr: fmtTR(vade),
        miktar: 0,
        aciklama: `${i + 1}. Taksit${data.aciklama ? ' - ' + data.aciklama : ''}`,
        kilitli: false,
      });
    }
    return redistributeTopluRows(rows, toplamTutar);
  };

  const handleTopluPlanlaFormChange = (field: string, value: string) => {
    if (field === 'taksitlendirilecekTutar') {
      const yeniToplam = parseFloat(value) || 0;
      const kilitliToplam = topluPlanlaRows.filter(r => r.kilitli).reduce((s, r) => s + r.miktar, 0);
      if (kilitliToplam > yeniToplam) {
        toast.error(t('sozlesmeDetay.kilitliTutarUyarisi', `Kilitli satırların toplamı (₺${kilitliToplam.toLocaleString('tr-TR')}) yeni toplam tutardan büyük. Önce ilgili satırların kilidini açın.`));
      }
    }
    setTopluPlanlaFormData(prev => {
      const next = { ...prev, [field]: value };
      setTopluPlanlaRows(generateTopluPlanlaRows(next, topluPlanlaRows));
      return next;
    });
  };

  const handleDeleteTopluRow = (idx: number) => {
    const newRows = topluPlanlaRows.filter((_, i) => i !== idx);
    const toplamTutar = parseFloat(topluPlanlaFormData.taksitlendirilecekTutar) || 0;
    setTopluPlanlaRows(redistributeTopluRows(newRows, toplamTutar));
    setTopluPlanlaFormData(prev => ({ ...prev, taksitSayisi: String(newRows.length) }));
  };

  const handleAddTopluRow = () => {
    const fmtTR = (d: Date): string =>
      `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    const last = topluPlanlaRows[topluPlanlaRows.length - 1];
    let vade: Date;
    if (last) {
      const [d, m, y] = last.vadeTarihiStr.split('.');
      vade = new Date(+y, +m - 1, +d);
      vade.setMonth(vade.getMonth() + 1);
    } else if (topluPlanlaFormData.baslamaTarihi) {
      vade = new Date(topluPlanlaFormData.baslamaTarihi);
    } else {
      vade = new Date();
    }
    const newRow: HazirPlanRow = {
      odemeTuru: topluPlanlaFormData.odemeTuru || 'Nakit',
      vadeTarihiStr: fmtTR(vade),
      miktar: 0,
      aciklama: `${topluPlanlaRows.length + 1}. Taksit`,
      kilitli: false,
    };
    const newRows = [...topluPlanlaRows, newRow];
    const toplamTutar = parseFloat(topluPlanlaFormData.taksitlendirilecekTutar) || 0;
    setTopluPlanlaRows(redistributeTopluRows(newRows, toplamTutar));
    setTopluPlanlaFormData(prev => ({ ...prev, taksitSayisi: String(newRows.length) }));
  };

  const handleEditOdemeFormChange = (field: string, value: string) => {
    setEditOdemeFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Sözleşmenin planlanan ödeme alanını güncelle
  const updateSozlesmePlanlananOdeme = async (yeniToplam: number) => {
    try {
      // Ortalama vadeyi hesapla
      const ortalamaVade = calculateOrtalamaVade();
      
      await axiosInstance.put(`/api/Sozlesmeler/${sozlesme?.sozlesmeID}`, {
        ...sozlesme,
        planlananOdeme: yeniToplam,
        ortalamaVade: ortalamaVade
      });
      
      // Local state'i güncelle
      if (sozlesme) {
        setSozlesme({
          ...sozlesme,
          planlananOdeme: yeniToplam,
          ortalamaVade: ortalamaVade
        });
      }
    } catch (error) {
      console.error('Sözleşme güncelleme hatası:', error);
    }
  };

  const handleSaveOdeme = async () => {
    try {
      if (!odemeFormData.miktar || parseFloat(odemeFormData.miktar) <= 0) {
        toast.error(t('msg.zorunluAlanlar')); return;
      }
      if (!odemeFormData.islemTarihi) {
        toast.error(t('msg.zorunluAlanlar')); return;
      }
      if (!odemeFormData.kasaID) {
        toast.error(t('msg.zorunluAlanlar')); return;
      }
      if (!sozlesme?.cari?.cariID) {
        toast.error(t('msg.islemHatasi')); return;
      }

      setSaving(true);

      const makbuzTutar = parseFloat(odemeFormData.miktar);
      const res = await axiosInstance.post('/api/Odemeler', {
        cariID: sozlesme.cari.cariID,
        islemTarihi: odemeFormData.islemTarihi,
        vadeTarihi: odemeFormData.islemTarihi,
        tahsilatTarihi: odemeFormData.islemTarihi,
        miktar: makbuzTutar,
        paraBirimiID: 1,
        islemTakipNo: sozlesme.sozlesmeTakipNo,
        odemeTuru: odemeFormData.odemeTuru || 'Nakit',
        odemeYonu: 'Tahsilat',
        odemeDurumu: 'Odendi',
        kasaID: parseInt(odemeFormData.kasaID),
        aciklama: odemeFormData.aciklama || null,
        aktifMi: true,
        sozlesmeOdemesi: true,
        islemTipi: 2,
      });

      // Otomatik FIFO dağıtım: açık faturalara eşleştir
      const makbuzID = res.data?.odemeID ?? res.data?.id ?? null;
      try {
        const acikRes = await axiosInstance.get(`/api/OdemeFaturaEslesmeleri/acik-faturalar/${sozlesme.cari.cariID}`);
        const faturalar: any[] = acikRes.data || [];
        if (faturalar.length > 0 && makbuzID) {
          let kalan = makbuzTutar;
          const eslesmeler: any[] = [];
          for (const f of faturalar) {
            if (kalan <= 0) break;
            const dagit = Math.min(kalan, f.kalanTutar);
            eslesmeler.push({ faturaOdemeID: f.tip === 'odeme' ? f.id : null, sozlesmeID: f.tip === 'sozlesme' ? f.id : null, odenenTutar: dagit });
            kalan -= dagit;
          }
          if (eslesmeler.length > 0) {
            await axiosInstance.post('/api/OdemeFaturaEslesmeleri', { makbuzOdemeID: makbuzID, eslesmeler });
          }
        }
      } catch (_) { /* Eşleştirme başarısız olsa bile makbuz kaydedildi */ }

      await fetchOdemeler();
      const yeniToplam = calculateTotalPayments() + makbuzTutar;
      await updateSozlesmePlanlananOdeme(yeniToplam);

      toast.success(t('msg.islemBasarili'));
      setShowOdemeModal(false);
      setOdemeFormData({ miktar: '', islemTarihi: '', vadeTarihi: '', odemeTuru: 'Nakit', odemeDurumu: '', kasaID: '', aciklama: '' });
    } catch (error: any) {
      toast.error(error.response?.data?.message  || t('msg.islemHatasi'));
      console.error('Tahsilat kaydetme hatası:', error);
    } finally {
      setSaving(false);
    }
  };

  const resetOdemeForm = () => {
    const today = getCurrentDate();
    setOdemeFormData({
      miktar: '',
      islemTarihi: today,
      vadeTarihi: today,
      odemeTuru: 'Nakit',
      odemeDurumu: 'Odendi',
      kasaID: '',
      aciklama: ''
    });
  };

  const handleTahsilatYap = (taksit: OdemePlanTaksiti) => {
    setTahsilatYapTaksit(taksit);
    setTahsilatYapFormData({ tarih: getCurrentDate(), kasaID: '', odemeTuru: 'Nakit', aciklama: taksit.aciklama || '' });
    setShowTahsilatYapModal(true);
  };

  const handleSaveTahsilatYap = async () => {
    if (!tahsilatYapTaksit || !sozlesme?.cari?.cariID || !sozlesme?.sozlesmeID) return;
    if (!tahsilatYapFormData.kasaID) { toast.error(t('msg.zorunluAlanlar')); return; }
    const toOT = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string, number>)[t] ?? 0;
    try {
      setSaving(true);
      // Adım 1: Gerçek ödeme kaydı oluştur
      const odemeRes = await axiosInstance.post('/api/Odemeler', {
        cariID: sozlesme.cari.cariID,
        islemTarihi: tahsilatYapFormData.tarih,
        vadeTarihi: tahsilatYapTaksit.vadeTarihi,
        tahsilatTarihi: tahsilatYapFormData.tarih,
        miktar: tahsilatYapTaksit.miktar,
        paraBirimiID: 1,
        islemTakipNo: sozlesme.sozlesmeTakipNo,
        odemeTuru: toOT(tahsilatYapFormData.odemeTuru),
        odemeYonu: 0, // Tahsilat
        odemeDurumu: 1, // Odendi
        kasaID: parseInt(tahsilatYapFormData.kasaID),
        aciklama: tahsilatYapFormData.aciklama || tahsilatYapTaksit.aciklama,
        aktifMi: true,
        sozlesmeOdemesi: true,
        eklemeTarihi: new Date().toISOString(),
        islemTipi: 2,
      });
      const yeniOdemeID = odemeRes.data?.odemeID;
      // Adım 2: Taksiti tahsilat bilgileriyle güncelle
      await axiosInstance.put(`/api/OdemePlanTaksitleri/${tahsilatYapTaksit.taksitID}/tahsilat`, {
        gercekOdemeID: yeniOdemeID,
        tahsilatTarihi: tahsilatYapFormData.tarih,
        kasaID: parseInt(tahsilatYapFormData.kasaID),
      });
      toast.success(t('msg.islemBasarili'));
      setShowTahsilatYapModal(false);
      setTahsilatYapTaksit(null);
      await fetchTaksitler(sozlesme.sozlesmeID);
    } catch { toast.error(t('msg.islemHatasi')); }
    finally { setSaving(false); }
  };

  const handleTahsilatGeriAl = async (taksit: OdemePlanTaksiti) => {
    if (!sozlesme?.sozlesmeID) return;
    if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
    try {
      setSaving(true);
      // Gerçek ödemeyi pasife al
      if (taksit.gercekOdemeID) {
        await axiosInstance.post(`/api/Odemeler/delete/${taksit.gercekOdemeID}`);
      }
      // Taksiti Bekliyor'a geri al
      await axiosInstance.put(`/api/OdemePlanTaksitleri/${taksit.taksitID}/geriali`);
      toast.success(t('msg.islemBasarili'));
      await fetchTaksitler(sozlesme.sozlesmeID);
      await fetchOdemeler();
    } catch (error: any) {
      console.error('Tahsilat geri alma hatası:', error);
      const msg = error?.response?.data?.message || error?.response?.data || error?.message || 'Tahsilat geri alma işlemi sırasında bir hata oluştu!';
      toast.error(String(msg), { autoClose: 10000 });
    }
    finally { setSaving(false); }
  };

  const handleMakbuzYazdir = async (taksit: OdemePlanTaksiti) => {
    if (!sozlesme) return;
    let kurumsal: any = null;
    try { const r = await axiosInstance.get('/api/Kurumsal/1'); kurumsal = r.data; } catch {}

    const makbuzNo = taksit.gercekOdemeID
      ? `MKB-${String(taksit.gercekOdemeID).padStart(6, '0')}`
      : `TKS-${String(taksit.taksitID).padStart(6, '0')}`;
    const lang = getAktifDil() || 'tr';
    const tahsilatTarih = taksit.tahsilatTarihi
      ? new Date(taksit.tahsilatTarihi).toLocaleDateString(lang)
      : new Date().toLocaleDateString(lang);
    const vadeTarih = taksit.vadeTarihi
      ? new Date(taksit.vadeTarihi).toLocaleDateString(lang)
      : '—';
    const miktar = taksit.miktar.toLocaleString(lang, { minimumFractionDigits: 2 });
    const odemeTuru = taksit.odemeTuru || taksit.odemeTuruDetay || 'Nakit';
    const cari = sozlesme.cari;
    const daire = sozlesme.daire;
    const proje = daire?.blok?.proje?.projeAdi || '—';
    const blok = daire?.blok?.blokAdi || '';
    const daireNo = daire ? `${blok ? blok + ' / ' : ''}${t('label.daire', 'Daire')} ${daire.daireNo}` : '—';

    const olusturmaTarihSaat = new Date().toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    const html = `<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="UTF-8">
<title>${t('sozlesme.tahsilatMakbuzu', 'Tahsilat Makbuzu')} ${makbuzNo}</title>
<style>${MAKBUZ_CSS}</style>
</head>
<body>
<button class="print-btn no-print" onclick="window.print()">🖨️ ${t('btn.yazdir', 'Yazdır')}</button>
<div class="makbuz">
  ${makbuzHeaderHtml({ firmaAdi: kurumsal?.firmaAdi, baslik: t('sozlesme.tahsilatMakbuzu', 'Tahsilat Makbuzu'), makbuzNo, olusturmaTarihSaat, tarih: tahsilatTarih })}

  <div class="tutar-card">
    <div class="tutar-left">
      <div class="icon-circle orange">${MAKBUZ_ICON_COINS}</div>
      <div>
        <div class="lbl">${t('sozlesme.alinanTutar', 'Alınan Tutar')}</div>
        <div class="tutar">${miktar} ₺</div>
      </div>
    </div>
    <div class="tutar-right">
      <div>
        <div class="lbl">${t('sozlesme.odemeTuru', 'Ödeme Türü')}</div>
        <div class="odeme-turu">${odemeTuru}</div>
        <div class="vade">${t('makbuz.vade', 'Vade')}: ${vadeTarih}</div>
      </div>
      <div class="icon-circle gray">${MAKBUZ_ICON_WALLET}</div>
    </div>
  </div>

  <div class="body-grid">
    <div class="info-block">
      <div class="block-title"><span class="icon-sm">${MAKBUZ_ICON_PERSON}</span>${t('musteri.musteriTab', 'Müşteri Bilgileri')}</div>
      <div class="title-underline"></div>
      ${cari?.adiSoyadi ? `<div class="info-row"><span class="lbl">${t('label.adSoyad', 'Ad Soyad')}</span><span class="sep">:</span><span class="val">${cari.adiSoyadi}</span></div>` : ''}
      ${cari?.tc ? `<div class="info-row"><span class="lbl">${t('label.tcKimlik', 'TC Kimlik')}</span><span class="sep">:</span><span class="val">${cari.tc}</span></div>` : ''}
      ${cari?.telefon ? `<div class="info-row"><span class="lbl">${t('label.telefon', 'Telefon')}</span><span class="sep">:</span><span class="val">${cari.telefon}</span></div>` : ''}
      ${cari?.email ? `<div class="info-row"><span class="lbl">${t('label.eposta', 'E-Posta')}</span><span class="sep">:</span><span class="val">${cari.email}</span></div>` : ''}
    </div>
    <div class="info-block">
      <div class="block-title"><span class="icon-sm">${MAKBUZ_ICON_DOC}</span>${t('label.islemBilgileri', 'İşlem Bilgileri')}</div>
      <div class="title-underline"></div>
      <div class="info-row"><span class="lbl">${t('sozlesme.no', 'Sözleşme No')}</span><span class="sep">:</span><span class="val">${sozlesme.sozlesmeTakipNo}</span></div>
      <div class="info-row"><span class="lbl">${t('label.proje', 'Proje')}</span><span class="sep">:</span><span class="val">${proje}</span></div>
      <div class="info-row"><span class="lbl">${t('label.daire', 'Daire')}</span><span class="sep">:</span><span class="val">${daireNo}</span></div>
      ${taksit.aciklama ? `<div class="info-row"><span class="lbl">${t('label.aciklama', 'Açıklama')}</span><span class="sep">:</span><span class="val">${taksit.aciklama}</span></div>` : ''}
    </div>
  </div>

  <div class="imza-row">
    <div class="imza-box">
      <div class="imza-line"></div>
      <div class="imza-label">${t('sozlesme.teslimEdenImza', 'Teslim Eden')}</div>
    </div>
    <div class="imza-box">
      <div class="imza-line"></div>
      <div class="imza-label">${t('sozlesme.teslimAlanImza', 'Teslim Alan')}</div>
    </div>
  </div>

  <div class="not">${t('makbuz.dipnot', '{{tarih}} tarihinde düzenlenmiştir.', { tarih: new Date().toLocaleDateString(lang, { day:'2-digit', month:'long', year:'numeric' }) })}</div>
</div>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=900,height=620');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.onload = () => setTimeout(() => w.print(), 300);
    }
  };

  const handleMakbuzYazdirOdeme = async (odeme: Odeme) => {
    if (!sozlesme) return;
    let kurumsal: any = null;
    try { const r = await axiosInstance.get('/api/Kurumsal/1'); kurumsal = r.data; } catch {}

    const makbuzNo = `MKB-${String(odeme.odemeID).padStart(6, '0')}`;
    const lang2 = getAktifDil() || 'tr';
    const tahsilatTarih = odeme.tahsilatTarihi
      ? new Date(odeme.tahsilatTarihi).toLocaleDateString(lang2)
      : new Date(odeme.islemTarihi).toLocaleDateString(lang2);
    const vadeTarih = odeme.vadeTarihi
      ? new Date(odeme.vadeTarihi).toLocaleDateString(lang2)
      : '—';
    const miktar = odeme.miktar.toLocaleString(lang2, { minimumFractionDigits: 2 });
    const odemeTuru = odeme.odemeTuru || 'Nakit';
    const cari = sozlesme.cari;
    const daire = sozlesme.daire;
    const proje = daire?.blok?.proje?.projeAdi || '—';
    const blok = daire?.blok?.blokAdi || '';
    const daireNo = daire ? `${blok ? blok + ' / ' : ''}${t('label.daire', 'Daire')} ${daire.daireNo}` : '—';

    const olusturmaTarihSaat2 = new Date().toLocaleDateString(lang2, { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date().toLocaleTimeString(lang2, { hour: '2-digit', minute: '2-digit' });
    const html = `<!DOCTYPE html>
<html lang="${lang2}">
<head>
<meta charset="UTF-8">
<title>${t('sozlesme.tahsilatMakbuzu', 'Tahsilat Makbuzu')} ${makbuzNo}</title>
<style>${MAKBUZ_CSS}</style>
</head>
<body>
<button class="print-btn no-print" onclick="window.print()">🖨️ ${t('btn.yazdir', 'Yazdır')}</button>
<div class="makbuz">
  ${makbuzHeaderHtml({ firmaAdi: kurumsal?.firmaAdi, baslik: t('sozlesme.tahsilatMakbuzu', 'Tahsilat Makbuzu'), makbuzNo, olusturmaTarihSaat: olusturmaTarihSaat2, tarih: tahsilatTarih })}

  <div class="tutar-card">
    <div class="tutar-left">
      <div class="icon-circle orange">${MAKBUZ_ICON_COINS}</div>
      <div>
        <div class="lbl">${t('sozlesme.alinanTutar', 'Alınan Tutar')}</div>
        <div class="tutar">${miktar} ₺</div>
      </div>
    </div>
    <div class="tutar-right">
      <div>
        <div class="lbl">${t('sozlesme.odemeTuru', 'Ödeme Türü')}</div>
        <div class="odeme-turu">${odemeTuru}</div>
        <div class="vade">${t('makbuz.vade', 'Vade')}: ${vadeTarih}</div>
      </div>
      <div class="icon-circle gray">${MAKBUZ_ICON_WALLET}</div>
    </div>
  </div>

  <div class="body-grid">
    <div class="info-block">
      <div class="block-title"><span class="icon-sm">${MAKBUZ_ICON_PERSON}</span>${t('musteri.musteriTab', 'Müşteri Bilgileri')}</div>
      <div class="title-underline"></div>
      ${cari?.adiSoyadi ? `<div class="info-row"><span class="lbl">${t('label.adSoyad', 'Ad Soyad')}</span><span class="sep">:</span><span class="val">${cari.adiSoyadi}</span></div>` : ''}
      ${cari?.tc ? `<div class="info-row"><span class="lbl">${t('label.tcKimlik', 'TC Kimlik')}</span><span class="sep">:</span><span class="val">${cari.tc}</span></div>` : ''}
      ${cari?.telefon ? `<div class="info-row"><span class="lbl">${t('label.telefon', 'Telefon')}</span><span class="sep">:</span><span class="val">${cari.telefon}</span></div>` : ''}
      ${cari?.email ? `<div class="info-row"><span class="lbl">${t('label.eposta', 'E-Posta')}</span><span class="sep">:</span><span class="val">${cari.email}</span></div>` : ''}
    </div>
    <div class="info-block">
      <div class="block-title"><span class="icon-sm">${MAKBUZ_ICON_DOC}</span>${t('label.islemBilgileri', 'İşlem Bilgileri')}</div>
      <div class="title-underline"></div>
      <div class="info-row"><span class="lbl">${t('sozlesme.no', 'Sözleşme No')}</span><span class="sep">:</span><span class="val">${sozlesme.sozlesmeTakipNo}</span></div>
      <div class="info-row"><span class="lbl">${t('label.proje', 'Proje')}</span><span class="sep">:</span><span class="val">${proje}</span></div>
      <div class="info-row"><span class="lbl">${t('label.daire', 'Daire')}</span><span class="sep">:</span><span class="val">${daireNo}</span></div>
      ${odeme.kasa?.kasaAdi ? `<div class="info-row"><span class="lbl">${t('label.kasa', 'Kasa')}</span><span class="sep">:</span><span class="val">${odeme.kasa.kasaAdi}</span></div>` : ''}
      ${odeme.aciklama ? `<div class="info-row"><span class="lbl">${t('label.aciklama', 'Açıklama')}</span><span class="sep">:</span><span class="val">${odeme.aciklama}</span></div>` : ''}
    </div>
  </div>

  <div class="imza-row">
    <div class="imza-box">
      <div class="imza-line"></div>
      <div class="imza-label">${t('sozlesme.teslimEdenImza', 'Teslim Eden')}</div>
    </div>
    <div class="imza-box">
      <div class="imza-line"></div>
      <div class="imza-label">${t('sozlesme.teslimAlanImza', 'Teslim Alan')}</div>
    </div>
  </div>

  <div class="not">${t('makbuz.dipnot', '{{tarih}} tarihinde düzenlenmiştir.', { tarih: new Date().toLocaleDateString(lang2, { day: '2-digit', month: 'long', year: 'numeric' }) })}</div>
</div>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=900,height=620');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.onload = () => setTimeout(() => w.print(), 300);
    }
  };

  const handleTaksitDetayOpen = (taksit: OdemePlanTaksiti) => {
    setSelectedTaksitForDetay(taksit);
    const miktarNum = taksit.miktar;
    setTaksitDetayForm({
      miktar: miktarNum.toString(),
      miktarDisplay: miktarNum.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
      vadeTarihi: taksit.vadeTarihi?.slice(0, 10) || '',
      odemeDurumu: taksit.odemeDurumu,
      aciklama: taksit.aciklama || '',
      tahsilatTarihi: getCurrentDate(),
      kasaID: '',
      odemeTuru: 'Nakit'
    });
    setShowTaksitDetayModal(true);
  };

  const handleTaksitDetaySave = async () => {
    if (!selectedTaksitForDetay || !sozlesme?.cari?.cariID || !sozlesme?.sozlesmeID) return;
    if (!taksitDetayForm.miktar || !taksitDetayForm.vadeTarihi) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }
    const miktar = parseFloat(taksitDetayForm.miktar);
    if (isNaN(miktar) || miktar <= 0) { toast.error(t('msg.zorunluAlanlar')); return; }

    if (taksitDetayForm.odemeDurumu === 'Odendi') {
      if (!taksitDetayForm.kasaID) { toast.error(t('msg.zorunluAlanlar')); return; }
      if (!taksitDetayForm.tahsilatTarihi) { toast.error(t('msg.zorunluAlanlar')); return; }
    }

    setTaksitDetaySaving(true);
    try {
      if (taksitDetayForm.odemeDurumu === 'Odendi') {
        const toOT = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string, number>)[t] ?? 0;
        // Önce taksit bilgilerini güncelle (miktar ve vade tarihi)
        await axiosInstance.put(`/api/OdemePlanTaksitleri/${selectedTaksitForDetay.taksitID}/guncelle`, {
          vadeTarihi: taksitDetayForm.vadeTarihi,
          miktar,
          odemeDurumu: 'Bekliyor',
          aciklama: taksitDetayForm.aciklama || null
        });
        // Gerçek ödeme kaydı oluştur
        const odemeRes = await axiosInstance.post('/api/Odemeler', {
          cariID: sozlesme.cari.cariID,
          islemTarihi: taksitDetayForm.tahsilatTarihi,
          vadeTarihi: taksitDetayForm.vadeTarihi,
          tahsilatTarihi: taksitDetayForm.tahsilatTarihi,
          miktar,
          paraBirimiID: 1,
          islemTakipNo: sozlesme.sozlesmeTakipNo,
          odemeTuru: toOT(taksitDetayForm.odemeTuru),
          odemeYonu: 0,
          odemeDurumu: 1,
          kasaID: parseInt(taksitDetayForm.kasaID),
          aciklama: taksitDetayForm.aciklama || selectedTaksitForDetay.aciklama,
          aktifMi: true,
          sozlesmeOdemesi: true,
          eklemeTarihi: new Date().toISOString(),
          islemTipi: 2,
        });
        // Taksiti tahsilat bilgileriyle işaretle
        await axiosInstance.put(`/api/OdemePlanTaksitleri/${selectedTaksitForDetay.taksitID}/tahsilat`, {
          gercekOdemeID: odemeRes.data?.odemeID,
          tahsilatTarihi: taksitDetayForm.tahsilatTarihi,
          kasaID: parseInt(taksitDetayForm.kasaID),
        });
        toast.success(t('msg.islemBasarili'));
      } else {
        await axiosInstance.put(`/api/OdemePlanTaksitleri/${selectedTaksitForDetay.taksitID}/guncelle`, {
          vadeTarihi: taksitDetayForm.vadeTarihi,
          miktar,
          odemeDurumu: taksitDetayForm.odemeDurumu,
          aciklama: taksitDetayForm.aciklama || null
        });
        toast.success(t('msg.islemBasarili'));
      }
      setShowTaksitDetayModal(false);
      await fetchTaksitler(sozlesme.sozlesmeID);
      if (taksitDetayForm.odemeDurumu === 'Odendi') await fetchOdemeler();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.response?.data || 'İşlem sırasında bir hata oluştu!';
      toast.error(String(msg));
    } finally {
      setTaksitDetaySaving(false);
    }
  };

  const handleShowOdemeModal = () => {
    resetOdemeForm();
    setShowOdemeModal(true);
  };

  const resetTopluPlanlaForm = () => {
    const today = getCurrentDate();
    const [y, m, d] = today.split('-');
    setBaslamaTarihiDisplay(`${d}.${m}.${y}`);
    setTopluPlanlaFormData({
      taksitlendirilecekTutar: '',
      baslamaTarihi: today,
      taksitSayisi: '',
      odemeTuru: '',
      aciklama: ''
    });
    setTopluPlanlaRows([]);
    setTopluPlanlaTemizleMevcut(false);
  };

  const handleShowTopluPlanlaModal = () => {
    resetTopluPlanlaForm();
    setShowTopluPlanlaModal(true);
  };

  const handleTaksitEkleFormChange = (field: string, value: string) => {
    setTaksitEkleFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleShowTaksitEkleModal = () => {
    const today = getCurrentDate();
    setTaksitEkleFormData({ vadeTarihi: today, odemeTuru: 'Nakit', miktar: '', aciklama: '' });
    setShowTaksitEkleModal(true);
  };

  const handleSaveTaksitEkle = async () => {
    if (!taksitEkleFormData.vadeTarihi || !taksitEkleFormData.miktar || parseFloat(taksitEkleFormData.miktar) <= 0) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }
    if (!sozlesme?.cari?.cariID || !sozlesme?.sozlesmeID) {
      toast.error(t('msg.islemHatasi'));
      return;
    }
    setTaksitEkleSaving(true);
    try {
      await axiosInstance.post('/api/OdemePlanTaksitleri/toplu', {
        sozlesmeID: sozlesme.sozlesmeID,
        cariID: sozlesme.cari.cariID,
        temizleMevcutPlan: false,
        taksitler: [{
          vadeTarihi: taksitEkleFormData.vadeTarihi,
          miktar: parseFloat(taksitEkleFormData.miktar),
          odemeTuruDetay: taksitEkleFormData.odemeTuru,
          odemeTuru: taksitEkleFormData.odemeTuru,
          aciklama: taksitEkleFormData.aciklama || 'Taksit',
          siraNo: 1,
        }],
      });
      await fetchTaksitler(sozlesme.sozlesmeID);
      toast.success(t('msg.islemBasarili'));
      setShowTaksitEkleModal(false);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setTaksitEkleSaving(false);
    }
  };

  // ── Vade Performansı ────────────────────────────────────────────────────────

  const VADE_DURUM_RENK: Record<string, string> = {
    Odendi: '#16a34a',
    Iptal: '#9ca3af',
    Gecikmeli: '#dc2626',
    Bekliyor: '#3b82f6',
  };
  const VADE_DURUM_ETIKET: Record<string, string> = {
    Odendi: 'Ödendi', Iptal: 'İptal', Gecikmeli: 'Gecikmeli (Bekliyor)', Bekliyor: 'Bekliyor',
  };

  const getVadePerformansData = () => {
    const gecerliTaksitler = taksitler.filter(t => t.aktifMi !== false);
    const today = new Date(); today.setHours(0, 0, 0, 0);

    const items = gecerliTaksitler
      .map(t => {
        const vade = new Date(t.vadeTarihi); vade.setHours(0, 0, 0, 0);
        const durumKey = t.odemeDurumu === 'Odendi' ? 'Odendi' : t.odemeDurumu === 'Iptal' ? 'Iptal' : (vade.getTime() < today.getTime() ? 'Gecikmeli' : 'Bekliyor');
        return { ...t, vade, durumKey };
      })
      .sort((a, b) => a.vade.getTime() - b.vade.getTime());

    let timelineItems: Array<typeof items[number] & { percent: number; barPct: number; offsetPx: number }> = [];
    if (items.length > 0) {
      const minTime = items[0].vade.getTime();
      const maxTime = items[items.length - 1].vade.getTime();
      const sameDay = maxTime === minTime;
      const span = Math.max(1, maxTime - minTime);
      const maxMiktar = Math.max(...items.map(i => i.miktar), 1);

      // Aynı aya denk gelenleri grupla — bunlar tek bir noktada, yan yana (yatayda piksel ofsetiyle) dizilecek
      const ayKeyOf = (d: Date) => d.getFullYear() * 12 + d.getMonth();
      const ayGruplari = new Map<number, typeof items>();
      items.forEach(i => {
        const key = ayKeyOf(i.vade);
        if (!ayGruplari.has(key)) ayGruplari.set(key, []);
        ayGruplari.get(key)!.push(i);
      });
      // Ayın ortasını (15'i) o ayın temsili konumu olarak kullan — böylece grup tek bir x konumunda toplanır
      const ayTemsiliZaman = (key: number) => new Date(Math.floor(key / 12), key % 12, 15).getTime();

      const BAR_GENISLIK = 14; // px, boşluk dahil
      timelineItems = items.map(i => {
        const ayKey = ayKeyOf(i.vade);
        const grup = ayGruplari.get(ayKey)!;
        const grupIcindekiSira = grup.indexOf(i);
        const offsetPx = (grupIcindekiSira - (grup.length - 1) / 2) * BAR_GENISLIK;
        const temsiliZaman = Math.min(maxTime, Math.max(minTime, ayTemsiliZaman(ayKey)));
        return {
          ...i,
          percent: sameDay ? 50 : ((temsiliZaman - minTime) / span) * 100,
          barPct: Math.max(12, (i.miktar / maxMiktar) * 100),
          offsetPx,
        };
      });
    }

    const todayInRange = items.length > 0 && today.getTime() >= items[0].vade.getTime() && today.getTime() <= items[items.length - 1].vade.getTime();
    const todayPercent = items.length > 0 ? ((today.getTime() - items[0].vade.getTime()) / Math.max(1, items[items.length - 1].vade.getTime() - items[0].vade.getTime())) * 100 : 0;

    const aylikMap = new Map<string, { key: string; label: string; toplam: number; adet: number; yearMonth: number }>();
    gecerliTaksitler.forEach(t => {
      const d = new Date(t.vadeTarihi);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
      const yearMonth = d.getFullYear() * 12 + d.getMonth();
      if (!aylikMap.has(key)) aylikMap.set(key, { key, label, toplam: 0, adet: 0, yearMonth });
      const rec = aylikMap.get(key)!;
      rec.toplam += t.miktar;
      rec.adet += 1;
    });
    const aylikDagilim = Array.from(aylikMap.values()).sort((a, b) => a.yearMonth - b.yearMonth);

    return { timelineItems, todayInRange, todayPercent, aylikDagilim, toplamTutar: gecerliTaksitler.reduce((s, t) => s + t.miktar, 0) };
  };

  // ── Hazır Ödeme Planı ────────────────────────────────────────────────────────

  // Hazır Ödeme Planı satırlarını, kilitli satırların mevcut tutarını (ve varsa manuel vade
  // tarihini) koruyarak; toplam tutardan kilitli satırların düşülmesiyle kalan miktarı,
  // kilitsiz satırlar arasında kendi (şablondan hesaplanan) oranlarına göre dağıtır.
  const redistributeHazirRows = (rows: HazirPlanRow[], toplamTutar: number): HazirPlanRow[] => {
    const round2 = (v: number) => Math.round(v * 100) / 100;
    const lockedSum = rows.filter(r => r.kilitli).reduce((s, r) => s + r.miktar, 0);
    const unlockedIdx = rows.reduce<number[]>((acc, r, i) => { if (!r.kilitli) acc.push(i); return acc; }, []);
    if (unlockedIdx.length === 0) return rows;
    const kalan = round2(toplamTutar - lockedSum);
    const unlockedFreshSum = unlockedIdx.reduce((s, i) => s + rows[i].miktar, 0);
    let dagitilan = 0;
    return rows.map((r, i) => {
      const pos = unlockedIdx.indexOf(i);
      if (pos === -1) return r;
      const isLast = pos === unlockedIdx.length - 1;
      const oran = unlockedFreshSum > 0 ? r.miktar / unlockedFreshSum : 1 / unlockedIdx.length;
      const m = isLast ? round2(kalan - dagitilan) : round2(kalan * oran);
      if (!isLast) dagitilan = round2(dagitilan + m);
      return { ...r, miktar: Math.max(0, m) };
    });
  };

  const generateHazirPlanRows = (sablon: HazirPlanSablon, miktarStr: string, prevRows: HazirPlanRow[] = []): HazirPlanRow[] => {
    const miktar = parseFloat(miktarStr) || 0;
    if (miktar <= 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const addMonths = (date: Date, months: number): Date => {
      const d = new Date(date);
      d.setMonth(d.getMonth() + months);
      return d;
    };
    const fmtTR = (d: Date): string =>
      `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    const round2 = (v: number) => Math.round(v * 100) / 100;

    const allRows: HazirPlanRow[] = [];

    // 1. Peşinat
    let pesinatMiktar = 0;
    if (sablon.pesinatTipi === 'Oran') {
      pesinatMiktar = round2(miktar * sablon.pesinatDegeri / 100);
    } else {
      pesinatMiktar = Math.min(sablon.pesinatDegeri, miktar);
    }
    if (pesinatMiktar > 0) {
      allRows.push({ odemeTuru: 'Pesinat', vadeTarihiStr: fmtTR(today), miktar: pesinatMiktar, aciklama: 'Peşinat' });
    }

    const kalanSonrasiPesinat = round2(miktar - pesinatMiktar);
    const sortedDetaylar = [...sablon.detaylar].sort((a, b) => a.vadeAyEndeksi - b.vadeAyEndeksi);

    // 2. Özel detaylar (AraOdeme, Teslimat vb.) — her vade tipinde hesapla
    let detaylarToplam = 0;
    let kalanDetay = kalanSonrasiPesinat;
    const detayRows: HazirPlanRow[] = [];
    sortedDetaylar.forEach((d, idx) => {
      let m = 0;
      if (d.hesaplamaYontemi === 'ToplaminYuzdesi') m = round2(miktar * d.deger / 100);
      else if (d.hesaplamaYontemi === 'KalaninYuzdesi') m = round2(kalanDetay * d.deger / 100);
      else m = d.deger; // SabitTutar
      m = Math.max(0, m);
      kalanDetay = round2(kalanDetay - m);
      detaylarToplam = round2(detaylarToplam + m);
      const label = HAZIR_PLAN_ODEME_TURU_LABEL[d.odemeTuru] || d.odemeTuru;
      detayRows.push({
        odemeTuru: d.odemeTuru,
        vadeTarihiStr: fmtTR(addMonths(today, d.vadeAyEndeksi)),
        miktar: m,
        aciklama: `${idx + 1}. ${label}`,
      });
    });

    // 3. Eşit taksitler — detaylar düşüldükten sonra kalan miktar
    //    Detayların kullandığı ay indeksleri taksit yerleşiminde atlanır
    //    Taksit adedi = MaksimumTaksitSayisi - detay satırı sayısı
    const taksitBase = round2(kalanSonrasiPesinat - detaylarToplam);
    const toplamTaksitAdedi = sablon.vadeTipi === 'SerbestOdeme' ? 0 : (sablon.maksimumTaksitSayisi || 0);
    const n = Math.max(0, toplamTaksitAdedi - sortedDetaylar.length);
    const detayAySet = new Set(sortedDetaylar.map(d => d.vadeAyEndeksi));

    if (n > 0 && taksitBase > 0) {
      const taksitMiktar = round2(taksitBase / n);
      let kalanT = taksitBase;
      let placed = 0;
      let ayIdx = 1;
      while (placed < n) {
        if (!detayAySet.has(ayIdx)) {
          const m = placed === n - 1 ? round2(kalanT) : taksitMiktar;
          kalanT = round2(kalanT - taksitMiktar);
          allRows.push({
            odemeTuru: 'Taksit',
            vadeTarihiStr: fmtTR(addMonths(today, ayIdx)),
            miktar: Math.max(0, m),
            aciklama: `${placed + 1}. Taksit`,
          });
          placed++;
        }
        ayIdx++;
      }
    } else if (sortedDetaylar.length === 0) {
      // SerbestOdeme ve detay yoksa tek kalem
      allRows.push({ odemeTuru: 'Taksit', vadeTarihiStr: fmtTR(addMonths(today, 1)), miktar: taksitBase, aciklama: '1. Taksit' });
    }

    // 4. Detay satırlarını ekle
    allRows.push(...detayRows);

    // 5. Vade tarihine göre sırala (peşinat her zaman başta, sonra vadeye göre)
    allRows.sort((a, b) => {
      if (a.odemeTuru === 'Pesinat') return -1;
      if (b.odemeTuru === 'Pesinat') return 1;
      const toMs = (s: string) => { const [d, m, y] = s.split('.'); return new Date(+y, +m - 1, +d).getTime(); };
      return toMs(a.vadeTarihiStr) - toMs(b.vadeTarihiStr);
    });

    // 6. Kilit durumunu uygula: önceki satırlarda kilitli olanların tutarı/vadesi korunur;
    //    hiç önceki karşılığı olmayan (ilk oluşturma) satırlarda Peşinat ve Ara Ödeme
    //    varsayılan olarak kilitli gelir.
    const prevMap = new Map<string, HazirPlanRow>();
    prevRows.forEach(r => prevMap.set(`${r.odemeTuru}|${r.aciklama}`, r));
    const merged = allRows.map(r => {
      const prev = prevMap.get(`${r.odemeTuru}|${r.aciklama}`);
      if (prev) {
        return prev.kilitli
          ? { ...r, miktar: prev.miktar, vadeTarihiStr: prev.vadeTarihiStr, kilitli: true }
          : { ...r, kilitli: false };
      }
      return { ...r, kilitli: r.odemeTuru === 'Pesinat' || r.odemeTuru === 'AraOdeme' };
    });

    return redistributeHazirRows(merged, miktar);
  };

  const calcHazirPlanAvgVade = (): number => {
    if (hazirPlanRows.length === 0) return 0;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let totalW = 0, totalM = 0;
    hazirPlanRows.forEach(r => {
      const [d, m, y] = r.vadeTarihiStr.split('.');
      const dt = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      const days = Math.max(0, Math.round((dt.getTime() - today.getTime()) / 86400000));
      totalW += days * r.miktar;
      totalM += r.miktar;
    });
    return totalM > 0 ? Math.round(totalW / totalM) : 0;
  };

  // Toplu Tahsilat Planla: sözleşmeye zaten eklenmiş tahsilatlar (iptal edilmeyen taksitler +
  // doğrudan tahsilatlar) ile bu modaldaki plan satırlarını birlikte ağırlıklandırarak,
  // sözleşme tarihinden itibaren ortalama vadeyi (gün) hesaplar.
  const calcTopluPlanlaOrtalamaVade = (): number => {
    if (!sozlesme?.sozlesmeTarihi) return 0;
    const sozlesmeTarihi = new Date(sozlesme.sozlesmeTarihi);
    let toplamAgirlikliGun = 0;
    let toplamTutar = 0;

    taksitler
      .filter(item => item.odemeDurumu !== 'Iptal' && item.vadeTarihi)
      .forEach(item => {
        const vade = new Date(item.vadeTarihi);
        const gun = Math.floor((vade.getTime() - sozlesmeTarihi.getTime()) / 86400000);
        toplamAgirlikliGun += gun * item.miktar;
        toplamTutar += item.miktar;
      });

    getDirectTahsilatlar().forEach(o => {
      const tarihStr = o.tahsilatTarihi || o.vadeTarihi || o.islemTarihi;
      if (!tarihStr) return;
      const tarih = new Date(tarihStr);
      const gun = Math.floor((tarih.getTime() - sozlesmeTarihi.getTime()) / 86400000);
      toplamAgirlikliGun += gun * o.miktar;
      toplamTutar += o.miktar;
    });

    topluPlanlaRows.forEach(r => {
      const [d, m, y] = r.vadeTarihiStr.split('.');
      const vade = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      const gun = Math.floor((vade.getTime() - sozlesmeTarihi.getTime()) / 86400000);
      toplamAgirlikliGun += gun * r.miktar;
      toplamTutar += r.miktar;
    });

    return toplamTutar > 0 ? Math.round(toplamAgirlikliGun / toplamTutar) : 0;
  };

  const handleOpenHazirPlanModal = async () => {
    setHazirPlanLoading(true);
    setShowHazirPlanModal(true);
    setSelectedHazirSablon(null);
    setHazirPlanRows([]);
    const kalan = calculateRemainingAmount();
    setHazirPlanMiktar(kalan > 0 ? String(Math.round(kalan * 100) / 100) : '');
    setHazirPlanOdemeTuru('Nakit');
    setHazirPlanTemizleMevcut(false);
    try {
      const res = await axiosInstance.get('/api/OdemePlanSablonlari');
      setHazirPlanSablonlar((res.data as HazirPlanSablon[]).filter(s => s.isActive));
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setHazirPlanLoading(false);
    }
  };

  const handleHazirSablonSelect = (sablon: HazirPlanSablon) => {
    setSelectedHazirSablon(sablon);
    setHazirPlanRows(generateHazirPlanRows(sablon, hazirPlanMiktar));
  };

  const handleHazirPlanMiktarChange = (val: string) => {
    if (selectedHazirSablon) {
      const yeniToplam = parseFloat(val) || 0;
      const kilitliToplam = hazirPlanRows.filter(r => r.kilitli).reduce((s, r) => s + r.miktar, 0);
      if (kilitliToplam > yeniToplam) {
        toast.error(t('sozlesmeDetay.kilitliTutarUyarisi', `Kilitli satırların toplamı (₺${kilitliToplam.toLocaleString('tr-TR')}) yeni toplam tutardan büyük. Önce ilgili satırların kilidini açın.`));
      }
    }
    setHazirPlanMiktar(val);
    if (selectedHazirSablon) {
      setHazirPlanRows(generateHazirPlanRows(selectedHazirSablon, val, hazirPlanRows));
    }
  };

  const handleDeleteHazirRow = (idx: number) => {
    const newRows = hazirPlanRows.filter((_, i) => i !== idx);
    const toplam = parseFloat(hazirPlanMiktar) || 0;
    setHazirPlanRows(redistributeHazirRows(newRows, toplam));
  };

  const handleAddHazirRow = () => {
    const fmtTR = (d: Date): string =>
      `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    const last = hazirPlanRows[hazirPlanRows.length - 1];
    let vade: Date;
    if (last) {
      const [d, m, y] = last.vadeTarihiStr.split('.');
      vade = new Date(+y, +m - 1, +d);
      vade.setMonth(vade.getMonth() + 1);
    } else {
      vade = new Date();
      vade.setMonth(vade.getMonth() + 1);
    }
    const newRow: HazirPlanRow = {
      odemeTuru: 'Taksit',
      vadeTarihiStr: fmtTR(vade),
      miktar: 0,
      aciklama: `${hazirPlanRows.length + 1}. Taksit`,
      kilitli: false,
    };
    const newRows = [...hazirPlanRows, newRow];
    const toplam = parseFloat(hazirPlanMiktar) || 0;
    setHazirPlanRows(redistributeHazirRows(newRows, toplam));
  };

  const handleHazirPlanSave = async () => {
    if (!sozlesme?.cari || !sozlesme?.sozlesmeID || hazirPlanRows.length === 0) return;
    setHazirPlanSaving(true);
    try {
      const taksitlerPayload = hazirPlanRows.map((row, idx) => {
        const [d, m, y] = row.vadeTarihiStr.split('.');
        return {
          vadeTarihi: `${y}-${m}-${d}`,
          miktar: row.miktar,
          odemeTuruDetay: row.odemeTuru,
          odemeTuru: hazirPlanOdemeTuru,
          aciklama: row.aciklama || `${idx + 1}. Ödeme`,
          siraNo: idx + 1,
        };
      });

      await axiosInstance.post('/api/OdemePlanTaksitleri/toplu', {
        sozlesmeID: sozlesme.sozlesmeID,
        cariID: sozlesme.cari.cariID,
        temizleMevcutPlan: hazirPlanTemizleMevcut,
        taksitler: taksitlerPayload,
      });

      await fetchTaksitler(sozlesme.sozlesmeID);
      toast.success(t('msg.islemBasarili'));
      setShowHazirPlanModal(false);
      setSelectedHazirSablon(null);
      setHazirPlanRows([]);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setHazirPlanSaving(false);
    }
  };

  const handleSaveTopluPlanla = async () => {
    try {
      if (!topluPlanlaFormData.taksitlendirilecekTutar || !topluPlanlaFormData.baslamaTarihi ||
          !topluPlanlaFormData.taksitSayisi || !topluPlanlaFormData.odemeTuru) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }
      if (!sozlesme?.cari?.cariID || !sozlesme?.sozlesmeID) {
        toast.error(t('msg.islemHatasi'));
        return;
      }
      if (topluPlanlaRows.length === 0) {
        toast.error('Taksit sayısı ve tutar 0\'dan büyük olmalıdır!');
        return;
      }

      setSaving(true);

      const taksitlerPayload = topluPlanlaRows.map((row, idx) => {
        const [d, m, y] = row.vadeTarihiStr.split('.');
        return {
          vadeTarihi: `${y}-${m}-${d}`,
          miktar: row.miktar,
          odemeTuruDetay: row.odemeTuru,
          odemeTuru: row.odemeTuru,
          aciklama: row.aciklama,
          siraNo: idx + 1,
        };
      });

      await axiosInstance.post('/api/OdemePlanTaksitleri/toplu', {
        sozlesmeID: sozlesme.sozlesmeID,
        cariID: sozlesme.cari.cariID,
        temizleMevcutPlan: topluPlanlaTemizleMevcut,
        taksitler: taksitlerPayload,
      });

      await fetchTaksitler(sozlesme.sozlesmeID);
      toast.success(t('msg.islemBasarili'));
      setShowTopluPlanlaModal(false);
      resetTopluPlanlaForm();
    } catch (error: any) {
      toast.error(t('msg.islemHatasi'));
      console.error('Toplu planla hatası:', error);
    } finally {
      setSaving(false);
    }
  };

  const fetchSablonlar = async () => {
    // Önce proje takip numarasını almamız gerekiyor
    if (!sozlesme?.daire?.blok?.proje?.projeID) {
      toast.error(t('msg.islemHatasi'));
      return;
    }

    try {
      setBelgelerLoading(true);
      // Önce proje detayını alarak takip numarasını öğrenelim
      const projeResponse = await axiosInstance.get(`/api/Projeler/${sozlesme.daire.blok.proje.projeID}`);
      const projeTakipNo = projeResponse.data.takipNo;
      
      // Şimdi şablonları getirelim
      const response = await axiosInstance.get(`/api/Dosyalar/takip/${projeTakipNo}`, {
        params: { dosyaTuru: 2 }
      });
      setSablonlar(response.data);
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      console.error('Şablon yükleme hatası:', error);
    } finally {
      setBelgelerLoading(false);
    }
  };

  // Sayıyı yazıya çeviren fonksiyon
  const numberToWords = (num: number): string => {
    if (num === 0) return 'Sıfır';
    
    const ones = ['', 'Bir', 'İki', 'Üç', 'Dört', 'Beş', 'Altı', 'Yedi', 'Sekiz', 'Dokuz'];
    const tens = ['', '', 'Yirmi', 'Otuz', 'Kırk', 'Elli', 'Altmış', 'Yetmiş', 'Seksen', 'Doksan'];
    const hundreds = ['', 'Yüz', 'İkiyüz', 'Üçyüz', 'Dörtyüz', 'Beşyüz', 'Altıyüz', 'Yediyüz', 'Sekizyüz', 'Dokuzyüz'];
    
    const convertThousands = (n: number): string => {
      let result = '';
      
      if (n >= 100) {
        result += hundreds[Math.floor(n / 100)] + ' ';
        n %= 100;
      }
      
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        const teens = ['On', 'Onbir', 'Oniki', 'Onüç', 'Ondört', 'Onbeş', 'Onaltı', 'Onyedi', 'Onsekiz', 'Ondokuz'];
        result += teens[n - 10] + ' ';
        n = 0;
      }
      
      if (n > 0) {
        result += ones[n] + ' ';
      }
      
      return result.trim();
    };
    
    if (num < 1000) {
      return convertThousands(num) + ' Türk Lirası';
    } else if (num < 1000000) {
      const thousands = Math.floor(num / 1000);
      const remainder = num % 1000;
      let result = '';
      
      if (thousands === 1) {
        result = 'Bin';
      } else {
        result = convertThousands(thousands) + ' Bin';
      }
      
      if (remainder > 0) {
        result += ' ' + convertThousands(remainder);
      }
      
      return result + ' Türk Lirası';
    } else {
      // Milyonlar için basit çözüm
      const millions = Math.floor(num / 1000000);
      const remainder = num % 1000000;
      let result = convertThousands(millions) + ' Milyon';
      
      if (remainder > 0) {
        if (remainder >= 1000) {
          const thousands = Math.floor(remainder / 1000);
          const lastPart = remainder % 1000;
          result += ' ' + convertThousands(thousands) + ' Bin';
          if (lastPart > 0) {
            result += ' ' + convertThousands(lastPart);
          }
        } else {
          result += ' ' + convertThousands(remainder);
        }
      }
      
      return result + ' Türk Lirası';
    }
  };

  // Ödeme planı tablosu oluşturan fonksiyon (Word XML formatında)
  const generateOdemeTablosu = (): string => {
    const direktTahsilatlarWord = getDirectTahsilatlar();
    if ((!taksitler || taksitler.length === 0) && direktTahsilatlarWord.length === 0) {
      return 'Henüz ödeme planı oluşturulmamıştır.';
    }

    // İptal edilmeyenleri al (hem ödendi hem bekliyor)
    const aktifOdemeler = taksitler
      .filter(item => item.odemeDurumu !== 'Iptal')
      .sort((a, b) => new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime());
    
    // Word XML tablo formatı
    let tablo = `<w:tbl>
      <w:tblPr>
        <w:tblStyle w:val="TableGrid"/>
        <w:tblW w:w="5000" w:type="pct"/>
        <w:tblLook w:val="04A0" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/>
      </w:tblPr>
      <w:tblGrid>
        <w:gridCol w:w="2000"/>
        <w:gridCol w:w="1500"/>
        <w:gridCol w:w="1500"/>
        <w:gridCol w:w="1000"/>
      </w:tblGrid>`;
    
    // Başlık satırı
    tablo += `
      <w:tr>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="D9D9D9"/></w:tcPr><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Vade Tarihi</w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="D9D9D9"/></w:tcPr><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Miktar</w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="D9D9D9"/></w:tcPr><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Ödeme Türü</w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="D9D9D9"/></w:tcPr><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Durum</w:t></w:r></w:p></w:tc>
      </w:tr>`;
    
    let odenenToplam = 0;
    let bekleyenToplam = 0;

    // Veri satırları
    aktifOdemeler.forEach(taksit => {
      const vadeTarihi = formatDate(taksit.vadeTarihi);
      const miktar = formatCurrency(taksit.miktar);
      const durum = taksit.odemeDurumu === 'Odendi' ? 'Ödendi' : 'Bekliyor';
      const odemeTuruStr = taksit.odemeTuru || taksit.odemeTuruDetay || '';

      tablo += `
        <w:tr>
          <w:tc><w:p><w:r><w:t>${vadeTarihi}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:r><w:t>${miktar}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:r><w:t>${odemeTuruStr}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:r><w:t>${durum}</w:t></w:r></w:p></w:tc>
        </w:tr>`;

      if (taksit.odemeDurumu === 'Odendi') odenenToplam += taksit.miktar;
      else bekleyenToplam += taksit.miktar;
    });

    // Direkt tahsilatlar (İşlemler'den eklenenler)
    direktTahsilatlarWord.forEach(o => {
      const tarih = o.tahsilatTarihi || o.islemTarihi;
      const tarihStr = tarih ? new Date(tarih).toLocaleDateString('tr-TR') : '';
      const miktar = formatCurrency(o.miktar);
      tablo += `
        <w:tr>
          <w:tc><w:p><w:r><w:t>${tarihStr}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:r><w:t>${miktar}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:r><w:t>${o.odemeTuru || ''}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:r><w:t>Ödendi</w:t></w:r></w:p></w:tc>
        </w:tr>`;
      odenenToplam += o.miktar;
    });

    // Özet satırları
    tablo += `
      <w:tr>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="D9EAD3"/></w:tcPr><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>ÖDENDİ TOPLAM</w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="D9EAD3"/></w:tcPr><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>${formatCurrency(odenenToplam)}</w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="D9EAD3"/></w:tcPr><w:p><w:r><w:t></w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="D9EAD3"/></w:tcPr><w:p><w:r><w:t></w:t></w:r></w:p></w:tc>
      </w:tr>
      <w:tr>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="FFF2CC"/></w:tcPr><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>BEKLİYOR TOPLAM</w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="FFF2CC"/></w:tcPr><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>${formatCurrency(bekleyenToplam)}</w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="FFF2CC"/></w:tcPr><w:p><w:r><w:t></w:t></w:r></w:p></w:tc>
        <w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="FFF2CC"/></w:tcPr><w:p><w:r><w:t></w:t></w:r></w:p></w:tc>
      </w:tr>
    </w:tbl>`;
    
    return tablo;
  };

  /** Depolanan "<guid>_Gerçek Ad.docx" adından GUID önekini atar. */
  const sablonGoruntuAdi = (depolananAd: string): string => {
    const altCizgi = depolananAd.indexOf('_');
    return altCizgi > -1 ? depolananAd.slice(altCizgi + 1) : depolananAd;
  };

  /** İndirilen sözleşmenin dosya adı: "Şablon Adı - Müşteri Adı.docx".
   *  Depolanan ad "<guid>_Gerçek Ad.docx" biçiminde olduğu için GUID öneki atılır. */
  const sozlesmeDosyaAdi = (depolananAd: string, musteriAdi: string): string => {
    const altCizgi = depolananAd.indexOf('_');
    const guidsiz = altCizgi > -1 ? depolananAd.slice(altCizgi + 1) : depolananAd;
    const uzantiIdx = guidsiz.lastIndexOf('.');
    const sablonAdi = (uzantiIdx > 0 ? guidsiz.slice(0, uzantiIdx) : guidsiz).trim();
    const uzanti = uzantiIdx > 0 ? guidsiz.slice(uzantiIdx) : '.docx';
    // Windows'ta dosya adında kullanılamayan karakterleri temizle
    const temizle = (s: string) => s.replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();
    return `${temizle(sablonAdi)} - ${temizle(musteriAdi)}${uzanti}`;
  };

  /** Word XML'ine metin gömerken & < > " ' karakterlerini kaçırır.
   *  Kaçırılmazsa document.xml bozulur; Word dosyayı "onarır" ve sayfa düzeni kayar. */
  const xmlKacis = (deger: string | null | undefined): string =>
    String(deger ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');

  /** Çok satırlı metni Word'ün satır sonu etiketiyle yazar. */
  const xmlCokSatir = (deger: string | null | undefined): string =>
    xmlKacis(deger).replace(/\r?\n/g, '</w:t><w:br/><w:t>');

  /** Yer tutucuyu İÇEREN paragrafın TAMAMINI verilen XML ile değiştirir.
   *  Bazı yer tutucuların yerine paragraf içine konulamayan içerik gelir:
   *  tablo (<w:tbl>) bir paragrafın (<w:p>) içine yazılamaz, sayfa sonu da
   *  paragrafın kendisine uygulanır. Yer tutucu metnini tek başına değiştirmek
   *  geçersiz belge üretiyor ve sayfa kaymalarına yol açıyordu. */
  const yerTutucuParagrafiniDegistir = (icerik: string, yerTutucu: string, yeniXml: string): string => {
    let sonuc = icerik;
    let guvenlikSayaci = 0;

    while (sonuc.includes(yerTutucu) && guvenlikSayaci++ < 20) {
      const idx = sonuc.indexOf(yerTutucu);

      // Paragraf başlangıcı "<w:p>" veya "<w:p ...>" olmalı.
      // Düz lastIndexOf('<w:p') kullanılamaz: <w:pPr>, <w:pStyle> gibi etiketlerle de
      // eşleşip belgeyi ortasından kesiyor ve bozuk XML üretiyordu.
      const paragrafBasi = /<w:p(?=[\s>])/g;
      let eslesme: RegExpExecArray | null;
      let pBasla = -1;
      while ((eslesme = paragrafBasi.exec(sonuc)) !== null) {
        if (eslesme.index >= idx) break;
        pBasla = eslesme.index;
      }

      const pBitisIdx = sonuc.indexOf('</w:p>', idx);

      if (pBasla === -1 || pBitisIdx === -1) {
        // Paragraf sınırı bulunamadıysa en azından yer tutucuyu temizle
        sonuc = sonuc.replace(yerTutucu, '');
        continue;
      }

      const pBitis = pBitisIdx + '</w:p>'.length;
      sonuc = sonuc.slice(0, pBasla) + yeniXml + sonuc.slice(pBitis);
    }
    return sonuc;
  };

  /** [SAYFASONU] yer tutucusunun yerine geçen paragraf.
   *  pageBreakBefore, kırılmayı bir SONRAKİ sayfaya taşır; paragrafın kendisi
   *  1 twip yüksekliğinde ve 1pt yazı boyutunda olduğu için yeni sayfanın
   *  tepesinde yer kaplamaz. Yer tutucunun bulunduğu sayfadan yükseklik
   *  ÇALMAMASI kritik: amaç o sayfayı taşırmadan kesmek. */
  const SAYFA_SONU_XML =
    '<w:p><w:pPr><w:pageBreakBefore/>' +
    '<w:spacing w:before="0" w:after="0" w:line="20" w:lineRule="exact"/>' +
    '<w:rPr><w:sz w:val="2"/><w:szCs w:val="2"/></w:rPr></w:pPr></w:p>';

  /** [PESINAT] / [KAPORA] etiketleri için: açıklaması verilen anahtar kelimeyle başlayan
   *  (iptal edilmemiş) taksitlerin ve doğrudan girilen tahsilatların tutarları toplanır. */
  const sumAciklamaTutari = (anahtar: string): number => {
    const eslesir = (aciklama?: string) => (aciklama || '').trim().toLowerCase().startsWith(anahtar.toLowerCase());
    const taksitToplam = taksitler
      .filter(t => t.odemeDurumu !== 'Iptal' && eslesir(t.aciklama))
      .reduce((s, t) => s + t.miktar, 0);
    const direktToplam = getDirectTahsilatlar()
      .filter(o => eslesir(o.aciklama))
      .reduce((s, o) => s + o.miktar, 0);
    return taksitToplam + direktToplam;
  };

  /** [TAKSITPLANI1] / [TAKSITPLANI2]: ödeme planındaki (iptal edilmemiş taksitler + direkt
   *  tahsilatlar — [ODEMEPLANI] tablosuyla aynı kapsam) kalemleri tutarına göre gruplar,
   *  "3x300.000TL, 1x330.000TL" biçiminde, tutara göre artan sırada döner.
   *  pesinatKaporaHaric: açıklamasında Peşinat veya Kapora/Kaparo geçen kalemleri dışarıda bırakır. */
  const taksitPlaniOzeti = (pesinatKaporaHaric: boolean): string => {
    const haricMi = (aciklama?: string) => {
      const a = (aciklama || '').toLocaleLowerCase('tr-TR');
      return a.includes('peşinat') || a.includes('pesinat') || a.includes('kapora') || a.includes('kaparo');
    };
    const kalemler = [
      ...taksitler.filter(t => t.odemeDurumu !== 'Iptal').map(t => ({ miktar: t.miktar, aciklama: t.aciklama })),
      ...getDirectTahsilatlar().map(o => ({ miktar: o.miktar, aciklama: o.aciklama })),
    ].filter(k => !(pesinatKaporaHaric && haricMi(k.aciklama)));

    const gruplar = new Map<number, number>();
    kalemler.forEach(k => {
      const tutar = Math.round((k.miktar || 0) * 100) / 100;
      gruplar.set(tutar, (gruplar.get(tutar) || 0) + 1);
    });
    const fmt = (n: number) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
    return Array.from(gruplar.entries())
      .sort(([a], [b]) => a - b)
      .map(([tutar, adet]) => `${adet}x${fmt(tutar)}TL`)
      .join(', ');
  };

  const processDocxContent = async (docxBlob: Blob, musteriAdi: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        const fileReader = new FileReader();
        
        fileReader.onload = function(e) {
          try {
            const arrayBuffer = e.target?.result as ArrayBuffer;
            const zip = new PizZip(arrayBuffer);
            
            // document.xml dosyasını oku
            const documentXml = zip.file("word/document.xml");
            if (!documentXml) {
              throw new Error('document.xml bulunamadı');
            }
            
            let content = documentXml.asText();
            
            // Tüm placeholder'ları değiştir (Global replace - sayfadaki tüm örnekleri)
            if (sozlesme) {
              console.log('Placeholder değiştirme işlemi başlatıldı...');
              
              // Değiştirme öncesi placeholder sayılarını say
              const countPlaceholders = (pattern: string) => (content.match(new RegExp(pattern.replace(/[[\]]/g, '\\$&'), 'g')) || []).length;
              
              console.log('Bulunan placeholder sayıları:');
              console.log('MUSTERIADI:', countPlaceholders('[MUSTERIADI]'));
              console.log('MUSTERITELEFON:', countPlaceholders('[MUSTERITELEFON]'));
              console.log('MUSTERIEPOSTA:', countPlaceholders('[MUSTERIEPOSTA]'));
              console.log('BLOK:', countPlaceholders('[BLOK]'));
              
              // Müşteri bilgileri - TÜM örnekleri değiştir
              const musteriTelefon = sozlesme.cari?.telefon || sozlesme.cari?.telefon2 || '';
              const musteriEmail = sozlesme.cari?.email || '';
              
              // Metin yer tutucuları — değerler XML'e gömülmeden önce kaçırılır
              content = content.replace(/\[MUSTERIADI\]/g, xmlKacis(sozlesme.cari?.adiSoyadi));
              content = content.replace(/\[MUSTERITC\]/g, xmlKacis(sozlesme.cari?.tc));
              content = content.replace(/\[MUSTERIADRES\]/g, xmlCokSatir(sozlesme.cari?.adres));
              content = content.replace(/\[MUSTERITELEFON\]/g, xmlKacis(musteriTelefon));
              content = content.replace(/\[MUSTERIEPOSTA\]/g, xmlKacis(musteriEmail));

              // Daire bilgileri - TÜM örnekleri değiştir
              const blokAdi = sozlesme.daire?.blok?.blokAdi || '';
              content = content.replace(/\[DAIRENO\]/g, xmlKacis(sozlesme.daire?.daireNo));
              content = content.replace(/\[DAIRETIPI\]/g, xmlKacis(sozlesme.daire?.daireTip?.tip));
              content = content.replace(/\[BLOK\]/g, xmlKacis(blokAdi));
              content = content.replace(/\[KAT\]/g, xmlKacis(sozlesme.daire?.kat?.katAdi));
              content = content.replace(/\[CEPHE\]/g, xmlKacis(sozlesme.daire?.cephe?.cepheAdi));
              content = content.replace(/\[MANZARA\]/g, xmlKacis(sozlesme.daire?.manzara?.manzaraAdi));

              // Sözleşme bilgileri - TÜM örnekleri değiştir
              content = content.replace(/\[SOZLESMETARIHI\]/g, xmlKacis(formatDate(sozlesme.sozlesmeTarihi)));
              if (sozlesme.sozlesmeTarihi) {
                const birGunOnce = new Date(sozlesme.sozlesmeTarihi);
                birGunOnce.setDate(birGunOnce.getDate() - 1);
                content = content.replace(/\[SOZLESMEDENBIRGUNONCE\]/g, xmlKacis(birGunOnce.toLocaleDateString('tr-TR')));
              } else {
                content = content.replace(/\[SOZLESMEDENBIRGUNONCE\]/g, '');
              }
              content = content.replace(/\[TAKSITPLANI1\]/g, xmlKacis(taksitPlaniOzeti(false)));
              content = content.replace(/\[TAKSITPLANI2\]/g, xmlKacis(taksitPlaniOzeti(true)));
              content = content.replace(/\[SATISFIYATI\]/g, xmlKacis(formatCurrency(sozlesme.sozlesmeFiyati)));
              content = content.replace(/\[VADELIODEMETUTAR\]/g, xmlKacis(formatCurrency(sozlesme.sozlesmeFiyati)));

              content = content.replace(/\[FIYATYAZI\]/g, xmlKacis(numberToWords(sozlesme.sozlesmeFiyati)));
              content = content.replace(/\[EKSARTLAR\]/g, xmlCokSatir(sozlesme.ozelSartlar));

              // Peşinat / Kapora toplamları — açıklaması "Peşinat"/"Kapora" ile başlayan kalemlerin toplamı
              content = content.replace(/\[PESINAT\]/g, xmlKacis(formatCurrency(sumAciklamaTutari('Peşinat'))));
              content = content.replace(/\[KAPORA\]/g, xmlKacis(formatCurrency(sumAciklamaTutari('Kapora'))));

              // Sayfa sonu — şablonda sayfanın bitmesi istenen yere [SAYFASONU] yazılır.
              // Şablonların sayfa düzeni içerik uzunluğuna bağlı: örneğin müşteri adresi
              // iki satıra sarınca sayfanın son satırı bir sonraki sayfaya kayıyor ve
              // sonraki tüm sayfalar birlikte kayıyordu. Açık sayfa sonu bunu sabitler.
              content = yerTutucuParagrafiniDegistir(content, '[SAYFASONU]', SAYFA_SONU_XML);

              // Ödeme planı tablosu — yer tutucuyu içeren PARAGRAFIN tamamı tabloyla değişir.
              // Tablodan sonra boş paragraf şart: Word, tabloyu takip eden paragraf yoksa
              // sonraki içerikle birleştirip düzeni bozuyor.
              const odemeTablosu = generateOdemeTablosu();
              if (odemeTablosu.trim().startsWith('<w:tbl')) {
                content = yerTutucuParagrafiniDegistir(content, '[ODEMEPLANI]', odemeTablosu + '<w:p/>');
              } else {
                // Ödeme planı yoksa düz metin dönüyor; paragrafı bozmadan yerine yazılır
                content = content.replace(/\[ODEMEPLANI\]/g, xmlKacis(odemeTablosu));
              }
              
              console.log('✅ Tüm placeholder\'lar başarıyla değiştirildi!');
            }
            
            // Güncellenmiş içeriği zip'e geri yaz
            zip.file("word/document.xml", content);
            
            // Yeni dosyayı oluştur
            const updatedDocx = zip.generate({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
            resolve(updatedDocx);
            
          } catch (error) {
            reject(error);
          }
        };
        
        fileReader.onerror = () => reject(new Error('Dosya okunamadı'));
        fileReader.readAsArrayBuffer(docxBlob);
        
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleSenetleriIndir = async () => {
    if (!sozlesme || !taksitler || taksitler.length === 0) {
      toast.warning(t('msg.uyari'));
      return;
    }

    try {
      // Cari bilgilerini console'a yaz
      console.log('=== SENET İNDİRME - CARİ BİLGİLERİ ===');
      console.log('Müşteri Adı:', sozlesme.cari?.adiSoyadi || 'Belirtilmemiş');
      console.log('Müşteri Adresi:', sozlesme.cari?.adres || 'Belirtilmemiş');
      console.log('Müşteri TC:', sozlesme.cari?.tc || 'Belirtilmemiş');
      console.log('Telefon:', sozlesme.cari?.telefon || 'Belirtilmemiş');
      console.log('Email:', sozlesme.cari?.email || 'Belirtilmemiş');
      console.log('Firma Adı:', sozlesme.cari?.firmaAdi || 'Belirtilmemiş');
      console.log('Vergi Dairesi:', sozlesme.cari?.cariVD || 'Belirtilmemiş');
      console.log('Vergi No:', sozlesme.cari?.cariVN || 'Belirtilmemiş');
      console.log('Cari Türü:', sozlesme.cari?.turu || 'Belirtilmemiş');
      console.log('=======================================');
      
      toast.info(t('msg.bilgi'), { autoClose: 3000 });
      
      // Sadece "Senet" türündeki aktif, iptal edilmemiş ve henüz tahsil edilmemiş (bekleyen) taksitleri al
      const senetOdemeleri = taksitler
        .filter(taksit =>
          taksit.aktifMi &&
          taksit.odemeDurumu !== 'Odendi' &&
          taksit.odemeDurumu !== 'Iptal' &&
          (taksit.odemeTuru === 'Senet' || taksit.odemeTuruDetay === 'Senet')
        )
        .sort((a, b) => new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime());

      // Senet türündeki taksitleri console'a yaz
      console.log('=== SENET TÜRÜNDEKİ TAKSİTLER ===');
      console.log('Toplam Senet Sayısı:', senetOdemeleri.length);
      senetOdemeleri.forEach((taksit, index) => {
        console.log(`--- Senet ${index + 1} ---`);
        console.log('Taksit ID:', taksit.taksitID);
        console.log('Vade Tarihi:', taksit.vadeTarihi);
        console.log('Miktar:', taksit.miktar);
        console.log('Ödeme Türü:', taksit.odemeTuru || taksit.odemeTuruDetay);
        console.log('Ödeme Durumu:', taksit.odemeDurumu);
        console.log('Açıklama:', taksit.aciklama || 'Yok');
        console.log('Aktif Mi:', taksit.aktifMi);
      });
      console.log('===============================');

      if (senetOdemeleri.length === 0) {
        toast.warning(t('msg.uyari'));
        return;
      }

      // Backend'den PDF al - Cari bilgilerini ve senet ödemelerini POST ile gönder
      const cariData = {
        MusteriAdi: sozlesme.cari?.adiSoyadi || 'Belirtilmemiş',
        MusteriAdres: sozlesme.cari?.adres || 'Belirtilmemiş',
        MusteriTC: sozlesme.cari?.tc || 'Belirtilmemiş'
      };

      const senetData = {
        cariData: cariData,
        senetOdemeleri: senetOdemeleri.map((taksit, index) => ({
          odemeID: taksit.taksitID,
          vadeTarihi: taksit.vadeTarihi,
          miktar: taksit.miktar,
          siraNo: index + 1,
          takipNo: sozlesme.sozlesmeTakipNo
        }))
      };

      const response = await axiosInstance.post(`/api/Senetler/sozlesme/${sozlesme.sozlesmeID}`, senetData, {
        responseType: 'blob'
      });

      // PDF'i indir
      const fileName = `Senetler_${sozlesme.sozlesmeTakipNo}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, fileName);
      
      toast.success(t('msg.islemBasarili'));
      
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      console.error('Senet indirme hatası:', error);
    }
  };

  const generateHazirSozlesmePDF = async () => {
    if (!sozlesme) return;
    setHazirSozlesmeLoading(true);
    try {
      let kurumsal: any = null;
      try { const r = await axiosInstance.get('/api/Kurumsal/1'); kurumsal = r.data; } catch {}
      setHazirSozlesmeKurumsal(kurumsal);
      setShowHazirSozlesme(true);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setHazirSozlesmeLoading(false);
    }
  };

  const downloadHazirSozlesmePDF = async () => {
    if (!hazirSozlesmeRef.current) return;
    toast.info(t('msg.bilgi'), { autoClose: 2000 });
    try {
      const el = hazirSozlesmeRef.current;
      // Temporarily remove box-shadow/border for clean capture
      const prevBoxShadow = el.style.boxShadow;
      const prevBorder = el.style.border;
      const prevBorderRadius = el.style.borderRadius;
      el.style.boxShadow = 'none';
      el.style.border = 'none';
      el.style.borderRadius = '0';

      const canvas = await html2canvas(el, { scale: 2.5, useCORS: true, backgroundColor: '#ffffff', logging: false });

      el.style.boxShadow = prevBoxShadow;
      el.style.border = prevBorder;
      el.style.borderRadius = prevBorderRadius;

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10; // mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const printableW = pageWidth - margin * 2;
      const printableH = pageHeight - margin * 2;

      // px per mm based on canvas
      const pxPerMm = canvas.width / printableW;
      const sliceHeightPx = Math.floor(printableH * pxPerMm);
      const totalPages = Math.ceil(canvas.height / sliceHeightPx);

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        const srcY = page * sliceHeightPx;
        const srcH = Math.min(sliceHeightPx, canvas.height - srcY);

        // Create a slice canvas for this page
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        }

        const sliceData = sliceCanvas.toDataURL('image/png');
        const sliceHeightMm = (srcH / pxPerMm);
        pdf.addImage(sliceData, 'PNG', margin, margin, printableW, sliceHeightMm);
      }

      pdf.save(`Sozlesme_${sozlesme?.sozlesmeTakipNo || 'belge'}.pdf`);
      toast.success('PDF indirildi!');
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleShowUpdateModal = () => {
    if (!sozlesme) return;

    // Mevcut verileri forma yükle
    setUpdateFormData({
      sozlesmeDurumu: sozlesme.sozlesmeDurumu || '',
      sozlesmeTarihi: sozlesme.sozlesmeTarihi ? sozlesme.sozlesmeTarihi.split('T')[0] : '',
      rezerveBitisTarihi: sozlesme.rezerveBitisTarihi ? sozlesme.rezerveBitisTarihi.split('T')[0] : '',
      sozlesmeFiyati: sozlesme.sozlesmeFiyati?.toString() || '',
      ozelSartlar: sozlesme.ozelSartlar || ''
    });
    
    const sozlesmeTarihiISO = sozlesme.sozlesmeTarihi ? sozlesme.sozlesmeTarihi.split('T')[0] : '';
    const rezerveISO = sozlesme.rezerveBitisTarihi ? sozlesme.rezerveBitisTarihi.split('T')[0] : '';
    setUpdateSozlesmeTarihiDisplay(toTRDate(sozlesmeTarihiISO));
    setUpdateRezerveTarihiDisplay(toTRDate(rezerveISO));
    setShowUpdateModal(true);
  };

  const handleUpdateFormChange = (field: string, value: string) => {
    setUpdateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSozlesmeUpdate = async () => {
    if (!sozlesme) return;

    try {
      // Form validasyonu
      if (!updateFormData.sozlesmeDurumu || !updateFormData.sozlesmeTarihi || !updateFormData.sozlesmeFiyati) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }

      setSaving(true);

      const updateData = {
        sozlesmeID: sozlesme.sozlesmeID,
        daireID: sozlesme.daire?.daireID,
        cariID: sozlesme.cari?.cariID,
        eklemeTarihi: sozlesme.eklemeTarihi,
        sozlesmeTarihi: updateFormData.sozlesmeTarihi,
        rezerveBitisTarihi: updateFormData.rezerveBitisTarihi || null,
        sozlesmeDurumu: updateFormData.sozlesmeDurumu,
        sozlesmeFiyati: parseFloat(updateFormData.sozlesmeFiyati),
        planlananOdeme: sozlesme.planlananOdeme,
        ortalamaVade: sozlesme.ortalamaVade,
        aktifMi: sozlesme.aktifMi,
        sozlesmeTakipNo: sozlesme.sozlesmeTakipNo,
        ozelSartlar: updateFormData.ozelSartlar || null
      };

      console.log('Güncellenecek sözleşme verisi:', updateData);
      
      await axiosInstance.put(`/api/Sozlesmeler/${sozlesme.sozlesmeID}`, updateData);
      
      toast.success(t('msg.islemBasarili'));
      setShowUpdateModal(false);
      
      // Sözleşme detaylarını yeniden yükle
      await fetchSozlesmeDetay();
      
    } catch (error: any) {
      toast.error(t('msg.islemHatasi'));
      console.error('Sözleşme güncelleme hatası:', error);
      console.error('Hata detayı:', error?.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleSozlesmeIptal = async () => {
    if (!sozlesme) return;
    
    try {
      setSaving(true);
      
      console.log('İptal işlemi başlatıldı - Sözleşme ID:', sozlesme.sozlesmeID);
      console.log('Daire ID:', sozlesme.daire?.daireID);
      console.log('Takip No:', sozlesme.sozlesmeTakipNo);
      
      // 1. Dairenin DaireDurumu'nu 0 yap
      if (sozlesme.daire) {
        console.log('Daire durumu güncelleniyor...');
        await axiosInstance.post(`/api/Daireler/update-durum/${sozlesme.daire.daireID}`, {
          DaireDurumu: 0  // Backend PascalCase bekliyor
        });
        console.log('Daire durumu başarıyla güncellendi');
      }
      
      // 2. Sözleşmeye ait tüm ödemelerin AktifMi değerini false yap
      console.log('Ödemeler iptal ediliyor...');
      await axiosInstance.post(`/api/Odemeler/iptal-sozlesme/${sozlesme.sozlesmeTakipNo}`);
      console.log('Ödemeler başarıyla iptal edildi');
      
      // 3. Sözleşmenin AktifMi değerini false yap
      console.log('Sözleşme iptal ediliyor...');
      await axiosInstance.post('/api/Sozlesmeler/iptal-takip', {
        takipNo: sozlesme.sozlesmeTakipNo
      });
      console.log('Sözleşme başarıyla iptal edildi');
      
      toast.success(t('msg.islemBasarili'));
      setShowIptalModal(false);
      
      // Sayfayı yenile veya geri dön
      navigate('/sozlesmeler');
      
    } catch (error) {
      console.error('İşlem hatası:', error);
      toast.error(t('msg.islemHatasi'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSablon = async (sablon: Sablon) => {
    try {
      toast.info(t('msg.bilgi'), { autoClose: 1000 });
      
      const response = await axiosInstance.get(`/api/Dosyalar/download/${sablon.dosyaID}`, {
        responseType: 'blob'
      });
      
      const fileExtension = sablon.dosyaAdi.split('.').pop()?.toLowerCase();
      const musteriAdi = sozlesme?.cari?.adiSoyadi || 'Müşteri';
      
      if (fileExtension === 'docx') {
        try {
          // DOCX dosyasını işle ve placeholder'ları değiştir
          const processedBlob = await processDocxContent(response.data, musteriAdi);
          
          // İşlenmiş dosyayı indir — "Şablon Adı - Müşteri Adı.docx"
          saveAs(processedBlob, sozlesmeDosyaAdi(sablon.dosyaAdi, musteriAdi));
          
          toast.success(t('msg.islemBasarili'));
        } catch (error) {
          console.error('DOCX işleme hatası:', error);
          toast.error(t('msg.islemHatasi'));
          
          // Hata durumunda orijinal dosyayı indir
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', sablonGoruntuAdi(sablon.dosyaAdi));
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      } else {
        // DOCX olmayan dosyalar için normal indirme
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', sablonGoruntuAdi(sablon.dosyaAdi));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('Dosya indirildi!');
      }
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      console.error('Dosya indirme hatası:', error);
    }
  };

  const handlePreviewSablon = async (sablon: Sablon) => {
    try {
      const ext = sablon.dosyaAdi.toLowerCase().split('.').pop() || '';
      const isPdf = ext === 'pdf';
      const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);

      if (!isPdf && !isImage) {
        toast.info(t('msg.bilgi'));
        handleDownloadSablon(sablon);
        return;
      }

      const response = await axiosInstance.get(`/api/Dosyalar/download/${sablon.dosyaID}`, {
        responseType: 'blob',
        headers: {
          'Accept': isPdf ? 'application/pdf' : 'image/*'
        }
      });

      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      const url = window.URL.createObjectURL(blob);
      
      setPreviewFile({
        url: url,
        type: isPdf ? 'pdf' : 'image',
        name: sablon.aciklama || sablon.dosyaAdi
      });
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Dosya önizleme hatası:', error);
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleClosePreview = () => {
    if (previewFile?.url) {
      window.URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
    setShowPreviewModal(false);
  };

  const fetchImalatKategorilerSD = async () => {
    if (!sozlesme?.daire?.blok?.proje?.projeID) return;
    try {
      const res = await axiosInstance.get(`/api/ImalatKategorileri?projeID=${sozlesme.daire.blok.proje.projeID}`);
      setImalatKategorilerSD(res.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const fetchImalatNotlariSD = async () => {
    if (!sozlesme?.sozlesmeID) return;
    try {
      const res = await axiosInstance.get(`/api/ImalatNotlari/sozlesme/${sozlesme.sozlesmeID}`);
      setImalatNotlariSD(res.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const openHareketlerModal = async () => {
    if (!sozlesme) return;
    setShowHareketlerModal(true);
    setHareketlerLoading(true);
    try {
      const res = await axiosInstance.get(`/api/LogKayitlari/takip/${sozlesme.sozlesmeTakipNo}`);
      setHareketler(res.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setHareketlerLoading(false);
    }
  };

  const openImalatNotlariModal = async () => {
    setImalatNotlariLoading(true);
    setShowImalatNotlariModal(true);
    try {
      await Promise.all([fetchImalatKategorilerSD(), fetchImalatNotlariSD()]);
    } finally {
      setImalatNotlariLoading(false);
    }
  };

  const handleImalatNotKaydet = async () => {
    if (!sozlesme || !imalatNotForm) return;
    const kat = imalatKategorilerSD.find(k => k.imalatKatID === imalatNotForm.katID);
    if (!kat) return;
    let degerVal = '';
    if (['TekSecim','CokSecim'].includes(kat.alanTipi)) {
      degerVal = JSON.stringify(imalatNotForm.secimler);
    } else {
      degerVal = imalatNotForm.deger;
    }
    if (!degerVal.trim() && imalatNotForm.secimler.length === 0) { toast.error(t('msg.islemHatasi')); return; }
    try {
      if (imalatNotEditID) {
        await axiosInstance.post(`/api/ImalatNotlari/${imalatNotEditID}/update`, { imalatKatID: imalatNotForm.katID, deger: degerVal, aciklama: imalatNotForm.aciklama });
      } else {
        await axiosInstance.post('/api/ImalatNotlari', { sozlesmeID: sozlesme.sozlesmeID, imalatKatID: imalatNotForm.katID, deger: degerVal, aciklama: imalatNotForm.aciklama, ekleyenKullaniciID: user?.kullaniciID });
      }
      toast.success(t('msg.islemBasarili'));
      setImalatNotForm(null);
      setImalatNotEditID(null);
      fetchImalatNotlariSD();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleImalatNotDelete = async (id: number) => {
    if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
    try {
      await axiosInstance.delete(`/api/ImalatNotlari/${id}`);
      toast.success(t('msg.islemBasarili'));
      fetchImalatNotlariSD();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleShowBelgelerModal = () => {
    setShowBelgelerModal(true);
    fetchSablonlar();
  };

  const getDurumConfig = (durum: string) => {
    switch (durum) {
      case 'Rezerve': return { color: '#b45309', bg: '#fef3c7', border: '#f59e0b', label: 'Rezerve' };
      case 'Sözleşme': return { color: '#1d4ed8', bg: '#dbeafe', border: '#3b82f6', label: 'Sözleşme' };
      case 'ArsaSahibi': return { color: '#6d28d9', bg: '#ede9fe', border: '#8b5cf6', label: 'Arsa Sahibi' };
      case 'Taşeron': return { color: '#0369a1', bg: '#e0f2fe', border: '#0ea5e9', label: 'Taşeron' };
      default: return { color: '#374151', bg: '#f3f4f6', border: '#9ca3af', label: durum };
    }
  };

  if (loading) {
    return (
      <div style={{ background: '#f8fafc', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #f1f5f9', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
          <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>{t('sozlesmeDetay.yukleniyor', 'Sözleşme yükleniyor...')}</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!sozlesme) {
    return (
      <div style={{ background: '#f8fafc', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: '48px 56px', textAlign: 'center', boxShadow: '0 4px 24px rgba(15,23,42,0.08)', border: '1.5px solid #f1f5f9' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📄</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{t('sozlesmeDetay.bulunamadi', 'Sözleşme Bulunamadı')}</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 24 }}>{t('sozlesmeDetay.bulunamadiMesaj', 'Belirtilen takip numarasına ait sözleşme bulunamadı.')}</div>
          <button onClick={() => navigate('/sozlesmeler')} style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>← {t('sozlesmeDetay.geriDon', 'Sözleşmelere Dön')}</button>
        </div>
      </div>
    );
  }

  const durumCfg = getDurumConfig(sozlesme.sozlesmeDurumu);
  const odenenTutar = calculateOdenenTutar();
  const bekleyenTutar = calculateBekleyenTutar();
  const kalanOdeme = calculateRemainingAmount();
  const planlanmamisTutar = calculatePlanlanmamisTutar();
  const planlananTutar = calculatePlanlananTutar();
  const sozlesmeFiyati = sozlesme.sozlesmeFiyati || 1;
  const odenenPct = Math.min(100, Math.round((odenenTutar / sozlesmeFiyati) * 100));

  // --- Ödeme Planı Rapor Önizleme (in-app) ---
  if (showOdemePlaniRapor && sozlesme) {
    const aktifOdemeler = taksitler
      .filter(item => item.odemeDurumu !== 'Iptal')
      .sort((a, b) => (a.vadeTarihi ? new Date(a.vadeTarihi).getTime() : 0) - (b.vadeTarihi ? new Date(b.vadeTarihi).getTime() : 0));
    const direktTahsilatlarRapor = getDirectTahsilatlar();

    const toplamTahsilat = aktifOdemeler
      .filter(item => item.odemeDurumu === 'Odendi')
      .reduce((s, t) => s + t.miktar, 0)
      + direktTahsilatlarRapor.reduce((s, o) => s + o.miktar, 0);
    const bekleyenToplam = aktifOdemeler
      .filter(item => item.odemeDurumu === 'Bekliyor')
      .reduce((s, t) => s + t.miktar, 0);
    const toplamBorç = sozlesme.sozlesmeFiyati;
    const kalanBakiye = toplamBorç - toplamTahsilat;

    // Running balance per row
    let runningBalance = toplamBorç;
    const rowsWithBalance = aktifOdemeler.map(item => {
      if (item.odemeDurumu === 'Odendi') runningBalance -= item.miktar;
      return { ...item, runningBalance };
    });
    const direktRowsWithBalance = direktTahsilatlarRapor.map(o => {
      runningBalance -= o.miktar;
      return { ...o, runningBalance };
    });

    const statusBadge = (durum: string, vade: string | null) => {
      const isGecikti = durum !== 'Odendi' && vade && new Date(vade) < new Date();
      if (durum === 'Odendi') return <span style={{display:'inline-flex',alignItems:'center',gap:4,color:'#16a34a',fontSize:11,fontWeight:600}}>✓ Ödendi</span>;
      if (isGecikti) return <span style={{display:'inline-flex',alignItems:'center',gap:4,color:'#dc2626',fontSize:11,fontWeight:600}}>⚠ Gecikti</span>;
      return <span style={{display:'inline-flex',alignItems:'center',gap:4,color:'#6b7280',fontSize:11,fontWeight:600}}>○ Bekliyor</span>;
    };

    return (
      <div id="print-rapor-root">
        <style>{`
          @media print {
            @page { size: A4; margin: 0.6cm; }
            body * { visibility: hidden !important; }
            #print-rapor-root, #print-rapor-root * { visibility: visible !important; }
            #print-rapor-root { position: absolute; left: 0; top: 0; width: 100%; }
            .rapor-toolbar { display: none !important; }
            .rapor-bg { background: white !important; padding: 0 !important; }
            .rapor-a4 { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; max-width: 100% !important; min-height: unset !important; padding: 0 !important; }
          }
        `}</style>

        {/* Sticky toolbar */}
        <div className="rapor-toolbar" style={{position:'sticky',top:0,zIndex:50,background:'rgba(255,255,255,0.9)',backdropFilter:'blur(8px)',borderBottom:'1px solid #e5e7eb',padding:'12px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <button
              onClick={() => setShowOdemePlaniRapor(false)}
              style={{display:'flex',alignItems:'center',gap:6,background:'none',border:'1px solid #e5e7eb',color:'#374151',borderRadius:8,padding:'7px 14px',fontSize:13,fontWeight:500,cursor:'pointer',transition:'all 0.15s'}}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background='#f9fafb'}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background='none'}
            >
              ← {t('btn.geri', 'Geri Dön')}
            </button>
            <span style={{fontSize:12,color:'#9ca3af'}}>{t('sozlesmeDetay.odemePlaniOnizleme', 'Ödeme Planı Önizleme')}</span>
          </div>
          <button
            onClick={() => window.print()}
            style={{display:'flex',alignItems:'center',gap:7,background:'#cd6906',border:'none',color:'#fff',borderRadius:8,padding:'8px 20px',fontSize:13,fontWeight:500,cursor:'pointer',boxShadow:'0 1px 3px rgba(205,105,6,0.3)',transition:'all 0.15s'}}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#a85505'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='#cd6906'; }}
          >
            <i className="fas fa-print"></i> {t('btn.yazdir', 'Yazdır')}
          </button>
        </div>

        {/* Grey background wrapper */}
        <div className="rapor-bg" style={{background:'#f3f4f6',minHeight:'100vh',padding:'24px 16px'}}>
          {/* A4 paper */}
          <div className="rapor-a4" style={{background:'#fff',boxShadow:'0 20px 60px rgba(0,0,0,0.12)',margin:'0 auto',padding:'28px 32px',width:'100%',maxWidth:'210mm',borderRadius:4,boxSizing:'border-box'}}>

            {/* Report Header — compact 1 row */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,paddingBottom:12,borderBottom:'2px solid #111827'}}>
              <div>
                <div style={{fontWeight:800,fontSize:14,color:'#111827',lineHeight:1.2}}>{sozlesme.daire?.blok?.proje?.projeAdi || 'FİRMA'}</div>
                <div style={{fontSize:9,color:'#6b7280',marginTop:1}}>Yapı Yönetimi Sistemi</div>
              </div>
              <div style={{textAlign:'center'}}>
                <div style={{fontSize:18,fontWeight:900,color:'#111827',letterSpacing:'-0.5px',lineHeight:1}}>ÖDEME PLANI</div>
              </div>
              <div style={{textAlign:'right',display:'flex',flexDirection:'column',gap:2}}>
                <div style={{fontSize:9,color:'#6b7280'}}>Sözleşme No: <strong style={{color:'#111827'}}>{sozlesme.sozlesmeTakipNo}</strong></div>
                <div style={{fontSize:9,color:'#6b7280'}}>Tarih: <strong style={{color:'#111827'}}>{new Date().toLocaleDateString('tr-TR')}</strong></div>
              </div>
            </div>

            {/* Parties summary — compact single row */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:14,padding:'10px 14px',border:'1px solid #e5e7eb',borderRadius:8}}>
              <div>
                <div style={{fontSize:9,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:5}}>{t('musteri.musteriTab')}</div>
                <div style={{display:'flex',flexDirection:'column',gap:3}}>
                  {[[t('label.musteri', 'Müşteri'), sozlesme.cari?.adiSoyadi],[t('label.telefon', 'Telefon'), sozlesme.cari?.telefon],[t('label.eposta', 'E-posta'), sozlesme.cari?.email || 'N/A']].map(([lbl,val]) => (
                    <div key={lbl as string} style={{display:'flex',gap:6}}>
                      <span style={{fontSize:9,fontWeight:700,color:'#6b7280',minWidth:55,textTransform:'uppercase'}}>{lbl}:</span>
                      <span style={{fontSize:11,fontWeight:500,color:'#111827'}}>{val || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{fontSize:9,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:5}}>{t('sozlesmeDetay.sozlesmeDetaylari', 'Sözleşme Detayları')}</div>
                <div style={{display:'flex',flexDirection:'column',gap:3}}>
                  {[
                    [t('label.proje', 'Proje'), sozlesme.daire?.blok?.proje?.projeAdi],
                    [t('sozlesmeDetay.blokDaire', 'Blok/Daire'), `${sozlesme.daire?.blok?.blokAdi || ''} — ${sozlesme.daire?.daireNo || ''}`],
                    [t('label.satisFiyati', 'Satış Fiyatı'), sozlesme.sozlesmeFiyati.toLocaleString('tr-TR') + ' ₺'],
                    [t('sozlesmeDetay.sozlesmeTar', 'Sözleşme Tar.'), sozlesme.sozlesmeTarihi ? new Date(sozlesme.sozlesmeTarihi).toLocaleDateString('tr-TR') : '—'],
                  ].map(([lbl,val]) => (
                    <div key={lbl as string} style={{display:'flex',gap:6}}>
                      <span style={{fontSize:9,fontWeight:700,color:'#6b7280',minWidth:72,textTransform:'uppercase'}}>{lbl}:</span>
                      <span style={{fontSize:11,fontWeight:500,color:'#111827'}}>{val || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment Table */}
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead>
                <tr style={{background:'#f3f4f6',borderTop:'1px solid #e5e7eb',borderBottom:'1px solid #e5e7eb'}}>
                  {[
                    {label:t('label.no', 'No'), align:'left'},
                    {label:t('sozlesmeDetay.vadeTarihi', 'Vade Tarihi'), align:'left'},
                    {label:t('sozlesmeDetay.taksitTutari', 'Taksit Tutarı'), align:'right'},
                    {label:t('sozlesmeDetay.tahsilatTar', 'Tahsilat Tar.'), align:'left'},
                    {label:t('sozlesmeDetay.kalanBakiye', 'Kalan Bakiye'), align:'right'},
                    {label:t('label.tur', 'Tür'), align:'left'},
                    {label:t('label.durum', 'Durum'), align:'left'},
                    {label:t('label.aciklama', 'Açıklama'), align:'left'},
                  ].map(h => (
                    <th key={h.label} style={{padding:'6px 8px',fontSize:9,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.05em',textAlign: h.align as any}}>{h.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowsWithBalance.map((taksit, idx) => (
                  <tr key={taksit.taksitID} style={{borderBottom:'1px solid #f3f4f6',background: idx%2===0 ? '#fff' : '#fafafa'}}>
                    <td style={{padding:'5px 8px',color:'#9ca3af',fontSize:10}}>{idx+1}</td>
                    <td style={{padding:'5px 8px',color:'#111827',fontWeight:500,whiteSpace:'nowrap',fontSize:11}}>{taksit.vadeTarihi ? new Date(taksit.vadeTarihi).toLocaleDateString('tr-TR') : '—'}</td>
                    <td style={{padding:'5px 8px',textAlign:'right',fontWeight:600,color:'#111827',whiteSpace:'nowrap',fontSize:11}}>{taksit.miktar.toLocaleString('tr-TR')} ₺</td>
                    <td style={{padding:'5px 8px',color:'#374151',whiteSpace:'nowrap',fontSize:11}}>{taksit.tahsilatTarihi ? new Date(taksit.tahsilatTarihi).toLocaleDateString('tr-TR') : '—'}</td>
                    <td style={{padding:'5px 8px',textAlign:'right',fontWeight:600,color: (taksit as any).runningBalance > 0 ? '#cd6906' : '#16a34a',whiteSpace:'nowrap',fontSize:11}}>{(taksit as any).runningBalance.toLocaleString('tr-TR')} ₺</td>
                    <td style={{padding:'5px 8px',color:'#374151',fontSize:11}}>{taksit.odemeTuruDetay || taksit.odemeTuru || '—'}</td>
                    <td style={{padding:'5px 8px'}}>{statusBadge(taksit.odemeDurumu, taksit.vadeTarihi ?? null)}</td>
                    <td style={{padding:'5px 8px',color:'#6b7280',fontSize:10}}>{taksit.aciklama || '—'}</td>
                  </tr>
                ))}
                {direktRowsWithBalance.map((o, idx) => {
                  const tarih = o.tahsilatTarihi || o.islemTarihi;
                  return (
                    <tr key={`dt-${o.odemeID}`} style={{borderBottom:'1px solid #f3f4f6',background:(rowsWithBalance.length+idx)%2===0 ? '#fff' : '#fafafa'}}>
                      <td style={{padding:'5px 8px',color:'#9ca3af',fontSize:10}}>{rowsWithBalance.length+idx+1}</td>
                      <td style={{padding:'5px 8px',color:'#111827',fontWeight:500,whiteSpace:'nowrap',fontSize:11}}>{tarih ? new Date(tarih).toLocaleDateString('tr-TR') : '—'}</td>
                      <td style={{padding:'5px 8px',textAlign:'right',fontWeight:600,color:'#111827',whiteSpace:'nowrap',fontSize:11}}>{o.miktar.toLocaleString('tr-TR')} ₺</td>
                      <td style={{padding:'5px 8px',color:'#374151',whiteSpace:'nowrap',fontSize:11}}>{tarih ? new Date(tarih).toLocaleDateString('tr-TR') : '—'}</td>
                      <td style={{padding:'5px 8px',textAlign:'right',fontWeight:600,color: o.runningBalance > 0 ? '#cd6906' : '#16a34a',whiteSpace:'nowrap',fontSize:11}}>{o.runningBalance.toLocaleString('tr-TR')} ₺</td>
                      <td style={{padding:'5px 8px',color:'#374151',fontSize:11}}>{o.odemeTuru || '—'}</td>
                      <td style={{padding:'5px 8px'}}><span style={{display:'inline-flex',alignItems:'center',gap:4,color:'#16a34a',fontSize:11,fontWeight:600}}>✓ Ödendi</span></td>
                      <td style={{padding:'5px 8px',color:'#6b7280',fontSize:10}}>{o.aciklama || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Summary + Signature inline */}
            <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginTop:14,paddingTop:12,borderTop:'1px solid #e5e7eb',gap:24}}>
              {/* Signatures */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,flex:1}}>
                {[t('sozlesmeDetay.musteriImzasi', 'Müşteri İmzası'), t('sozlesmeDetay.yetkiliImzasi', 'Yetkili İmzası')].map(label => (
                  <div key={label} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                    <div style={{width:'100%',height:36,border:'1px dashed #d1d5db',borderRadius:4,background:'#fafafa'}}></div>
                    <div style={{fontSize:9,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</div>
                  </div>
                ))}
              </div>
              {/* Totals */}
              <div style={{border:'1px solid #e5e7eb',borderRadius:8,overflow:'hidden',minWidth:220}}>
                {[
                  [t('sozlesmeDetay.toplamBorc', 'Toplam Borç'), toplamBorç.toLocaleString('tr-TR') + ' ₺', '#111827', false],
                  [t('sozlesmeDetay.odendiToplam', 'Ödendi Toplam'), toplamTahsilat.toLocaleString('tr-TR') + ' ₺', '#16a34a', false],
                  [t('sozlesmeDetay.bekleyenToplam', 'Bekliyor Toplam'), bekleyenToplam.toLocaleString('tr-TR') + ' ₺', '#b45309', false],
                  [t('sozlesmeDetay.kalanBakiye', 'Kalan Bakiye'), kalanBakiye.toLocaleString('tr-TR') + ' ₺', '#cd6906', true],
                ].map(([lbl, val, clr, bold]) => (
                  <div key={lbl as string} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 12px',borderBottom: lbl!=='Kalan Bakiye' ? '1px solid #f3f4f6' : 'none',background: bold ? '#fff7ed' : lbl==='Ödendi Toplam' ? '#f0fdf4' : lbl==='Bekliyor Toplam' ? '#fefce8' : '#fff'}}>
                    <span style={{fontSize:9,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.04em'}}>{lbl as string}</span>
                    <span style={{fontSize:12,fontWeight: bold ? 700 : 600, color: clr as string}}>{val as string}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }
  // --- End Rapor Önizleme ---

  // --- Hazır Sözleşme Önizleme ---
  if (showHazirSozlesme && sozlesme) {
    const aktifOdemeler = taksitler
      .filter(item => item.odemeDurumu !== 'Iptal')
      .sort((a, b) => (a.vadeTarihi ? new Date(a.vadeTarihi).getTime() : 0) - (b.vadeTarihi ? new Date(b.vadeTarihi).getTime() : 0));
    const direktTahsilatlarHS = getDirectTahsilatlar();
    const odenenToplam = aktifOdemeler.filter(item => item.odemeDurumu === 'Odendi').reduce((s, t) => s + t.miktar, 0)
      + direktTahsilatlarHS.reduce((s, o) => s + o.miktar, 0);
    const bekleyenToplam = aktifOdemeler.filter(item => item.odemeDurumu === 'Bekliyor').reduce((s, t) => s + t.miktar, 0);
    const k = hazirSozlesmeKurumsal;
    const fv = (val: string | number | undefined | null, bold?: boolean) =>
      <span style={{fontWeight: bold ? 700 : 600, color:'#111827', borderBottom:'1px solid #e5e7eb'}}>{val || '\u2014'}</span>;

    return (
      <div id="hazir-sozlesme-root">
        <style>{`
          @media print {
            @page { size: A4; margin: 0; }
            body * { visibility: hidden !important; }
            #hazir-sozlesme-root, #hazir-sozlesme-root * { visibility: visible !important; }
            #hazir-sozlesme-root { position: absolute; left: 0; top: 0; width: 100%; }
            .hs-toolbar { display: none !important; }
            .hs-bg { background: white !important; padding: 0 !important; }
            .hs-paper { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; max-width: 100% !important; border: none !important; }
          }
        `}</style>

        {/* Toolbar */}
        <div className="hs-toolbar" style={{position:'sticky',top:0,zIndex:50,background:'rgba(255,255,255,0.92)',backdropFilter:'blur(8px)',borderBottom:'1px solid #e5e7eb',padding:'12px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <button
            onClick={() => setShowHazirSozlesme(false)}
            style={{display:'flex',alignItems:'center',gap:6,background:'none',border:'none',color:'#6b7280',fontSize:13,fontWeight:500,cursor:'pointer',padding:'6px 0'}}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color='#111827'}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color='#6b7280'}
          >
            ← {t('btn.geri', 'Geri Dön')}
          </button>
          <div style={{display:'flex',gap:10}}>
            <button
              onClick={downloadHazirSozlesmePDF}
              style={{display:'flex',alignItems:'center',gap:7,background:'#fff',border:'1px solid #d1d5db',color:'#374151',borderRadius:8,padding:'8px 18px',fontSize:13,fontWeight:500,cursor:'pointer',transition:'all 0.15s'}}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background='#f9fafb'}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background='#fff'}
            >
              <i className="fas fa-file-pdf" style={{color:'#dc2626'}}></i> {t('btn.indir', 'PDF İndir')}
            </button>
            <button
              onClick={() => window.print()}
              style={{display:'flex',alignItems:'center',gap:7,background:'#cd6906',border:'none',color:'#fff',borderRadius:8,padding:'8px 18px',fontSize:13,fontWeight:500,cursor:'pointer',boxShadow:'0 1px 3px rgba(205,105,6,0.3)',transition:'all 0.15s'}}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#a85505'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='#cd6906'; }}
            >
              <i className="fas fa-print"></i> {t('btn.yazdir', 'Yazdır')}
            </button>
          </div>
        </div>

        {/* Background */}
        <div className="hs-bg" style={{background:'rgba(243,244,246,0.8)',minHeight:'100vh',padding:'32px 16px'}}>
          {/* A4 Paper */}
          <div ref={hazirSozlesmeRef} className="hs-paper" style={{background:'#fff',boxShadow:'0 20px 60px rgba(0,0,0,0.14)',margin:'0 auto',padding:'15mm 10mm',width:'100%',maxWidth:'210mm',minHeight:'297mm',borderRadius:2,boxSizing:'border-box',border:'1px solid #e5e7eb',fontFamily:"'Inter','Segoe UI',sans-serif"}}>

            {/* Header */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',paddingBottom:16,borderBottom:'2px solid #111827',marginBottom:20}}>
              <div>
                <div style={{fontSize:22,fontWeight:900,color:'#111827',letterSpacing:'-0.5px',textTransform:'uppercase',lineHeight:1}}>SATIŞ SÖZLEŞMESİ</div>
                {k?.firmaAdi && <div style={{fontSize:11,color:'#6b7280',marginTop:4}}>{k.firmaAdi}</div>}
              </div>
              <div style={{background:'#111827',color:'#fff',borderRadius:8,padding:'10px 16px',textAlign:'right',minWidth:160}}>
                <div style={{fontSize:9,opacity:0.7,marginBottom:2}}>Takip No</div>
                <div style={{fontSize:12,fontWeight:800,letterSpacing:0.5}}>{sozlesme.sozlesmeTakipNo}</div>
                <div style={{fontSize:9,opacity:0.7,marginTop:6,marginBottom:2}}>Tarih</div>
                <div style={{fontSize:11,fontWeight:700}}>{new Date(sozlesme.sozlesmeTarihi).toLocaleDateString('tr-TR')}</div>
              </div>
            </div>

            {/* Price strip */}
            <div style={{background:'#111827',color:'#fff',borderRadius:8,padding:'12px 18px',display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
              <div>
                <div style={{fontSize:9,opacity:0.7,marginBottom:2}}>Sözleşme Fiyatı</div>
                <div style={{fontSize:18,fontWeight:900,color:'#cd6906'}}>{sozlesme.sozlesmeFiyati.toLocaleString('tr-TR', {minimumFractionDigits:2})} ₺</div>
              </div>
              <div style={{width:1,background:'rgba(255,255,255,0.15)',height:36}}></div>
              <div>
                <div style={{fontSize:9,opacity:0.7,marginBottom:2}}>Sözleşme Tarihi</div>
                <div style={{fontSize:14,fontWeight:700}}>{new Date(sozlesme.sozlesmeTarihi).toLocaleDateString('tr-TR')}</div>
              </div>
            </div>

            {/* Parties */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
              {[
                {title:'SATICI BİLGİLERİ', rows: [['Firma Adı', k?.firmaAdi],['Adres', k?.adres ? `${k.adres}${k.sehir ? ' / '+k.sehir : ''}` : null],['Vergi Dairesi', k?.vd],['Vergi No', k?.vn]]},
                {title:'ALICI BİLGİLERİ', rows: [['Ad Soyad', sozlesme.cari?.adiSoyadi],['T.C. Kimlik', sozlesme.cari?.tc],['Telefon', sozlesme.cari?.telefon],['E-Posta', sozlesme.cari?.email],['Adres', sozlesme.cari?.adres]]}
              ].map(card => (
                <div key={card.title} style={{background:'#f8fafc',borderLeft:'3px solid #111827',borderRadius:'0 8px 8px 0',padding:'10px 14px'}}>
                  <div style={{fontSize:8,fontWeight:800,textTransform:'uppercase',letterSpacing:1,color:'#111827',marginBottom:8}}>{card.title}</div>
                  {card.rows.filter(r => r[1]).map(([lbl, val]) => (
                    <div key={lbl as string} style={{display:'flex',gap:8,marginBottom:3,fontSize:9.5}}>
                      <span style={{fontWeight:700,color:'#4b5563',minWidth:80,flexShrink:0}}>{lbl}</span>
                      <span style={{color:'#111827'}}>{val}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Gayrimenkul */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:1,color:'#cd6906',borderBottom:'1.5px solid #cd6906',paddingBottom:4,marginBottom:10}}>GAYRİMENKUL BİLGİLERİ</div>
              <div style={{background:'#f8fafc',borderLeft:'3px solid #cd6906',borderRadius:'0 8px 8px 0',padding:'10px 14px',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'3px 0'}}>
                {[
                  ['Proje', sozlesme.daire?.blok?.proje?.projeAdi],
                  ['Blok', sozlesme.daire?.blok?.blokAdi],
                  ['Daire No', sozlesme.daire?.daireNo],
                  ['Kat', sozlesme.daire?.kat?.katAdi],
                  ['Cephe', sozlesme.daire?.cephe?.cepheAdi],
                  ['Manzara', sozlesme.daire?.manzara?.manzaraAdi],
                  ['Net Alan', sozlesme.daire?.netM2 != null ? `${sozlesme.daire.netM2} m²` : null],
                  ['Brüt Alan', sozlesme.daire?.brutM2 != null ? `${sozlesme.daire.brutM2} m²` : null],
                  ['Daire Tipi', sozlesme.daire?.daireTip?.tip],
                ].map(([lbl, val]) => (
                  <div key={lbl as string} style={{display:'flex',gap:6,fontSize:9.5}}>
                    <span style={{fontWeight:700,color:'#6b7280',minWidth:72}}>{lbl}</span>
                    <span style={{color:'#111827'}}>{val || '—'}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sözleşme Maddeleri */}
            {sozlesme.ozelSartlar && (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:1,color:'#cd6906',borderBottom:'1.5px solid #cd6906',paddingBottom:4,marginBottom:10}}>SÖZLEŞME MADDELERİ</div>
                <div
                  style={{background:'#fafafa',border:'1.5px solid #e5e7eb',borderRadius:8,padding:'12px 16px',fontSize:9.5,lineHeight:1.8,color:'#374151'}}
                  dangerouslySetInnerHTML={{__html: sozlesme.ozelSartlar.replace(/&nbsp;/g,' ')}}
                />
              </div>
            )}

            {/* Ödeme Planı */}
            {aktifOdemeler.length > 0 && (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:1,color:'#cd6906',borderBottom:'1.5px solid #cd6906',paddingBottom:4,marginBottom:10}}>ÖDEME PLANI</div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:9.5}}>
                  <thead>
                    <tr style={{background:'#111827',color:'#fff'}}>
                      {['#','Vade Tarihi','Miktar','Ödeme Türü','Durum','Açıklama'].map(h => (
                        <th key={h} style={{padding:'6px 8px',textAlign: h==='Miktar' ? 'right' : 'left',fontSize:8,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {aktifOdemeler.map((item, i) => (
                      <tr key={item.taksitID} style={{borderBottom:'1px solid #f1f5f9',background: i%2===0 ? '#fff' : '#f8fafc'}}>
                        <td style={{padding:'5px 8px',color:'#9ca3af',fontSize:9}}>{i+1}</td>
                        <td style={{padding:'5px 8px'}}>{item.vadeTarihi ? new Date(item.vadeTarihi).toLocaleDateString('tr-TR') : '—'}</td>
                        <td style={{padding:'5px 8px',textAlign:'right',fontWeight:700}}>{(item.miktar ?? 0).toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</td>
                        <td style={{padding:'5px 8px'}}>{item.odemeTuru || item.odemeTuruDetay || '—'}</td>
                        <td style={{padding:'5px 8px'}}>
                          <span style={{background: item.odemeDurumu==='Odendi' ? '#dcfce7' : '#fef9c3', color: item.odemeDurumu==='Odendi' ? '#15803d' : '#a16207', borderRadius:20, padding:'2px 8px', fontSize:8, fontWeight:700}}>
                            {item.odemeDurumu==='Odendi' ? 'Ödendi' : 'Bekliyor'}
                          </span>
                        </td>
                        <td style={{padding:'5px 8px',color:'#6b7280',fontSize:9}}>{item.aciklama || '—'}</td>
                      </tr>
                    ))}
                    {direktTahsilatlarHS.map((o, i) => (
                      <tr key={`dt-${o.odemeID}`} style={{borderBottom:'1px solid #f1f5f9',background: (aktifOdemeler.length+i)%2===0 ? '#fff' : '#f8fafc'}}>
                        <td style={{padding:'5px 8px',color:'#9ca3af',fontSize:9}}>{aktifOdemeler.length+i+1}</td>
                        <td style={{padding:'5px 8px'}}>{(o.tahsilatTarihi || o.islemTarihi) ? new Date(o.tahsilatTarihi || o.islemTarihi).toLocaleDateString('tr-TR') : '—'}</td>
                        <td style={{padding:'5px 8px',textAlign:'right',fontWeight:700}}>{o.miktar.toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</td>
                        <td style={{padding:'5px 8px'}}>{o.odemeTuru || '—'}</td>
                        <td style={{padding:'5px 8px'}}>
                          <span style={{background:'#dcfce7',color:'#15803d',borderRadius:20,padding:'2px 8px',fontSize:8,fontWeight:700}}>Ödendi</span>
                        </td>
                        <td style={{padding:'5px 8px',color:'#6b7280',fontSize:9}}>{o.aciklama || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{background:'#f0fdf4'}}>
                      <td colSpan={2} style={{padding:'6px 8px',fontSize:8,fontWeight:800,textTransform:'uppercase',letterSpacing:0.5,color:'#15803d'}}>ÖDENDİ TOPLAM</td>
                      <td style={{padding:'6px 8px',textAlign:'right',fontWeight:800,color:'#15803d'}}>{odenenToplam.toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</td>
                      <td colSpan={3}></td>
                    </tr>
                    <tr style={{background:'#fefce8'}}>
                      <td colSpan={2} style={{padding:'6px 8px',fontSize:8,fontWeight:800,textTransform:'uppercase',letterSpacing:0.5,color:'#a16207'}}>BEKLİYOR TOPLAM</td>
                      <td style={{padding:'6px 8px',textAlign:'right',fontWeight:800,color:'#a16207'}}>{bekleyenToplam.toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Signatures */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:48,marginTop:32,paddingTop:24,borderTop:'1px solid #e5e7eb'}}>
              {[{title:'SATICI', name: k?.firmaAdi || ''}, {title:'ALICI', name: sozlesme.cari?.adiSoyadi || ''}].map(s => (
                <div key={s.title}>
                  <div style={{fontSize:8,fontWeight:800,textTransform:'uppercase',letterSpacing:1,color:'#111827',marginBottom:4}}>{s.title}</div>
                  <div style={{fontSize:10,fontWeight:600,color:'#374151',marginBottom:12}}>{s.name}</div>
                  <div style={{height:48,borderBottom:'1.5px dashed #cbd5e1',marginBottom:8}}></div>
                  <div style={{fontSize:8.5,color:'#9ca3af'}}>İmza &nbsp;&nbsp;&nbsp; Tarih: _____ / _____ / _________</div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{marginTop:20,borderTop:'1px solid #e5e7eb',paddingTop:8,textAlign:'center',fontSize:8,color:'#9ca3af'}}>
              {k?.firmaAdi ? k.firmaAdi + ' | ' : ''}{k?.adres || ''}{k?.sehir ? ' / '+k.sehir : ''} &nbsp;|&nbsp; Bu belge {new Date().toLocaleDateString('tr-TR')} tarihinde düzenlenmiştir.
            </div>

          </div>
        </div>
      </div>
    );
  }
  // --- End Hazır Sözleşme Önizleme ---

  // --- Teklif Önizleme ---
  if (showTeklif && sozlesme) {
    const aktifOdemeler = odemeler
      .filter(o => o.aktifMi)
      .sort((a, b) => (a.vadeTarihi ? new Date(a.vadeTarihi).getTime() : 0) - (b.vadeTarihi ? new Date(b.vadeTarihi).getTime() : 0));
    const k = teklifKurumsal;
    const teklifNo = sozlesme.sozlesmeTakipNo || '—';
    const teklifTarihi = new Date().toLocaleDateString('tr-TR');
    const gecerlilikTarihi = sozlesme.rezerveBitisTarihi
      ? new Date(sozlesme.rezerveBitisTarihi).toLocaleDateString('tr-TR')
      : null;

    return (
      <div id="teklif-root">
        <style>{`
          @media print {
            @page { size: A4; margin: 0; }
            .teklif-toolbar { display: none !important; }
            .teklif-bg { background: white !important; padding: 0 !important; }
            .teklif-paper {
              box-shadow: none !important;
              margin: 0 !important;
              border-radius: 0 !important;
              max-width: 100% !important;
              min-height: unset !important;
              border: none !important;
              padding: 12mm 10mm !important;
              width: 100% !important;
            }
            tr { break-inside: avoid; page-break-inside: avoid; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
            table { page-break-inside: auto; }
          }
        `}</style>

        {/* Toolbar */}
        <div className="teklif-toolbar" style={{position:'sticky',top:0,zIndex:50,background:'rgba(255,255,255,0.92)',backdropFilter:'blur(8px)',borderBottom:'1px solid #e5e7eb',padding:'12px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <button
            onClick={() => setShowTeklif(false)}
            style={{display:'flex',alignItems:'center',gap:6,background:'none',border:'none',color:'#6b7280',fontSize:13,fontWeight:500,cursor:'pointer',padding:'6px 0'}}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color='#111827'}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color='#6b7280'}
          >
            ← {t('btn.geri', 'Geri Dön')}
          </button>
          <div style={{display:'flex',gap:10}}>
            <button
              onClick={downloadTeklifPDF}
              style={{display:'flex',alignItems:'center',gap:7,background:'#fff',border:'1px solid #d1d5db',color:'#374151',borderRadius:8,padding:'8px 18px',fontSize:13,fontWeight:500,cursor:'pointer'}}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background='#f9fafb'}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background='#fff'}
            >
              <i className="fas fa-file-pdf" style={{color:'#dc2626'}}></i> {t('btn.indir', 'PDF İndir')}
            </button>
            <button
              onClick={() => window.print()}
              style={{display:'flex',alignItems:'center',gap:7,background:'#cd6906',border:'none',color:'#fff',borderRadius:8,padding:'8px 18px',fontSize:13,fontWeight:500,cursor:'pointer',boxShadow:'0 1px 3px rgba(205,105,6,0.3)'}}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#a85505'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='#cd6906'; }}
            >
              <i className="fas fa-print"></i> {t('btn.yazdir', 'Yazdır')}
            </button>
          </div>
        </div>

        {/* BG */}
        <div className="teklif-bg" style={{background:'rgba(243,244,246,0.8)',minHeight:'100vh',padding:'32px 16px'}}>
          <div ref={teklifRef} className="teklif-paper" style={{background:'#fff',boxShadow:'0 20px 60px rgba(0,0,0,0.14)',margin:'0 auto',padding:'15mm 10mm',width:'100%',maxWidth:'210mm',minHeight:'297mm',borderRadius:2,boxSizing:'border-box',border:'1px solid #e5e7eb',fontFamily:"'Inter','Segoe UI',sans-serif"}}>

            {/* Header */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20,paddingBottom:18,borderBottom:'2px solid #111827'}}>
              {/* Left: logo + firma */}
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {teklifLogoBase64
                  ? <img src={teklifLogoBase64} alt="logo" style={{maxHeight:52,maxWidth:160,objectFit:'contain',marginBottom:4}} />
                  : k?.firmaAdi && <div style={{fontSize:18,fontWeight:900,color:'#111827',letterSpacing:'-0.5px'}}>{k.firmaAdi}</div>
                }
                {k && (
                  <div style={{fontSize:9,color:'#6b7280',lineHeight:1.6}}>
                    {k.adres && <div>{k.adres}{k.sehir ? ' / '+k.sehir : ''}</div>}
                    {k.vd && <div>Vergi D.: {k.vd}{k.vn ? '  |  VN: '+k.vn : ''}</div>}
                    {k.tel && <div>Tel: {k.tel}</div>}
                  </div>
                )}
              </div>
              {/* Right: TEKLİF + meta */}
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:28,fontWeight:900,color:'#cd6906',letterSpacing:'-1px',lineHeight:1}}>TEKLİF</div>
                <div style={{marginTop:10,display:'grid',gridTemplateColumns:'auto auto',gap:'3px 14px',fontSize:9}}>
                  <span style={{color:'#9ca3af',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>Teklif No</span>
                  <span style={{fontWeight:700,color:'#111827',textAlign:'left'}}>{teklifNo}</span>
                  <span style={{color:'#9ca3af',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>Tarih</span>
                  <span style={{fontWeight:700,color:'#111827',textAlign:'left'}}>{teklifTarihi}</span>
                  {gecerlilikTarihi && (<>
                    <span style={{color:'#9ca3af',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>Geçerlilik</span>
                    <span style={{fontWeight:700,color:'#d97706',textAlign:'left'}}>{gecerlilikTarihi}</span>
                  </>)}
                </div>
              </div>
            </div>

            {/* Müşteri & Proje */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,borderBottom:'1px solid #f1f5f9',paddingBottom:16,marginBottom:16}}>
              <div>
                <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.1em',color:'#9ca3af',marginBottom:10}}>Teklif Sunulan</div>
                <div style={{fontSize:13,fontWeight:700,color:'#111827',marginBottom:3}}>{sozlesme.cari?.adiSoyadi || '—'}</div>
                {sozlesme.cari?.firmaAdi && <div style={{fontSize:10,color:'#6b7280',marginBottom:2}}>{sozlesme.cari.firmaAdi}</div>}
                {sozlesme.cari?.telefon && <div style={{fontSize:10,color:'#6b7280',marginBottom:2}}>{sozlesme.cari.telefon}</div>}
                {sozlesme.cari?.email && <div style={{fontSize:10,color:'#6b7280',marginBottom:2}}>{sozlesme.cari.email}</div>}
                {sozlesme.cari?.adres && <div style={{fontSize:10,color:'#6b7280'}}>{sozlesme.cari.adres}</div>}
              </div>
              <div>
                <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.1em',color:'#9ca3af',marginBottom:10}}>Konu / Proje</div>
                <div style={{fontSize:13,fontWeight:700,color:'#111827',marginBottom:3}}>{sozlesme.daire?.blok?.proje?.projeAdi || '—'}</div>
                {[  
                  ['Blok / Daire', `${sozlesme.daire?.blok?.blokAdi || '—'} – Daire ${sozlesme.daire?.daireNo || '—'}`],
                  ['Kat', sozlesme.daire?.kat?.katAdi],
                  ['Tip', sozlesme.daire?.daireTip?.tip],
                  ['Cephe / Manzara', [sozlesme.daire?.cephe?.cepheAdi, sozlesme.daire?.manzara?.manzaraAdi].filter(Boolean).join(' / ')],
                  ['Alan', sozlesme.daire?.netM2 != null ? `Net ${sozlesme.daire.netM2} m²  /  Brüt ${sozlesme.daire.brutM2 || '—'} m²` : null],
                ].filter(r => r[1]).map(([lbl, val]) => (
                  <div key={lbl as string} style={{display:'flex',gap:8,marginBottom:3,fontSize:10}}>
                    <span style={{fontWeight:700,color:'#9ca3af',minWidth:90,flexShrink:0,fontSize:9,textTransform:'uppercase',letterSpacing:'0.04em'}}>{lbl}</span>
                    <span style={{fontWeight:600,color:'#374151'}}>{val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Kalemler tablosu */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.08em',color:'#cd6906',borderBottom:'1.5px solid #cd6906',paddingBottom:4,marginBottom:0}}>Teklif Kalemleri</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:9.5}}>
                <thead>
                  <tr style={{background:'#f9fafb',borderBottom:'2px solid #e5e7eb'}}>
                    {['Açıklama','Birim Fiyat','Adet','KDV (%)','Toplam'].map((h,i) => (
                      <th key={h} style={{padding:'10px 12px',textAlign: i>0 ? 'right' : 'left',fontSize:10,fontWeight:700,color:'#6b7280',letterSpacing:'0.04em'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{borderBottom:'1px solid #f1f5f9'}}>
                    <td style={{padding:'12px 12px'}}>
                      <div style={{fontWeight:600,color:'#111827',fontSize:10.5}}>{sozlesme.daire?.blok?.proje?.projeAdi || 'Gayrimenkul'} – {sozlesme.daire?.blok?.blokAdi || ''} Daire {sozlesme.daire?.daireNo || ''}</div>
                      <div style={{fontSize:9,color:'#9ca3af',marginTop:2}}>{[sozlesme.daire?.daireTip?.tip, sozlesme.daire?.kat?.katAdi, sozlesme.daire?.cephe?.cepheAdi].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td style={{padding:'12px 12px',textAlign:'right',fontWeight:600,color:'#374151'}}>{sozlesme.sozlesmeFiyati.toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</td>
                    <td style={{padding:'12px 12px',textAlign:'right',color:'#374151'}}>1</td>
                    <td style={{padding:'12px 12px',textAlign:'right',color:'#9ca3af'}}>—</td>
                    <td style={{padding:'12px 12px',textAlign:'right',fontWeight:700,color:'#111827'}}>{sozlesme.sozlesmeFiyati.toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Finansal özet + notlar */}
            <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:24,marginBottom:20,alignItems:'start'}}>
              {/* Notlar */}
              <div style={{paddingRight:16}}>
                {sozlesme.ozelSartlar && (
                  <>
                    <div style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',color:'#9ca3af',marginBottom:6}}>Teklif Notları & Şartlar</div>
                    <div
                      style={{fontSize:9,color:'#6b7280',fontStyle:'italic',lineHeight:1.7,borderLeft:'2px solid #e5e7eb',paddingLeft:10}}
                      dangerouslySetInnerHTML={{__html: sozlesme.ozelSartlar.replace(/&nbsp;/g,' ')}}
                    />
                  </>
                )}
                {gecerlilikTarihi && (
                  <div style={{marginTop:10,fontSize:9,color:'#d97706',fontWeight:600,background:'#fffbeb',border:'1px solid #fde68a',borderRadius:6,padding:'6px 10px',display:'inline-block'}}>
                    ⏰ Teklif geçerlilik tarihi: {gecerlilikTarihi}
                  </div>
                )}
              </div>
              {/* Finansal özet */}
              <div style={{minWidth:200}}>
                <div style={{borderTop:'1px solid #f1f5f9',paddingTop:8}}>
                  {[['Ara Toplam',sozlesme.sozlesmeFiyati],['KDV','—'],].map(([lbl, val]) => (
                    <div key={lbl as string} style={{display:'flex',justifyContent:'space-between',gap:24,padding:'4px 0',fontSize:10,color:'#6b7280'}}>
                      <span>{lbl}</span>
                      <span style={{fontWeight:600,color:'#374151'}}>{typeof val === 'number' ? val.toLocaleString('tr-TR',{minimumFractionDigits:2})+' ₺' : val as string}</span>
                    </div>
                  ))}
                </div>
                <div style={{background:'#cd6906',color:'#fff',borderRadius:8,padding:'12px 16px',marginTop:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:10,fontWeight:700,opacity:0.9,textTransform:'uppercase',letterSpacing:'0.05em'}}>Genel Toplam</span>
                  <span style={{fontSize:15,fontWeight:800}}>{sozlesme.sozlesmeFiyati.toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</span>
                </div>
              </div>
            </div>

            {/* Ödeme Planı */}
            {aktifOdemeler.length > 0 && (
              <div style={{marginBottom:20}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.08em',color:'#9ca3af',borderBottom:'1px solid #e5e7eb',paddingBottom:4,marginBottom:0}}>{t('sozlesme.odemePlani')}</div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:9.5}}>
                  <thead>
                    <tr style={{background:'#f9fafb',borderBottom:'2px solid #e5e7eb'}}>
                      {['#','Vade Tarihi','Tutar','Ödeme Türü'].map((h,i) => (
                        <th key={h} style={{padding:'8px 10px',textAlign: i>1 ? 'right' : i===0 ? 'center' : 'left',fontSize:9,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.04em'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {aktifOdemeler.map((o, i) => (
                      <tr key={o.odemeID} style={{borderBottom:'1px solid #f1f5f9',background: i%2===0 ? '#fff' : '#fafafa'}}>
                        <td style={{padding:'6px 10px',textAlign:'center',color:'#9ca3af',fontSize:9}}>{i+1}</td>
                        <td style={{padding:'6px 10px',fontWeight:600,color:'#374151'}}>{o.vadeTarihi ? new Date(o.vadeTarihi).toLocaleDateString('tr-TR') : '—'}</td>
                        <td style={{padding:'6px 10px',fontWeight:700,color:'#111827'}}>{o.miktar.toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</td>
                        <td style={{padding:'6px 10px',textAlign:'right',color:'#6b7280'}}>{o.odemeTuru || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{background:'#f9fafb',borderTop:'2px solid #e5e7eb'}}>
                      <td colSpan={2} style={{padding:'6px 10px',fontSize:9,fontWeight:800,color:'#374151',textTransform:'uppercase',letterSpacing:'0.04em'}}>Toplam</td>
                      <td style={{padding:'6px 10px',fontWeight:800,color:'#cd6906'}}>{aktifOdemeler.reduce((s,o)=>s+o.miktar,0).toLocaleString('tr-TR',{minimumFractionDigits:2})} ₺</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* İmza */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:48,marginTop:32,paddingTop:24,borderTop:'1px solid #e5e7eb'}}>
              {[{title:'SATICI', name: k?.firmaAdi || ''}, {title:'ALICI', name: sozlesme.cari?.adiSoyadi || ''}].map(s => (
                <div key={s.title}>
                  <div style={{fontSize:8,fontWeight:800,textTransform:'uppercase',letterSpacing:1,color:'#111827',marginBottom:4}}>{s.title}</div>
                  <div style={{fontSize:10,fontWeight:600,color:'#374151',marginBottom:12}}>{s.name}</div>
                  <div style={{height:48,borderBottom:'1.5px dashed #cbd5e1',marginBottom:8}}></div>
                  <div style={{fontSize:8.5,color:'#9ca3af'}}>Kaşe / İmza &nbsp;&nbsp; Tarih: _____ / _____ / _________</div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{marginTop:20,borderTop:'1px solid #e5e7eb',paddingTop:8,textAlign:'center',fontSize:8,color:'#9ca3af'}}>
              {k?.firmaAdi ? k.firmaAdi+'  |  ' : ''}{k?.adres || ''}{k?.sehir ? ' / '+k.sehir : ''}
              {k && <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>}Bu teklif {teklifTarihi} tarihinde düzenlenmiştir.
            </div>

          </div>
        </div>
      </div>
    );
  }
  // --- End Teklif Önizleme ---

  return (
    <>
      <style>{`
        @keyframes sd-spin { to { transform: rotate(360deg); } }
        .sd-wrap { padding: 24px; font-family: 'Inter','Segoe UI',sans-serif; }
        .sd-topbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #f3f4f6; }
        .sd-page-title { font-size: 20px; font-weight: 600; color: #111827; display: flex; align-items: center; gap: 10px; }
        .sd-takip-chip { background: #f3f4f6; color: #6b7280; font-size: 11px; font-weight: 500; padding: 3px 10px; border-radius: 20px; letter-spacing: 0.3px; }
        .sd-action-bar { display: flex; align-items: center; gap: 8px; position: relative; }
        .sd-btn-back { background: none; border: none; border-radius: 8px; padding: 8px 14px; font-size: 13px; font-weight: 500; color: #6b7280; cursor: pointer; font-family: inherit; transition: all 0.15s; display: flex; align-items: center; gap: 6px; }
        .sd-btn-back:hover { background: #f3f4f6; color: #111827; }
        .sd-btn-actions { background: #111827; border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 500; color: #fff; cursor: pointer; font-family: inherit; transition: background 0.15s; display: flex; align-items: center; gap: 6px; }
        .sd-btn-actions:hover { background: #1f2937; }
        .sd-ctx-menu { position: absolute; top: calc(100% + 8px); right: 0; background: rgba(255,255,255,0.98); backdrop-filter: blur(16px); border: 1px solid #e5e7eb; border-radius: 12px; box-shadow: 0 8px 24px rgba(15,23,42,0.1); min-width: 230px; z-index: 9999; padding: 6px; }
        .sd-ctx-item { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500; color: #374151; transition: background 0.12s; border: none; background: none; width: 100%; text-align: left; font-family: inherit; }
        .sd-ctx-item:hover { background: #f9fafb; color: #111827; }
        .sd-ctx-item.danger { color: #e11d48; } .sd-ctx-item.danger:hover { background: #fff1f2; }
        .sd-ctx-icon { width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; }
        .sd-ctx-divider { height: 1px; background: #f3f4f6; margin: 4px 0; }
        /* Cards */
        .sd-cards-row { display: grid; grid-template-columns: 1fr 1fr 1.6fr; gap: 16px; margin-bottom: 24px; }
        .sd-card { background: #fff; border-radius: 14px; border: 1px solid #e5e7eb; overflow: hidden; }
        .sd-card-accent { display: none; }
        .sd-card-body { padding: 20px 22px; }
        .sd-card-title { font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #f3f4f6; display: flex; align-items: center; gap: 7px; }
        .sd-card-title i { color: #9ca3af; }
        .sd-row { display: flex; flex-direction: column; gap: 11px; }
        .sd-field { display: flex; flex-direction: column; gap: 3px; }
        .sd-daire-grid { display: flex; flex-direction: column; }
        .sd-daire-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border-bottom: 1px solid #eeeeee; }
        .sd-daire-row:last-child { border-bottom: none; }
        .sd-daire-cell { display: flex; flex-direction: column; gap: 3px; padding: 10px 4px; }
        .sd-daire-cell:first-child { padding-right: 20px; border-right: 1px solid #eeeeee; }
        .sd-daire-cell:last-child { padding-left: 20px; }
        .sd-daire-label { font-size: 10px; font-weight: 600; color: #9ca3af; letter-spacing: 0.06em; text-transform: uppercase; }
        .sd-daire-val { font-size: 13px; font-weight: 500; color: #111827; line-height: 1.4; }
        .sd-label { font-size: 10px; color: #9ca3af; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
        .sd-val { font-size: 13.5px; font-weight: 500; color: #111827; }
        .sd-val-link { font-size: 13.5px; font-weight: 600; color: #cd6906; cursor: pointer; background: none; border: none; padding: 0; font-family: inherit; }
        .sd-val-link:hover { color: #a85505; text-decoration: underline; }
        .sd-val-muted { font-size: 13px; color: #6b7280; font-weight: 400; }
        .sd-durum-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; border-radius: 20px; padding: 5px 13px; }
        /* Financial section */
        .sd-fin-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 16px; }
        .sd-fin-item { display: flex; flex-direction: column; gap: 3px; }
        .sd-fin-label { font-size: 10px; color: #9ca3af; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
        .sd-fin-val { font-size: 16px; font-weight: 600; color: #cd6906; letter-spacing: -0.3px; }
        .sd-progress-wrap { margin-top: 14px; }
        .sd-progress-header { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 11.5px; font-weight: 600; }
        .sd-progress-bar { height: 8px; background: #f1f5f9; border-radius: 20px; overflow: hidden; }
        .sd-progress-fill { height: 100%; border-radius: 20px; background: linear-gradient(90deg,#22c55e,#16a34a); transition: width 0.4s ease; }
        .sd-summary-pills { display: flex; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
        .sd-summary-pill { display: flex; flex-direction: column; gap: 2px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px 14px; flex: 1; min-width: 90px; }
        .sd-szel { font-size: 10px; color: #9ca3af; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
        .sd-szev { font-size: 13px; font-weight: 600; letter-spacing: -0.2px; }
        /* Payments table */
        .sd-payments-wrap { background: #fff; border-radius: 14px; border: 1px solid #e5e7eb; }
        .sd-ledger-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .sd-payments-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid #f3f4f6; }
        .sd-payments-title { font-size: 13px; font-weight: 600; color: #111827; display: flex; align-items: center; gap: 8px; }
        .sd-count-chip { background: #f3f4f6; color: #6b7280; border-radius: 20px; padding: 2px 10px; font-size: 12px; font-weight: 500; }
        .sd-hdr-btns { display: flex; gap: 8px; }
        .sd-btn-plan { background: none; border: 1px solid #e5e7eb; color: #374151; border-radius: 8px; padding: 7px 14px; font-size: 12px; font-weight: 500; cursor: pointer; font-family: inherit; display: flex; align-items: center; gap: 6px; transition: all 0.13s; }
        .sd-btn-plan:hover { background: #f9fafb; border-color: #d1d5db; }
        .sd-btn-add { background: #cd6906; border: none; color: #fff; border-radius: 8px; padding: 7px 14px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; display: flex; align-items: center; gap: 6px; transition: background 0.13s; box-shadow: 0 1px 3px rgba(205,105,6,0.3); }
        .sd-btn-add:hover { background: #a85505; }
        /* Ledger table */
        .sd-ledger { width: 100%; border-collapse: collapse; }
        .sd-ledger thead th { padding: 10px 14px; font-size: 10px; font-weight: 600; color: #9ca3af; letter-spacing: 0.06em; text-transform: uppercase; background: #f9fafb; border-bottom: 1px solid #e5e7eb; white-space: nowrap; }
        .sd-ledger tbody tr { border-bottom: 1px solid #f3f4f6; transition: background 0.1s; }
        .sd-ledger tbody tr:last-child { border-bottom: none; }
        .sd-ledger tbody tr:hover { background: #fafafa; }
        .sd-ledger td { padding: 11px 14px; font-size: 13px; vertical-align: middle; }
        .sd-amount { font-size: 13px; font-weight: 500; color: #111827; letter-spacing: -0.1px; white-space: nowrap; }
        .sd-date-cell { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 500; white-space: nowrap; }
        .sd-date-normal { color: #334155; }
        .sd-date-overdue { color: #dc2626; font-weight: 700; }
        .sd-date-done { color: #16a34a; }
        .sd-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 600; border-radius: 20px; padding: 4px 10px; white-space: nowrap; }
        .sd-badge-tahsilat { background: #dcfce7; color: #15803d; }
        .sd-badge-odeme { background: #fee2e2; color: #b91c1c; }
        .sd-badge-bekliyor { background: #fef9c3; color: #a16207; }
        .sd-badge-odendi { background: #dcfce7; color: #15803d; }
        .sd-badge-tur { background: #fff8f0; color: #e67e22; }
        .sd-edit-btn { width: 30px; height: 30px; border-radius: 8px; background: none; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: none; cursor: pointer; opacity: 0; transition: all 0.15s; color: #64748b; font-size: 12px; align-items: center; justify-content: center; }
        .sd-ledger tbody tr:hover .sd-edit-btn { opacity: 1; }
        .sd-edit-btn:hover { background: #fffbeb; border-color: #f59e0b; color: #b45309; }
        .sd-aciklama { font-size: 12px; color: #64748b; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        /* Ledger footer */
        .sd-ledger-footer { display: grid; grid-template-columns: 1fr 1fr 1fr; border-top: 1px solid #e5e7eb; }
        .sd-footer-cell { padding: 12px 16px; display: flex; flex-direction: column; gap: 4px; border-right: 1px solid #f3f4f6; }
        .sd-footer-cell:last-child { border-right: none; }
        .sd-footer-label { font-size: 10px; font-weight: 600; color: #9ca3af; letter-spacing: 0.06em; text-transform: uppercase; }
        .sd-footer-val { font-size: 13px; font-weight: 600; letter-spacing: -0.2px; }
        .sd-empty { padding: 56px 24px; text-align: center; }
        .sd-empty-icon { font-size: 38px; opacity: 0.2; margin-bottom: 12px; }
        .sd-empty-text { font-size: 13px; color: #94a3b8; font-weight: 500; }
        /* Native Modals */
        .sd-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 24px; backdrop-filter: blur(4px); }
        .sd-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 560px; box-shadow: 0 20px 60px rgba(15,23,42,0.14); overflow: hidden; max-height: 90vh; display: flex; flex-direction: column; }
        .sd-modal-lg { max-width: 680px; }
        .sd-modal-xl { max-width: 920px; }
        .sd-modal-sm { max-width: 440px; }
        .sd-modal-header { padding: 16px 22px; border-bottom: 1px solid #f3f4f6; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
        .sd-modal-title { font-size: 14px; font-weight: 600; color: #111827; }
        .sd-modal-close { width: 28px; height: 28px; border-radius: 7px; background: none; border: 1px solid #e5e7eb; cursor: pointer; color: #9ca3af; font-size: 13px; display: flex; align-items: center; justify-content: center; }
        .sd-modal-close:hover { background: #f3f4f6; color: #374151; }
        .sd-modal-body { padding: 20px 22px; overflow-y: auto; }
        .sd-modal-footer { padding: 12px 22px; border-top: 1px solid #f3f4f6; display: flex; justify-content: flex-end; gap: 9px; flex-shrink: 0; background: rgba(255,255,255,0.8); backdrop-filter: blur(8px); }
        .sd-modal-footer-split { justify-content: space-between; }
        .sd-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .sd-form-field { display: flex; flex-direction: column; gap: 5px; }
        .sd-form-label { font-size: 11px; font-weight: 600; color: #6b7280; letter-spacing: 0.06em; text-transform: uppercase; }
        .sd-form-input { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; padding: 9px 12px; font-size: 13px; color: #111827; outline: none; font-family: inherit; transition: all 0.15s; width: 100%; box-sizing: border-box; }
        .sd-form-input:focus { border-color: #111827; box-shadow: 0 0 0 3px rgba(17,24,39,0.08); }
        .sd-form-select { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; padding: 9px 32px 9px 12px; font-size: 13px; color: #111827; outline: none; font-family: inherit; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%2394a3b8' d='M5 7L0 2h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; width: 100%; box-sizing: border-box; cursor: pointer; }
        .sd-form-select:focus { border-color: #111827; box-shadow: 0 0 0 3px rgba(17,24,39,0.08); }
        .sd-form-textarea { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; padding: 9px 12px; font-size: 13px; color: #111827; outline: none; font-family: inherit; transition: all 0.15s; width: 100%; box-sizing: border-box; resize: vertical; }
        .sd-form-textarea:focus { border-color: #111827; box-shadow: 0 0 0 3px rgba(17,24,39,0.08); }
        .sd-form-group-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
        .sd-sablon-btn { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 7px; padding: 5px 11px; font-size: 12px; color: #cd6906; cursor: pointer; font-family: inherit; font-weight: 500; transition: all 0.15s; display: flex; align-items: center; gap: 6px; }
        .sd-sablon-btn:hover { background: #cd6906; color: #fff; border-color: #cd6906; }
        .sd-sablon-list { max-height: 380px; overflow-y: auto; }
        .sd-sablon-item { padding: 13px 18px; border-bottom: 1px solid #f1f5f9; cursor: pointer; transition: background 0.15s; }
        .sd-sablon-item:hover { background: #f8fafc; }
        .sd-sablon-item:last-child { border-bottom: none; }
        .sd-sablon-title { font-size: 14px; font-weight: 600; color: #0f172a; margin-bottom: 3px; }
        .sd-sablon-preview { font-size: 12px; color: #64748b; line-height: 1.4; max-height: 32px; overflow: hidden; }
        /* Quill editor in update modal */
        .sd-quill-wrap .quill { background: #fff; border-radius: 8px; border: 1px solid #d1d5db; }
        .sd-quill-wrap .quill:focus-within { border-color: #111827; box-shadow: 0 0 0 3px rgba(17,24,39,0.08); }
        .sd-quill-wrap .ql-toolbar { border: none; border-bottom: 1px solid #e5e7eb; border-radius: 8px 8px 0 0; background: #f9fafb; }
        .sd-quill-wrap .ql-container { border: none; font-size: 13px; font-family: inherit; min-height: 180px; }
        .sd-quill-wrap .ql-editor { min-height: 180px; padding: 12px 14px; }
        .sd-quill-wrap .ql-editor.ql-blank::before { color: #94a3b8; font-style: normal; }
        .sd-input-wrap { position: relative; display: flex; align-items: center; }
        .sd-input-wrap .sd-form-input { padding-right: 30px; }
        .sd-input-suffix { position: absolute; right: 10px; font-size: 13px; color: #94a3b8; pointer-events: none; }
        .sd-btn-cancel { background: none; border: 1px solid #e5e7eb; border-radius: 8px; padding: 9px 18px; font-size: 13px; font-weight: 500; color: #6b7280; cursor: pointer; font-family: inherit; transition: all 0.15s; }
        .sd-btn-cancel:hover { border-color: #d1d5db; color: #374151; }
        .sd-btn-cancel:disabled { opacity: 0.4; cursor: not-allowed; }
        .sd-btn-primary { background: #111827; border: none; border-radius: 8px; padding: 9px 20px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; font-family: inherit; transition: background 0.15s; display: flex; align-items: center; gap: 7px; }
        .sd-btn-primary:hover { background: #1f2937; }
        .sd-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .sd-btn-success { background: #cd6906; border: none; border-radius: 8px; padding: 9px 20px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; font-family: inherit; transition: background 0.15s; display: flex; align-items: center; gap: 7px; box-shadow: 0 1px 3px rgba(205,105,6,0.3); }
        .sd-btn-success:hover { background: #a85505; }
        .sd-btn-success:disabled { opacity: 0.5; cursor: not-allowed; }
        .sd-btn-danger { background: #f43f5e; border: none; border-radius: 8px; padding: 9px 20px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; font-family: inherit; transition: background 0.15s; display: flex; align-items: center; gap: 7px; }
        .sd-btn-danger:hover { background: #e11d48; }
        .sd-btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
        .sd-btn-info { background: #2563eb; border: none; border-radius: 8px; padding: 9px 20px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; font-family: inherit; transition: background 0.15s; display: flex; align-items: center; gap: 7px; }
        .sd-btn-info:hover { background: #1d4ed8; }
        .sd-btn-info:disabled { opacity: 0.5; cursor: not-allowed; }
        .sd-warn-box { background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 12px; padding: 14px 16px; margin-bottom: 14px; }
        .sd-warn-box ul { padding-left: 18px; margin: 6px 0 0; }
        .sd-warn-box li { font-size: 13px; color: #78350f; margin-bottom: 3px; }
        .sd-iptal-card { background: #f8fafc; border: 1.5px solid #f1f5f9; border-radius: 12px; padding: 14px 16px; margin-bottom: 14px; }
        .sd-iptal-card p { margin: 0 0 6px; font-size: 13px; color: #334155; }
        .sd-iptal-card p:last-child { margin: 0; }
        .sd-preview-chip { background: #eff6ff; border: 1.5px solid #bfdbfe; border-radius: 10px; padding: 10px 14px; font-size: 12.5px; color: #1e40af; margin-top: 12px; }
        .sd-file-list { display: flex; flex-direction: column; gap: 8px; }
        .sd-file-item { display: flex; align-items: center; justify-content: space-between; background: #f8fafc; border: 1.5px solid #f1f5f9; border-radius: 11px; padding: 12px 14px; }
        .sd-file-name { font-size: 13px; font-weight: 600; color: #0f172a; display: flex; align-items: center; gap: 8px; }
        .sd-file-dl { background: none; border: 1.5px solid #3b82f6; color: #3b82f6; border-radius: 8px; padding: 6px 14px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.13s; }
        .sd-file-dl:hover { background: #eff6ff; }
        .sd-spinner-sm { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: sd-spin 0.7s linear infinite; }
        @keyframes sd-spin { to { transform: rotate(360deg); } }
        /* Responsive */
        @media (max-width: 991px) {
          .sd-cards-row { grid-template-columns: 1fr; }
          .sd-ledger-footer { grid-template-columns: 1fr; }
          .sd-footer-cell { border-right: none; border-bottom: 1px solid #f1f5f9; }
          .sd-footer-cell:last-child { border-bottom: none; }
        }
        @media (max-width: 768px) {
          .sd-wrap { padding: 16px 12px; }
          .sd-topbar { flex-wrap: wrap; gap: 10px; }
          .sd-page-title { font-size: 17px; }
          .sd-action-bar { width: 100%; justify-content: flex-end; }
          .sd-daire-row { grid-template-columns: 1fr; }
          .sd-daire-cell:first-child { border-right: none; padding-right: 4px; border-bottom: 1px solid #eee; }
          .sd-daire-cell:last-child { padding-left: 4px; }
          .sd-fin-row { grid-template-columns: 1fr; gap: 10px; }
          .sd-payments-header { flex-wrap: wrap; gap: 10px; }
          .sd-hdr-btns { width: 100%; justify-content: flex-end; }
          .sd-ledger { font-size: 12px; }
          .sd-ledger thead th { padding: 8px 10px; font-size: 10px; }
          .sd-ledger td { padding: 10px; }
          .sd-form-grid { grid-template-columns: 1fr; }
          .sd-summary-pills { flex-direction: column; }
          .sd-summary-pill { min-width: unset; }
        }
      `}</style>

      {/* Context menu overlay */}
      {showActionsMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setShowActionsMenu(false)} />}

      <div className="sd-wrap">
        {/* Top Bar */}
        <div className="sd-topbar">
          <div className="sd-page-title">
            <i className="fas fa-file-contract" style={{ color: '#9ca3af', fontSize: 18 }}></i> {t('sozlesmeDetay.baslik', 'Sözleşme Detayı')}
          </div>
          <div className="sd-action-bar">
            <button className="sd-btn-back" onClick={() => navigate('/sozlesmeler')}>
              <i className="fas fa-arrow-left"></i> {t('btn.geri', 'Geri Dön')}
            </button>
            <button className="sd-btn-actions" onClick={() => setShowActionsMenu(v => !v)}>
              <i className="fas fa-cog"></i> {t('label.islemler', 'İşlemler')} <i className="fas fa-chevron-down" style={{ fontSize: 10 }}></i>
            </button>
            {showActionsMenu && (
              <div className="sd-ctx-menu">
                <button className="sd-ctx-item" onClick={() => { setShowActionsMenu(false); handleShowBelgelerModal(); }}>
                  <span className="sd-ctx-icon" style={{ background: '#eff6ff' }}><i className="fas fa-file-alt" style={{color:'#3b82f6'}}></i></span> {t('sozlesmeDetay.sozlesmeBelgeleri', 'Sözleşme Belgeleri')}
                </button>
                <button className="sd-ctx-item" onClick={() => { setShowActionsMenu(false); openImalatNotlariModal(); }}>
                  <span className="sd-ctx-icon" style={{ background: '#fff7ed' }}><i className="fas fa-tools" style={{color:'#d97706'}}></i></span> {t('sozlesmeDetay.imalatNotlari', 'İmalat Notları')}
                </button>
                <button className="sd-ctx-item" onClick={() => { setShowActionsMenu(false); openHareketlerModal(); }}>
                  <span className="sd-ctx-icon" style={{ background: '#f0fdf4' }}><i className="fas fa-history" style={{color:'#16a34a'}}></i></span> {t('sozlesmeDetay.hareketler', 'Sözleşme Hareketleri')}
                </button>
                <div className="sd-ctx-divider" />
                <button className="sd-ctx-item" onClick={() => { setShowActionsMenu(false); handleShowUpdateModal(); }}>
                  <span className="sd-ctx-icon" style={{ background: '#f0fdf4' }}><i className="fas fa-edit" style={{color:'#16a34a'}}></i></span> {t('sozlesmeDetay.bilgileriGuncelle', 'Bilgileri Güncelle')}
                </button>
                <div className="sd-ctx-divider" />
                <button className="sd-ctx-item" onClick={() => { setShowActionsMenu(false); setShowOdemePlaniRapor(true); }}>
                  <span className="sd-ctx-icon" style={{ background: '#fefce8' }}><i className="fas fa-print" style={{color:'#ca8a04'}}></i></span> {t('sozlesmeDetay.odemePlaniYazdir', 'Ödeme Planı Yazdır')}
                </button>
                <button className="sd-ctx-item" onClick={() => { setShowActionsMenu(false); handleSenetleriIndir(); }}>
                  <span className="sd-ctx-icon" style={{ background: '#f0fdf4' }}><i className="fas fa-file-download" style={{color:'#16a34a'}}></i></span> {t('sozlesmeDetay.senetleriIndir', 'Senetleri İndir')}
                </button>
                <div className="sd-ctx-divider" />
                <button className="sd-ctx-item" onClick={() => { setShowActionsMenu(false); generateHazirSozlesmePDF(); }}>
                  <span className="sd-ctx-icon" style={{ background: '#fff7ed' }}><i className="fas fa-file-contract" style={{color:'#cd6906'}}></i></span> {t('sozlesmeDetay.hazirSozlesmeIndir', 'Hazır Sözleşme İndir')}
                </button>
                {sozlesme.sozlesmeDurumu === 'Rezerve' && (
                  <>
                    <div className="sd-ctx-divider" />
                    <button className="sd-ctx-item" onClick={() => { setShowActionsMenu(false); generateTeklifPDF(); }}>
                      <span className="sd-ctx-icon" style={{ background: '#fef3c7' }}><i className="fas fa-file-download" style={{color:'#d97706'}}></i></span> {t('sozlesmeDetay.teklifIndir', 'Teklif Olarak İndir')}
                    </button>
                  </>
                )}
                <div className="sd-ctx-divider" />
                <button className="sd-ctx-item danger" onClick={() => { setShowActionsMenu(false); setShowIptalModal(true); }}>
                  <span className="sd-ctx-icon" style={{ background: '#fef2f2' }}><i className="fas fa-ban" style={{color:'#dc2626'}}></i></span> {t('sozlesmeDetay.sozlesmeIptal', 'Sözleşmeyi İptal Et')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Info Cards Row */}
        <div className="sd-cards-row">

          {/* Müşteri Bilgileri */}
          <div className="sd-card">
            <div className="sd-card-body">
          <div className="sd-card-title"><i className="fas fa-user"></i> {t('sozlesmeDetay.musteriBilgileri', 'Müşteri Bilgileri')}</div>
              <div className="sd-row">
                <div className="sd-field">
                  <span className="sd-label">{t('sozlesmeDetay.adSoyad', 'Ad Soyad')}</span>
                  <button className="sd-val-link" onClick={() => navigate(`/musteriler/${sozlesme.cari?.cariID}`)}>
                    {sozlesme.cari?.adiSoyadi || '—'}
                  </button>
                </div>
                {sozlesme.cari?.firmaAdi && (
                  <div className="sd-field">
                    <span className="sd-label">{t('label.firma', 'Firma')}</span>
                    <span className="sd-val-muted">{sozlesme.cari.firmaAdi}</span>
                  </div>
                )}
                <div className="sd-field">
                  <span className="sd-label">{t('label.telefon', 'Telefon')}</span>
                  <span className="sd-val">{sozlesme.cari?.telefon || '—'}</span>
                  {sozlesme.cari?.telefon2 && <span className="sd-val-muted">{sozlesme.cari.telefon2}</span>}
                </div>
                {sozlesme.cari?.email && (
                  <div className="sd-field">
                    <span className="sd-label">{t('label.eposta', 'E-Posta')}</span>
                    <span className="sd-val-muted">{sozlesme.cari.email}</span>
                  </div>
                )}
                {sozlesme.cari?.tc && (
                  <div className="sd-field">
                    <span className="sd-label">{t('label.tcKimlik', 'TC Kimlik')}</span>
                    <span className="sd-val-muted">{sozlesme.cari.tc}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Daire Bilgileri */}
          <div className="sd-card">
            <div className="sd-card-body">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="sd-card-title" style={{ marginBottom: 12, borderBottom: '1px solid #f3f4f6', paddingBottom: 12 }}><i className="fas fa-building"></i> {t('sozlesmeDetay.daireBilgileri', 'Daire Bilgileri')}</div>
                <button
                  onClick={() => setShowDaireDetayModal(true)}
                  title="Daire Detayı"
                  style={{
                    background: 'none',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 16,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#8b5cf6'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#64748b'; }}
                >
                  ⋮
                </button>
              </div>
              <div className="sd-daire-grid" style={{ marginTop: 12 }}>
                <div className="sd-daire-row">
                  <div className="sd-daire-cell">
                    <span className="sd-daire-label">{t('label.proje', 'Proje')}:</span>
                    <span className="sd-daire-val">{sozlesme.daire?.blok?.proje?.projeAdi || '—'}</span>
                  </div>
                  <div className="sd-daire-cell">
                    <span className="sd-daire-label">{t('sozlesmeDetay.blokDaire', 'Blok / Daire')}:</span>
                    <span className="sd-daire-val">{sozlesme.daire?.blok?.blokAdi || '—'} – {sozlesme.daire?.daireNo ? `Daire ${sozlesme.daire.daireNo}` : '—'}</span>
                  </div>
                </div>
                <div className="sd-daire-row">
                  <div className="sd-daire-cell">
                    <span className="sd-daire-label">{t('sozlesmeDetay.kat', 'Kat')}:</span>
                    <span className="sd-daire-val">{sozlesme.daire?.kat?.katAdi || '—'}</span>
                  </div>
                  <div className="sd-daire-cell">
                    <span className="sd-daire-label">{t('sozlesmeDetay.cepheManzara', 'Cephe / Manzara')}:</span>
                    <span className="sd-daire-val">
                      {[sozlesme.daire?.cephe?.cepheAdi, sozlesme.daire?.manzara?.manzaraAdi].filter(Boolean).join(' / ') || '—'}
                    </span>
                  </div>
                </div>
                <div className="sd-daire-row">
                  <div className="sd-daire-cell">
                    <span className="sd-daire-label">{t('sozlesmeDetay.alan', 'Alan')}:</span>
                    <span className="sd-daire-val">Net {sozlesme.daire?.netM2 ?? '—'} m² &ndash; Brüt {sozlesme.daire?.brutM2 ?? '—'} m²</span>
                  </div>
                  <div className="sd-daire-cell">
                    <span className="sd-daire-label">{t('label.tip', 'Tip')}:</span>
                    <span className="sd-daire-val">{sozlesme.daire?.daireTip?.tip || '—'}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Sözleşme Bilgileri */}
          <div className="sd-card">
            <div className="sd-card-body">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div className="sd-card-title" style={{ marginBottom: 12, borderBottom: '1px solid #f3f4f6', paddingBottom: 12 }}><i className="fas fa-file-contract"></i> {t('sozlesmeDetay.sozlesmeBilgileri', 'Sözleşme Bilgileri')}</div>
                <span className="sd-durum-badge" style={{ background: durumCfg.bg, color: durumCfg.color, border: `1.5px solid ${durumCfg.border}` }}>
                  {durumCfg.label}
                </span>
              </div>

              <div className="sd-fin-row">
                <div className="sd-fin-item">
                  <span className="sd-fin-label">{t('sozlesmeDetay.sozlesmeFiyati', 'Sözleşme Fiyatı')}</span>
                  <span className="sd-fin-val" style={{ color: '#cd6906' }}>{formatCurrency(sozlesme.sozlesmeFiyati)}</span>
                </div>
                <div className="sd-fin-item">
                  <span className="sd-fin-label">{t('sozlesmeDetay.sozlesmeTarihi', 'Sözleşme Tarihi')}</span>
                <span className="sd-fin-val" style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>{formatDate(sozlesme.sozlesmeTarihi)}</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="sd-progress-wrap">
                <div className="sd-progress-header">
                  <span style={{ color: '#16a34a' }}>{t('sozlesmeDetay.odenenPct', 'Ödenen')} %{odenenPct}</span>
                  <span style={{ color: '#94a3b8' }}>{t('sozlesmeDetay.kalanPct', 'Kalan')} %{100 - odenenPct}</span>
                </div>
                <div className="sd-progress-bar">
                  <div className="sd-progress-fill" style={{ width: `${odenenPct}%` }} />
                </div>
              </div>

              {/* Summary pills */}
              <div className="sd-summary-pills">
                <div className="sd-summary-pill">
                  <span className="sd-szel">{t('sozlesmeDetay.planlanan', 'Planlanan')}</span>
                  <span className="sd-szev" style={{ color: '#374151' }}>{formatCurrency(planlananTutar)}</span>
                </div>
                <div className="sd-summary-pill">
                  <span className="sd-szel">{t('sozlesmeDetay.odenen', 'Ödenen')}</span>
                  <span className="sd-szev" style={{ color: '#16a34a' }}>{formatCurrency(odenenTutar)}</span>
                </div>
                <div className="sd-summary-pill">
                  <span className="sd-szel">{t('sozlesmeDetay.kalan', 'Kalan')}</span>
                  <span className="sd-szev" style={{ color: kalanOdeme > 0 ? '#dc2626' : '#16a34a' }}>
                    {kalanOdeme > 0 ? formatCurrency(kalanOdeme) : '✓ ' + t('sozlesmeDetay.tamamlandi', 'Tamamlandı')}
                  </span>
                </div>
                <div className="sd-summary-pill">
                  <span className="sd-szel">{t('sozlesmeDetay.ortVade', 'Ort. Vade')}</span>
                  <span className="sd-szev" style={{ color: '#e67e22', fontSize: 13 }}>{calculateOrtalamaVade()} {t('sozlesmeDetay.gun', 'gün')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Planlanmamış tutar uyarısı */}
        {planlanmamisTutar > 0.01 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: '#fffbeb',
            border: '1.5px solid #f59e0b',
            borderRadius: 10,
            padding: '11px 18px',
            margin: '0 0 12px 0',
          }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 600, color: '#b45309', fontSize: 13 }}>{t('sozlesmeDetay.planlanmamisTutar', 'Planlanmamış Tutar Var')}</span>
              <span style={{ color: '#92400e', fontSize: 13, marginLeft: 8 }}>
                {t('sozlesmeDetay.planlanmamisMesaj', 'Bu sözleşme için henüz taksitlendirilmemiş')}
                {' '}<strong>{new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(planlanmamisTutar)} ₺</strong> {t('sozlesmeDetay.tutarindaOdeme', 'tutarında ödeme planlanmamış.')}</span>
            </div>
          </div>
        )}

        {/* Payments Ledger */}
        {sozlesme.sozlesmeDurumu === 'Taşeron' ? (
          <div className="sd-payments-wrap">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              background: '#fffbeb',
              border: '1.5px solid #fcd34d',
              borderRadius: 12,
              padding: '18px 22px',
              margin: '4px 0'
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: '#fef3c7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}>
                <i className="fas fa-info-circle" style={{ color: '#d97706', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 3 }}>{t('sozlesmeDetay.odemePlaniHakkinda', 'Ödeme Planı Hakkında Bilgi')}</div>
                <div style={{ fontSize: 13, color: '#b45309' }}>
                  {t('sozlesmeDetay.taseronOdemeMesaji', 'Taşeron sözleşmeleri ödeme planları cari hesapları üzerinden takip edilmektedir.')}
                </div>
              </div>
            </div>
          </div>
        ) : (
        <div id="tahsilatlar-section" className="sd-payments-wrap">
          <div className="sd-payments-header">
            <div className="sd-payments-title">
              <i className="fas fa-credit-card"></i> {t('sozlesmeDetay.tahsilatlar', 'Tahsilatlar')}
              <span className="sd-count-chip">{taksitler.length + getDirectTahsilatlar().length}</span>
            </div>
            <div className="sd-hdr-btns" style={{ position: 'relative' }}>
              {showTahsilatIslemlerMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setShowTahsilatIslemlerMenu(false)} />}
              <button className="sd-btn-plan" onClick={() => setShowTahsilatIslemlerMenu(v => !v)} style={{ position: 'relative', zIndex: 9999 }}>
                <i className="fas fa-cog"></i> {t('label.islemler', 'İşlemler')} <i className="fas fa-chevron-down" style={{ fontSize: 10 }}></i>
              </button>
              {showTahsilatIslemlerMenu && (
                <div className="sd-ctx-menu">
                  {kalanOdeme > 0 && (
                    <button className="sd-ctx-item" onClick={() => { setShowTahsilatIslemlerMenu(false); handleOpenHazirPlanModal(); }}>
                      <span className="sd-ctx-icon" style={{ background: '#fff7ed' }}><i className="fas fa-file-invoice-dollar" style={{ color: 'rgb(205,105,6)' }}></i></span> {t('sozlesmeDetay.hazirOdemePlani', 'Hazır Ödeme Planı')}
                    </button>
                  )}
                  <button className="sd-ctx-item" onClick={() => { setShowTahsilatIslemlerMenu(false); handleShowOdemeModal(); }}>
                    <span className="sd-ctx-icon" style={{ background: '#f0fdf4' }}><i className="fas fa-plus-circle" style={{ color: '#16a34a' }}></i></span> {t('sozlesmeDetay.tahsilatEkle', 'Tahsilat Ekle')}
                  </button>
                  <button className="sd-ctx-item" onClick={() => { setShowTahsilatIslemlerMenu(false); handleShowTaksitEkleModal(); }}>
                    <span className="sd-ctx-icon" style={{ background: '#fff7ed' }}><i className="fas fa-calendar-plus" style={{ color: 'rgb(205,105,6)' }}></i></span> {t('sozlesmeDetay.taksitEkle', 'Taksit Ekle')}
                  </button>
                  <button className="sd-ctx-item" onClick={() => { setShowTahsilatIslemlerMenu(false); handleShowTopluPlanlaModal(); }}>
                    <span className="sd-ctx-icon" style={{ background: '#fff7ed' }}><i className="fas fa-calendar-alt" style={{ color: 'rgb(205,105,6)' }}></i></span> {t('sozlesmeDetay.topluPlanla', 'Toplu Planla')}
                  </button>
                  <div style={{ height: 1, background: '#f1f5f9', margin: '4px 6px' }} />
                  <button className="sd-ctx-item" onClick={() => { setShowTahsilatIslemlerMenu(false); setShowVadePerformansModal(true); }}>
                    <span className="sd-ctx-icon" style={{ background: '#eff6ff' }}><i className="fas fa-chart-bar" style={{ color: '#3b82f6' }}></i></span> {t('sozlesmeDetay.vadePerformansi', 'Vade Performansı')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {(odemelerLoading || taksitlerLoading) ? (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ width: 28, height: 28, border: '3px solid #f1f5f9', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'sd-spin 0.8s linear infinite', margin: '0 auto 10px' }} />
              <div style={{ fontSize: 13, color: '#94a3b8' }}>{t('sozlesmeDetay.taksitlerYukleniyor', 'Taksitler yükleniyor...')}</div>
            </div>
          ) : taksitler.length === 0 && getDirectTahsilatlar().length === 0 ? (
            <div className="sd-empty">
              <div className="sd-empty-icon"><i className="fas fa-credit-card"></i></div>
              <div className="sd-empty-text">{t('sozlesmeDetay.odemePlaniYok', 'Bu sözleşmeye ait ödeme planı bulunmamaktadır.')}</div>
            </div>
          ) : (
            <>
              {taksitler.length > 0 && (
              <div className="sd-ledger-scroll">
              <table className="sd-ledger">
                <thead>
                  <tr>
                    <th style={{ width: 36, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        title={t('label.tumunuSec')}
                        checked={taksitler.filter(item => item.odemeDurumu === 'Bekliyor').length > 0 && taksitler.filter(item => item.odemeDurumu === 'Bekliyor').every(item => selectedOdemeler.has(item.taksitID))}
                        onChange={e => {
                          const bekleyenler = taksitler.filter(item => item.odemeDurumu === 'Bekliyor');
                          if (e.target.checked) {
                            setSelectedOdemeler(new Set(bekleyenler.map(item => item.taksitID)));
                          } else {
                            setSelectedOdemeler(new Set());
                          }
                        }}
                      />
                    </th>
                    <th>{t('label.tutar', 'MİKTAR')}</th>
                    <th>{t('sozlesmeDetay.vadeTarihi', 'VADE TARİHİ')}</th>
                    <th>{t('sozlesmeDetay.tahsilatTarihi', 'TAHSİLAT TARİHİ')}</th>
                    <th>{t('label.tur', 'TÜR')}</th>
                    <th>{t('label.durum', 'DURUM')}</th>
                    <th>{t('label.aciklama', 'AÇIKLAMA')}</th>
                    <th style={{ textAlign: 'center' }}>{t('label.islemler', 'İŞLEM')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...taksitler].filter(item => item.odemeDurumu !== 'Iptal').sort((a, b) => new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime()).map((taksit) => {
                    const overdue = taksit.vadeTarihi && taksit.odemeDurumu !== 'Odendi' && new Date(taksit.vadeTarihi) < new Date();
                    const isBekliyor = taksit.odemeDurumu === 'Bekliyor';
                    const isSelected = selectedOdemeler.has(taksit.taksitID);
                    return (
                      <tr key={taksit.taksitID} style={isSelected ? { background: '#fef3c7' } : undefined}>
                        <td style={{ textAlign: 'center', width: 36 }}>
                          {isBekliyor ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={e => {
                                setSelectedOdemeler(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(taksit.taksitID);
                                  else next.delete(taksit.taksitID);
                                  return next;
                                });
                              }}
                            />
                          ) : null}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <div className="sd-amount">{formatCurrency(taksit.miktar)} <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>TL</span></div>
                        </td>
                        <td>
                          {taksit.vadeTarihi ? (
                            <div className={`sd-date-cell ${overdue ? 'sd-date-overdue' : 'sd-date-normal'}`}>
                              <i className="fas fa-calendar-alt"></i> {formatDate(taksit.vadeTarihi)}
                              {overdue && <span style={{ fontSize: 10, background: '#fee2e2', color: '#b91c1c', borderRadius: 4, padding: '1px 5px' }}>{t('durum.gecikmeli', 'Gecikmeli')}</span>}
                            </div>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td>
                          {taksit.tahsilatTarihi ? (
                            <div className="sd-date-cell sd-date-done"><i className="fas fa-check-circle" style={{color:'#16a34a'}}></i> {formatDate(taksit.tahsilatTarihi)}</div>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td><span className="sd-badge sd-badge-tur">{taksit.odemeTuru || taksit.odemeTuruDetay || '—'}</span></td>
                        <td>
                          <span className={`sd-badge ${taksit.odemeDurumu === 'Odendi' ? 'sd-badge-odendi' : taksit.odemeDurumu === 'Iptal' ? 'sd-badge-odeme' : 'sd-badge-bekliyor'}`}>
                            {taksit.odemeDurumu === 'Odendi' ? <><i className="fas fa-check"></i> {t('durum.odendi', 'Ödendi')}</> : taksit.odemeDurumu === 'Iptal' ? <><i className="fas fa-ban"></i> {t('durum.iptal', 'İptal')}</> : <><i className="fas fa-clock"></i> {t('durum.bekliyor', 'Bekliyor')}</>}
                          </span>
                        </td>
                        <td>
                          <div className="sd-aciklama" title={taksit.aciklama}>{taksit.aciklama || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
                        </td>
                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                            {taksit.odemeDurumu === 'Bekliyor' && (
                              <button
                                onClick={() => handleTaksitDetayOpen(taksit)}
                                style={{ padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                                title={t('btn.duzenle')}
                              >
                                <i className="fas fa-edit" style={{ marginRight: 3 }}></i>{t('btn.detay', 'Detay')}
                              </button>
                            )}
                            {taksit.odemeDurumu === 'Bekliyor' && (
                              <button
                                onClick={() => handleTahsilatYap(taksit)}
                                style={{ padding: '4px 10px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                              >
                                {t('btn.tahsilatYap', 'Tahsilat Yap')}
                              </button>
                            )}
                            {taksit.odemeDurumu === 'Odendi' && (
                              <button
                                onClick={() => handleMakbuzYazdir(taksit)}
                                style={{ padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                                title={t('btn.yazdir')}
                              >
                                <i className="fas fa-print"></i> {t('sozlesmeDetay.makbuz', 'Makbuz')}
                              </button>
                            )}
                            {taksit.odemeDurumu === 'Odendi' && (
                              <button
                                onClick={() => handleTahsilatGeriAl(taksit)}
                                style={{ padding: '4px 10px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                              >
                                {t('sozlesmeDetay.geriAl', 'Geri Al')}
                              </button>
                            )}
                            {taksit.odemeDurumu === 'Bekliyor' && (
                              <button
                                onClick={() => axiosInstance.put(`/api/OdemePlanTaksitleri/${taksit.taksitID}/iptal`).then(() => { if (sozlesme?.sozlesmeID) fetchTaksitler(sozlesme.sozlesmeID); })}
                                style={{ padding: '4px 10px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                                title={t('sozlesmeDetay.taksitIptal', 'Taksiti iptal et')}
                              >
                                <i className="fas fa-ban" style={{ marginRight: 3 }}></i>{t('btn.iptal', 'İptal')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              )}

              {/* Toplu İşlem Çubuğu */}
              {taksitler.length > 0 && selectedOdemeler.size > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: '#fef3c7', borderTop: '1px solid #fcd34d', borderRadius: '0 0 8px 8px' }}>
                  <span style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                    <i className="fas fa-check-square" style={{ marginRight: 6 }}></i>
                    {selectedOdemeler.size} {t('sozlesmeDetay.taksitSecildi', 'taksit seçildi')}
                  </span>
                  <button
                    onClick={handleTopluIptal}
                    disabled={topluIptalLoading}
                    style={{ padding: '5px 14px', fontSize: 13, fontWeight: 600, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {topluIptalLoading ? <><i className="fas fa-spinner fa-spin"></i> {t('sozlesmeDetay.iptalEdiliyor', 'İptal ediliyor...')}</> : <><i className="fas fa-ban"></i> {t('sozlesmeDetay.taksitleriIptalEt', 'Taksitleri İptal Et')}</>}
                  </button>
                  <button
                    onClick={() => setSelectedOdemeler(new Set())}
                    style={{ padding: '5px 10px', fontSize: 13, background: 'transparent', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 6, cursor: 'pointer' }}
                  >
                    {t('sozlesmeDetay.secimiTemizle', 'Seçimi Temizle')}
                  </button>
                </div>
              )}

              {/* Direkt (İşlemler'den) girilen tahsilatlar */}
              {getDirectTahsilatlar().length > 0 && (
                <div style={{ marginTop: 12, border: '1px solid #dbeafe', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 14px', background: '#eff6ff', borderBottom: '1px solid #dbeafe', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="fas fa-exchange-alt" style={{ color: '#3b82f6', fontSize: 12 }}></i>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{t('sozlesmeDetay.direktTahsilatlar', 'İşlemler\'den Girilen Tahsilatlar')}</span>
                    <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>{t('sozlesmeDetay.direktTahsilatlarMesaj', 'Bu tahsilatlar ödeme planı dışında doğrudan girilmiştir.')}</span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{t('label.tutar', 'MİKTAR')}</th>
                        <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{t('sozlesmeDetay.tahsilatTarihi', 'TAHSİLAT TARİHİ')}</th>
                        <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{t('label.tur', 'TÜR')}</th>
                        <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{t('label.kasa', 'KASA')}</th>
                        <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{t('label.aciklama', 'AÇIKLAMA')}</th>
                        <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{t('label.durum', 'DURUM')}</th>
                        <th style={{ padding: '6px 12px', textAlign: 'center', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{t('label.islemler', 'İŞLEM')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getDirectTahsilatlar().map(o => (
                        <tr key={o.odemeID} style={{ borderTop: '1px solid #e0e7ff' }}>
                          <td style={{ padding: '7px 12px', fontWeight: 700, color: '#1e40af' }}>
                            {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0 }).format(o.miktar)} ₺
                          </td>
                          <td style={{ padding: '7px 12px', color: '#374151' }}>
                            {o.tahsilatTarihi ? new Date(o.tahsilatTarihi).toLocaleDateString('tr-TR') : new Date(o.islemTarihi).toLocaleDateString('tr-TR')}
                          </td>
                          <td style={{ padding: '7px 12px', color: '#374151' }}>{o.odemeTuru}</td>
                          <td style={{ padding: '7px 12px', color: '#374151' }}>{o.kasa?.kasaAdi || '—'}</td>
                          <td style={{ padding: '7px 12px', color: '#374151' }}>{o.aciklama || '—'}</td>
                          <td style={{ padding: '7px 12px' }}>
                            <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }}>{t('durum.odendi', 'Ödendi')}</span>
                          </td>
                          <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                            <button
                              onClick={() => handleMakbuzYazdirOdeme(o)}
                              style={{ padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              title={t('btn.yazdir')}
                            >
                              <i className="fas fa-print"></i> {t('sozlesmeDetay.makbuz', 'Makbuz')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Ledger footer totals */}
              <div className="sd-ledger-footer">
                <div className="sd-footer-cell">
                  <span className="sd-footer-label">{t('sozlesmeDetay.toplamOdendi', 'Toplam Ödendi')}</span>
                  <span className="sd-footer-val" style={{ color: '#16a34a' }}>{formatCurrency(odenenTutar)}</span>
                </div>
                <div className="sd-footer-cell">
                  <span className="sd-footer-label">{t('sozlesmeDetay.kalanBakiye', 'Kalan Bakiye')}</span>
                  <span className="sd-footer-val" style={{ color: kalanOdeme > 0 ? '#dc2626' : '#16a34a' }}>
                    {kalanOdeme > 0 ? formatCurrency(kalanOdeme) : `✓ ${t('sozlesmeDetay.tamamlandi', 'Tamamlandı')}`}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* Tahsilat Makbuzu Modal */}
      {showOdemeModal && (
        <div className="sd-modal-overlay" onClick={() => setShowOdemeModal(false)}>
          <div className="sd-modal" style={{ maxWidth: 480, width: '95%' }} onClick={e => e.stopPropagation()}>
            <div className="sd-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="fas fa-arrow-down" style={{ color: '#16a34a', fontSize: 14 }}></i>
                </div>
                <div>
                  <div className="sd-modal-title">{t('sozlesmeDetay.tahsilatMakbuzu', 'Tahsilat Makbuzu')}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{t('sozlesmeDetay.makbuzFormu', 'Makbuz Formu')} · {sozlesme?.cari?.adiSoyadi || ''}</div>
                </div>
              </div>
              <button className="sd-modal-close" onClick={() => setShowOdemeModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="sd-modal-body">
              <div style={{ padding: '6px 10px', background: '#f8fafc', borderRadius: 9, border: '1px solid #f1f5f9', fontSize: 12.5, color: '#475569', marginBottom: 16 }}>
                <strong>{sozlesme?.cari?.adiSoyadi}</strong> {t('sozlesmeDetay.makbuzOlustur', 'için tahsilat makbuzu oluşturun.')}
                {t('sozlesmeDetay.kasaGuncellenir', 'Kasa anında güncellenir; açık faturalara otomatik eşleştirilir.')}
              </div>
              <div className="sd-form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="sd-form-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="sd-form-label">{t('sozlesmeDetay.tutarTL', 'Tutar (₺)')} *</label>
                  <div className="sd-input-wrap">
                    <input
                      type="text"
                      className="sd-form-input"
                      placeholder="0"
                      style={{ textAlign: 'right', fontSize: 16, fontWeight: 700 }}
                      value={odemeFormData.miktar ? formatCurrency(parseFloat(odemeFormData.miktar) || 0) : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[₺\s.]/g, '').replace(/\D/g, '');
                        handleOdemeFormChange('miktar', value);
                      }}
                      onFocus={(e) => { e.target.value = odemeFormData.miktar || ''; }}
                      onBlur={(e) => { if (odemeFormData.miktar) e.target.value = formatCurrency(parseFloat(odemeFormData.miktar) || 0); }}
                    />
                    <span className="sd-input-suffix">₺</span>
                  </div>
                </div>
                <div className="sd-form-field">
                  <label className="sd-form-label">{t('label.tarih', 'Tarih')} *</label>
                  <TrDateInput className="sd-form-input" value={odemeFormData.islemTarihi} onChange={(e) => handleOdemeFormChange('islemTarihi', e.target.value)} />
                </div>
                <div className="sd-form-field">
                  <label className="sd-form-label">{t('label.odemeTuru', 'Ödeme Türü')}</label>
                  <select className="sd-form-select" value={odemeFormData.odemeTuru} onChange={(e) => handleOdemeFormChange('odemeTuru', e.target.value)}>
                    <option value="Nakit">{t('odeme.nakit')}</option>
                    <option value="Çek">{t('odeme.cek')}</option>
                    <option value="Havale/EFT">{t('odeme.havale')}</option>
                    <option value="Kredi Kartı">{t('odeme.krediKarti')}</option>
                  </select>
                </div>
                <div className="sd-form-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="sd-form-label">{t('label.kasa', 'Kasa')} *</label>
                  <select className="sd-form-select" value={odemeFormData.kasaID} onChange={(e) => handleOdemeFormChange('kasaID', e.target.value)}>
                    <option value="">{t('sozlesmeDetay.kasaSecin', 'Kasa seçin...')}</option>
                    {kasalar.map(kasa => (
                      <option key={kasa.kasaID} value={kasa.kasaID}>
                        {kasa.kasaAdi} ({kasa.kasaTuru === 'Nakit' ? '💵' : '🏦'})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sd-form-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="sd-form-label">{t('label.aciklama')}</label>
                  <textarea className="sd-form-textarea" rows={2} placeholder={t('label.ara')} value={odemeFormData.aciklama} onChange={(e) => handleOdemeFormChange('aciklama', e.target.value)}></textarea>
                </div>
              </div>
            </div>
            <div className="sd-modal-footer">
              <button className="sd-btn-cancel" onClick={() => setShowOdemeModal(false)} disabled={saving}>{t('btn.iptal')}</button>
              <button className="sd-btn-success" onClick={handleSaveOdeme} disabled={saving} style={{ background: '#16a34a', boxShadow: '0 1px 3px rgba(22,163,74,0.3)' }}>
                {saving ? <><span className="sd-spinner-sm"></span> {t('msg.kaydediliyor', 'Kaydediliyor...')}</> : <><i className="fas fa-save"></i> {t('btn.kaydet', 'Kaydet')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Taksit Ekle Modal */}
      {showTaksitEkleModal && (
        <div className="sd-modal-overlay" onClick={() => setShowTaksitEkleModal(false)}>
          <div className="sd-modal" style={{ maxWidth: 480, width: '95%' }} onClick={e => e.stopPropagation()}>
            <div className="sd-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="fas fa-calendar-plus" style={{ color: 'rgb(205,105,6)', fontSize: 14 }}></i>
                </div>
                <div>
                  <div className="sd-modal-title">{t('sozlesmeDetay.taksitEkle', 'Taksit Ekle')}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{t('sozlesmeDetay.taksitEkleAciklama', 'Ödeme planına bekleyen tek bir taksit ekleyin.')}</div>
                </div>
              </div>
              <button className="sd-modal-close" onClick={() => setShowTaksitEkleModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="sd-modal-body">
              <div className="sd-form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="sd-form-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="sd-form-label">{t('sozlesmeDetay.miktar', 'Miktar')} (₺) *</label>
                  <div className="sd-input-wrap">
                    <input
                      type="text"
                      className="sd-form-input"
                      placeholder="0"
                      style={{ textAlign: 'right', fontSize: 16, fontWeight: 700 }}
                      value={taksitEkleFormData.miktar ? formatCurrency(parseFloat(taksitEkleFormData.miktar) || 0) : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[₺\s.]/g, '').replace(/\D/g, '');
                        handleTaksitEkleFormChange('miktar', value);
                      }}
                      onFocus={(e) => { e.target.value = taksitEkleFormData.miktar || ''; }}
                      onBlur={(e) => { if (taksitEkleFormData.miktar) e.target.value = formatCurrency(parseFloat(taksitEkleFormData.miktar) || 0); }}
                    />
                    <span className="sd-input-suffix">₺</span>
                  </div>
                </div>
                <div className="sd-form-field">
                  <label className="sd-form-label">{t('sozlesmeDetay.vadeTarihi', 'Vade Tarihi')} *</label>
                  <TrDateInput className="sd-form-input" value={taksitEkleFormData.vadeTarihi} onChange={(e) => handleTaksitEkleFormChange('vadeTarihi', e.target.value)} />
                </div>
                <div className="sd-form-field">
                  <label className="sd-form-label">{t('label.odemeTuru', 'Ödeme Türü')}</label>
                  <select className="sd-form-select" value={taksitEkleFormData.odemeTuru} onChange={(e) => handleTaksitEkleFormChange('odemeTuru', e.target.value)}>
                    <option value="Nakit">{t('odeme.nakit')}</option>
                    <option value="Senet">{t('odeme.senet')}</option>
                    <option value="Cek">{t('odeme.cek')}</option>
                    <option value="Takas">{t('odeme.takas', 'Takas')}</option>
                  </select>
                </div>
                <div className="sd-form-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="sd-form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>{t('label.aciklama')}</span>
                    <AciklamaTurButonlari value={taksitEkleFormData.aciklama} onChange={deger => handleTaksitEkleFormChange('aciklama', deger)} />
                  </label>
                  <textarea className="sd-form-textarea" rows={2} placeholder={t('label.ara')} value={taksitEkleFormData.aciklama} onChange={(e) => handleTaksitEkleFormChange('aciklama', e.target.value)}></textarea>
                </div>
              </div>
            </div>
            <div className="sd-modal-footer">
              <button className="sd-btn-cancel" onClick={() => setShowTaksitEkleModal(false)} disabled={taksitEkleSaving}>{t('btn.iptal')}</button>
              <button className="sd-btn-success" onClick={handleSaveTaksitEkle} disabled={taksitEkleSaving} style={{ background: 'rgb(205,105,6)', boxShadow: '0 1px 3px rgba(205,105,6,0.3)' }}>
                {taksitEkleSaving ? <><span className="sd-spinner-sm"></span> {t('msg.kaydediliyor', 'Kaydediliyor...')}</> : <><i className="fas fa-save"></i> {t('btn.kaydet', 'Kaydet')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vade Performansı Modal */}
      {showVadePerformansModal && (() => {
        const { timelineItems, todayInRange, todayPercent, aylikDagilim, toplamTutar } = getVadePerformansData();
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowVadePerformansModal(false)}>
            <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 940, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-chart-bar" style={{ color: '#3b82f6', fontSize: 15 }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{t('sozlesmeDetay.vadePerformansi', 'Vade Performansı')}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{t('sozlesmeDetay.vadePerformansiAciklama', 'Ödeme planının vade tarihine göre dağılımı')}</div>
                  </div>
                </div>
                <button onClick={() => setShowVadePerformansModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
              </div>

              <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
                {timelineItems.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    {t('sozlesmeDetay.odemePlaniYok', 'Bu sözleşme için henüz ödeme planı bulunmuyor.')}
                  </div>
                ) : (
                  <>
                    {/* Zaman çizelgesi */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t('sozlesmeDetay.zamanCizelgesi', 'Zaman Çizelgesi')} ({timelineItems.length} {t('sozlesmeDetay.kalem', 'kalem')})</div>
                      <span style={{ padding: '4px 12px', background: '#f0fdf4', borderRadius: 20, color: '#166534', fontWeight: 500, fontSize: 12 }}>
                        {t('sozlesmeDetay.toplamHP', 'Toplam')}: ₺{toplamTutar.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '20px 20px 12px', background: '#fafafa' }}>
                      <div style={{ position: 'relative', height: 180, margin: '0 10px' }}>
                        {/* Bugün çizgisi */}
                        {todayInRange && (
                          <div style={{ position: 'absolute', left: `${todayPercent}%`, top: 0, bottom: 24, width: 2, background: '#111827', opacity: 0.35, transform: 'translateX(-50%)' }}>
                            <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: '#111827', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('sozlesmeDetay.bugun', 'Bugün')}</div>
                          </div>
                        )}
                        {/* Taban çizgisi */}
                        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 24, height: 1, background: '#e2e8f0' }} />
                        {/* Barlar */}
                        {timelineItems.map((item, idx) => (
                          <div key={item.taksitID ?? idx} title={`${item.vade.toLocaleDateString('tr-TR')} — ₺${item.miktar.toLocaleString('tr-TR')} — ${VADE_DURUM_ETIKET[item.durumKey]}`}
                            style={{ position: 'absolute', left: `calc(${item.percent}% + ${item.offsetPx}px)`, bottom: 24, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default' }}>
                            <div style={{ width: 10, height: `${(item.barPct / 100) * 130}px`, background: VADE_DURUM_RENK[item.durumKey], borderRadius: '4px 4px 0 0', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
                          </div>
                        ))}
                        {/* Tarih etiketleri (ilk / son) */}
                        <div style={{ position: 'absolute', left: 0, bottom: 0, fontSize: 10.5, color: '#64748b', fontWeight: 500 }}>{timelineItems[0].vade.toLocaleDateString('tr-TR')}</div>
                        <div style={{ position: 'absolute', right: 0, bottom: 0, fontSize: 10.5, color: '#64748b', fontWeight: 500 }}>{timelineItems[timelineItems.length - 1].vade.toLocaleDateString('tr-TR')}</div>
                      </div>
                      {/* Legend */}
                      <div style={{ display: 'flex', gap: 16, marginTop: 14, paddingTop: 12, borderTop: '1px solid #eef2f7', flexWrap: 'wrap' }}>
                        {Object.entries(VADE_DURUM_ETIKET).map(([key, label]) => (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#475569' }}>
                            <span style={{ width: 9, height: 9, borderRadius: 2, background: VADE_DURUM_RENK[key], display: 'inline-block' }} />
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Aylık dağılım */}
                    <div style={{ marginTop: 22 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>{t('sozlesmeDetay.aylikDagilim', 'Aylara Göre Ödeme Dağılımı')}</div>
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('sozlesmeDetay.ayYil', 'Ay / Yıl')}</th>
                              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('sozlesmeDetay.toplamTutar', 'Toplam Tutar')}</th>
                              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', width: 90 }}>{t('sozlesmeDetay.adet', 'Adet')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {aylikDagilim.map((row, idx) => (
                              <tr key={row.key} style={{ borderBottom: idx < aylikDagilim.length - 1 ? '1px solid #f1f5f9' : 'none', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500, textTransform: 'capitalize' }}>{row.label}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>₺{row.toplam.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b' }}>{row.adet}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowVadePerformansModal(false)} style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  {t('btn.kapat', 'Kapat')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Ödeme Düzenleme Modal */}
      {showEditOdemeModal && (
        <div className="sd-modal-overlay" onClick={() => setShowEditOdemeModal(false)}>
          <div className="sd-modal" onClick={e => e.stopPropagation()}>
            <div className="sd-modal-header">
              <div className="sd-modal-title"><i className="fas fa-edit"></i> {t('sozlesmeDetay.odemeDuzenle', 'Ödeme Düzenle')}</div>
              <button className="sd-modal-close" onClick={() => setShowEditOdemeModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="sd-modal-body">
              <div className="sd-form-grid" style={{gridTemplateColumns:'1fr 1fr'}}>
                <div className="sd-form-field" style={{gridColumn:'1/-1'}}>
                  <label className="sd-form-label">{t('sozlesmeDetay.miktar', 'Miktar')} *</label>
                  <div className="sd-input-wrap">
                    <input
                      type="text"
                      className="sd-form-input"
                      placeholder="0"
                      value={editOdemeFormData.miktar ? formatCurrency(parseFloat(editOdemeFormData.miktar) || 0) : ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[₺\s.]/g, '').replace(/\D/g, '');
                        handleEditOdemeFormChange('miktar', value);
                      }}
                      onFocus={(e) => { e.target.value = editOdemeFormData.miktar || ''; }}
                      onBlur={(e) => { if (editOdemeFormData.miktar) e.target.value = formatCurrency(parseFloat(editOdemeFormData.miktar) || 0); }}
                    />
                    <span className="sd-input-suffix">₺</span>
                  </div>
                </div>
                <div className="sd-form-field">
                  <label className="sd-form-label">{t('label.odemeTuru', 'Ödeme Türü')} *</label>
                  <select className="sd-form-select" value={editOdemeFormData.odemeTuru} onChange={(e) => handleEditOdemeFormChange('odemeTuru', e.target.value)}>
                    <option value="Nakit">{t('odeme.nakit')}</option>
                    <option value="Senet">{t('odeme.senet')}</option>
                    <option value="Cek">{t('odeme.cek')}</option>
                    <option value="Takas">{t('odeme.takas', 'Takas')}</option>
                  </select>
                </div>
                <div className="sd-form-field">
                  <label className="sd-form-label">{t('sozlesmeDetay.odemeDurumu', 'Ödeme Durumu')} *</label>
                  <select className="sd-form-select" value={editOdemeFormData.odemeDurumu} onChange={(e) => handleEditOdemeFormChange('odemeDurumu', e.target.value)}>
                    <option value="">{t('sozlesmeDetay.durumSecin', 'Durum seçin')}</option>
                    <option value="Bekliyor">{t('durum.bekliyor', 'Bekliyor')}</option>
                    <option value="Odendi">{t('durum.odendi', 'Ödendi')}</option>
                  </select>
                </div>
                {editOdemeFormData.odemeDurumu === 'Odendi' && (
                  <div className="sd-form-field">
                    <label className="sd-form-label">{t('label.kasa', 'Kasa')} *</label>
                    <select className="sd-form-select" value={editOdemeFormData.kasaID} onChange={(e) => handleEditOdemeFormChange('kasaID', e.target.value)}>
                      <option value="">{t('sozlesmeDetay.kasaSecin', 'Kasa seçin')}</option>
                      {kasalar.map(kasa => (
                        <option key={kasa.kasaID} value={kasa.kasaID}>
                          {kasa.kasaAdi} ({kasa.kasaTuru === 'Nakit' ? '💵' : '🏦'})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="sd-form-field">
                  <label className="sd-form-label">{t('sozlesmeDetay.vadeTarihi', 'Vade Tarihi')} *</label>
                  <TrDateInput className="sd-form-input" value={editOdemeFormData.vadeTarihi} onChange={(e) => handleEditOdemeFormChange('vadeTarihi', e.target.value)} />
                </div>
                {editOdemeFormData.odemeDurumu === 'Odendi' && (
                <div className="sd-form-field">
                  <label className="sd-form-label">{t('sozlesmeDetay.tahsilatTarihi', 'Tahsilat Tarihi')}</label>
                  <TrDateInput className="sd-form-input" value={editOdemeFormData.tahsilatTarihi} onChange={(e) => handleEditOdemeFormChange('tahsilatTarihi', e.target.value)} />
                </div>
                )}
                <div className="sd-form-field" style={{gridColumn:'1/-1'}}>
                  <label className="sd-form-label">{t('label.aciklama')}</label>
                  <textarea className="sd-form-textarea" rows={2} placeholder={t('label.aciklama')} value={editOdemeFormData.aciklama} onChange={(e) => handleEditOdemeFormChange('aciklama', e.target.value)}></textarea>
                </div>
              </div>
            </div>
            <div className="sd-modal-footer sd-modal-footer-split">
              <button className="sd-btn-danger" onClick={handleCancelOdeme} disabled={saving}><i className="fas fa-ban"></i> {t('sozlesmeDetay.odeyiIptalEt', 'Ödemeyi İptal Et')}</button>
              <div style={{display:'flex',gap:'8px'}}>
                <button className="sd-btn-cancel" onClick={() => setShowEditOdemeModal(false)} disabled={saving}>{t('btn.kapat')}</button>
                <button className="sd-btn-success" onClick={handleSaveEditOdeme} disabled={saving}>
                  {saving ? <><span className="sd-spinner-sm"></span> {t('msg.kaydediliyor', 'Kaydediliyor...')}</> : <><i className="fas fa-save"></i> {t('btn.kaydet', 'Kaydet')}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toplu Planla Modal */}
      {showTopluPlanlaModal && (
        <div
          style={{position:'fixed',inset:0,zIndex:1050,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.4)',backdropFilter:'blur(4px)'}}
          onClick={() => setShowTopluPlanlaModal(false)}
        >
          <div
            style={{background:'#fff',borderRadius:16,boxShadow:'0 20px 60px rgba(0,0,0,0.2)',width:'100%',maxWidth:760,maxHeight:'92vh',margin:'0 16px',overflow:'hidden',display:'flex',flexDirection:'column'}}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{padding:'24px 28px 16px',borderBottom:'1px solid #f3f4f6',background:'#fff',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                <div style={{width:36,height:36,borderRadius:10,background:'#fff7ed',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>
                  <CalendarDays size={18} style={{color:'#cd6906'}} />
                </div>
                <div>
                  <div style={{fontSize:17,fontWeight:600,color:'#111827',lineHeight:1.3}}>{t('sozlesmeDetay.topluTahsilatPlanla', 'Toplu Tahsilat Planla')}</div>
                  <div style={{fontSize:13,color:'#6b7280',marginTop:3}}>{t('sozlesmeDetay.topluPlanlaAciklama', 'Belirlediğiniz tutarı taksitlendirerek otomatik tahsilat planı oluşturun.')}</div>
                </div>
              </div>
              <button
                onClick={() => setShowTopluPlanlaModal(false)}
                style={{flexShrink:0,background:'none',border:'none',cursor:'pointer',padding:'6px',borderRadius:6,color:'#9ca3af',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s'}}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#f3f4f6'; (e.currentTarget as HTMLButtonElement).style.color='#374151'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='none'; (e.currentTarget as HTMLButtonElement).style.color='#9ca3af'; }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div style={{padding:'28px',display:'flex',flexDirection:'column',gap:20,overflowY:'auto',flex:1,minHeight:0}}>
              {/* Taksitlendirilecek Tutar — full width */}
              <div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em'}}>{t('sozlesmeDetay.taksitlendirilecekTutar', 'Taksitlendirilecek Tutar')} *</label>
                  <button
                    type="button"
                    onClick={() => handleTopluPlanlaFormChange('taksitlendirilecekTutar', String(Math.max(0, Math.round(calculatePlanlanmamisTutar()))))}
                    style={{background:'none',border:'none',cursor:'pointer',color:'#cd6906',fontSize:11,fontWeight:600,padding:0}}
                  >
                    {t('sozlesmeDetay.kalanTutariGetir', 'Kalan Tutarı Getir')}
                  </button>
                </div>
                <div style={{position:'relative'}}>
                  <input
                    type="text"
                    style={{width:'100%',padding:'10px 36px 10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',transition:'all 0.15s'}}
                    placeholder="0"
                    value={topluPlanlaFormData.taksitlendirilecekTutar ? formatCurrency(parseFloat(topluPlanlaFormData.taksitlendirilecekTutar) || 0) : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[₺\s.]/g, '').replace(/\D/g, '');
                      handleTopluPlanlaFormChange('taksitlendirilecekTutar', value);
                    }}
                    onFocus={(e) => { e.target.value = topluPlanlaFormData.taksitlendirilecekTutar || ''; e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                    onBlur={(e) => { if (topluPlanlaFormData.taksitlendirilecekTutar) e.target.value = formatCurrency(parseFloat(topluPlanlaFormData.taksitlendirilecekTutar) || 0); e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                  />
                  <span style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'#6b7280',pointerEvents:'none'}}>₺</span>
                </div>
              </div>

              {/* 2-col: Taksit Sayısı + Başlama Tarihi */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('sozlesmeDetay.taksitSayisi', 'Taksit Sayısı')} *</label>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    style={{width:'100%',padding:'10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',transition:'all 0.15s'}}
                    value={topluPlanlaFormData.taksitSayisi}
                    onChange={(e) => handleTopluPlanlaFormChange('taksitSayisi', e.target.value)}
                    onFocus={(e) => { e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                    onBlur={(e) => { e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                    placeholder={t('label.ara')}
                  />
                  {topluPlanlaFormData.taksitSayisi && topluPlanlaFormData.taksitlendirilecekTutar && (
                    <div style={{fontSize:11,color:'#cd6906',marginTop:4,fontWeight:500}}>
                      {t('sozlesmeDetay.taksitBasina', 'Taksit başına')}: {formatCurrency((parseFloat(topluPlanlaFormData.taksitlendirilecekTutar) || 0) / (parseInt(topluPlanlaFormData.taksitSayisi) || 1))}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('sozlesmeDetay.baslamaTarihi', 'Başlama Tarihi')} *</label>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{flex:1,minWidth:0}}>
                      <TrDateInput
                        style={{width:'100%',padding:'10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',transition:'all 0.15s'}}
                        value={topluPlanlaFormData.baslamaTarihi}
                        onChange={(e) => handleTopluPlanlaFormChange('baslamaTarihi', e.target.value)}
                        onFocus={(e) => { e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                        onBlur={(e) => { e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                      />
                    </div>
                    {topluPlanlaRows.length > 0 && (
                      <div style={{fontSize:11,color:'#cd6906',fontWeight:600,whiteSpace:'nowrap'}}>
                        {t('sozlesmeDetay.ortVade', 'Ort. Vade')}: {calcTopluPlanlaOrtalamaVade()} {t('sozlesmeDetay.gun', 'gün')}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Ödeme Türü — full width */}
              <div>
                <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('label.odemeTuru', 'Ödeme Türü')} *</label>
                <select
                  style={{width:'100%',padding:'10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',transition:'all 0.15s',appearance:'auto'}}
                  value={topluPlanlaFormData.odemeTuru}
                  onChange={(e) => handleTopluPlanlaFormChange('odemeTuru', e.target.value)}
                  onFocus={(e) => { e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                  onBlur={(e) => { e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                >
                  <option value="">{t('sozlesmeDetay.turSecin', 'Tür seçin')}</option>
                  <option value="Nakit">{t('odeme.nakit')}</option>
                  <option value="Senet">{t('odeme.senet')}</option>
                  <option value="Cek">{t('odeme.cek')}</option>
                  <option value="Takas">{t('odeme.takas', 'Takas')}</option>
                </select>
              </div>

              {/* Açıklama — full width */}
              <div>
                <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('label.aciklama')}</label>
                <textarea
                  rows={2}
                  style={{width:'100%',padding:'10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',resize:'vertical',transition:'all 0.15s',fontFamily:'inherit'}}
                  placeholder={t('label.aciklama')}
                  value={topluPlanlaFormData.aciklama}
                  onChange={(e) => handleTopluPlanlaFormChange('aciklama', e.target.value)}
                  onFocus={(e) => { e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                  onBlur={(e) => { e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                />
              </div>

              {/* Oluşan taksit satırları — düzenlenebilir */}
              {topluPlanlaRows.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t('sozlesmeDetay.odemeTakvimi', 'Ödeme Takvimi')} ({topluPlanlaRows.length} {t('sozlesmeDetay.kalem', 'kalem')})</div>
                    <span style={{ padding: '4px 12px', background: '#f0fdf4', borderRadius: 20, color: '#166534', fontWeight: 500, fontSize: 12 }}>
                      {t('sozlesmeDetay.toplamHP', 'Toplam')}: ₺{topluPlanlaRows.reduce((s, r) => s + r.miktar, 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', width: 34 }}>#</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('label.tur')}</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('sozlesmeDetay.vadeTarihi', 'Vade Tarihi')}</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('sozlesmeDetay.miktarTL', 'Miktar (₺)')}</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('label.aciklama')}</th>
                          <th style={{ padding: '10px 12px', width: 34, borderBottom: '1px solid #e2e8f0' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {topluPlanlaRows.map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: idx < topluPlanlaRows.length - 1 ? '1px solid #f1f5f9' : 'none', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '8px 12px', color: '#94a3b8', fontWeight: 500 }}>{idx + 1}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <select
                                value={row.odemeTuru}
                                onChange={e => {
                                  const newRows = [...topluPlanlaRows];
                                  newRows[idx] = { ...newRows[idx], odemeTuru: e.target.value };
                                  setTopluPlanlaRows(newRows);
                                }}
                                style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, outline: 'none', background: '#fff' }}
                              >
                                <option value="Nakit">{t('odeme.nakit')}</option>
                                <option value="Senet">{t('odeme.senet')}</option>
                                <option value="Cek">{t('odeme.cek')}</option>
                                <option value="Takas">{t('odeme.takas', 'Takas')}</option>
                              </select>
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <TrDateInput
                                value={(() => { const [d, m, y] = row.vadeTarihiStr.split('.'); return `${y}-${m}-${d}`; })()}
                                onChange={e => {
                                  const [y, m, d] = e.target.value.split('-');
                                  const newRows = [...topluPlanlaRows];
                                  newRows[idx] = { ...newRows[idx], vadeTarihiStr: `${d}.${m}.${y}` };
                                  setTopluPlanlaRows(newRows);
                                }}
                                style={{ width: 140, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none' }}
                              />
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                <button
                                  type="button"
                                  title={row.kilitli ? t('sozlesmeDetay.kilitliAciklama', 'Bu tutar kilitli, sistem otomatik dağıtımda değiştirmez') : t('sozlesmeDetay.kilitsizAciklama', 'Bu tutar sistem tarafından otomatik dağıtılır')}
                                  onClick={() => {
                                    const newRows = [...topluPlanlaRows];
                                    newRows[idx] = { ...newRows[idx], kilitli: !newRows[idx].kilitli };
                                    const toplamTutar = parseFloat(topluPlanlaFormData.taksitlendirilecekTutar) || 0;
                                    setTopluPlanlaRows(redistributeTopluRows(newRows, toplamTutar));
                                  }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: row.kilitli ? '#cd6906' : '#cbd5e1' }}
                                >
                                  {row.kilitli ? <Lock size={14} /> : <Unlock size={14} />}
                                </button>
                                <input
                                  type="text"
                                  value={row.miktar.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                                  onChange={e => {
                                    const raw = e.target.value.replace(/\./g, '');
                                    const val = parseInt(raw, 10) || 0;
                                    const toplamTutar = parseFloat(topluPlanlaFormData.taksitlendirilecekTutar) || 0;
                                    const digerKilitliToplam = topluPlanlaRows.reduce((s, r, i) => i !== idx && r.kilitli ? s + r.miktar : s, 0);
                                    const maxDeger = Math.max(0, toplamTutar - digerKilitliToplam);
                                    if (val > maxDeger) {
                                      toast.error(t('sozlesmeDetay.tutarAsimUyarisi', `Girilen tutar toplam tutarı aşamaz. En fazla ₺${maxDeger.toLocaleString('tr-TR')} girilebilir.`));
                                    }
                                    const newRows = [...topluPlanlaRows];
                                    newRows[idx] = { ...newRows[idx], miktar: Math.min(val, maxDeger), kilitli: true };
                                    setTopluPlanlaRows(redistributeTopluRows(newRows, toplamTutar));
                                  }}
                                  style={{ width: 110, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', textAlign: 'right' as const }}
                                />
                              </div>
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input
                                  type="text"
                                  value={row.aciklama}
                                  onChange={e => {
                                    const newRows = [...topluPlanlaRows];
                                    newRows[idx] = { ...newRows[idx], aciklama: e.target.value };
                                    setTopluPlanlaRows(newRows);
                                  }}
                                  style={{ flex: 1, minWidth: 0, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none' }}
                                />
                                <AciklamaTurButonlari
                                  value={row.aciklama}
                                  onChange={deger => {
                                    const newRows = [...topluPlanlaRows];
                                    newRows[idx] = { ...newRows[idx], aciklama: deger };
                                    setTopluPlanlaRows(newRows);
                                  }}
                                />
                              </div>
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              <button
                                type="button"
                                title={t('sozlesmeDetay.satiriSil', 'Satırı sil')}
                                onClick={() => handleDeleteTopluRow(idx)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}
                              >
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="button"
                  onClick={handleAddTopluRow}
                  style={{ background: 'none', border: '1px dashed #d1d5db', color: '#6b7280', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center' }}
                >
                  <i className="fas fa-plus"></i> {t('sozlesmeDetay.satirEkle', 'Satır Ekle')}
                </button>
              </div>

              {/* Güncel planı temizle */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#b91c1c', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={topluPlanlaTemizleMevcut} onChange={e => setTopluPlanlaTemizleMevcut(e.target.checked)} style={{ width: 15, height: 15, accentColor: '#dc2626' }} />
                  {t('sozlesmeDetay.guncelPlaniTemizle', 'Güncel ödeme planını temizle')}
                </label>
                {topluPlanlaTemizleMevcut && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginLeft: 23 }}>
                    {t('sozlesmeDetay.guncelPlaniTemizleAciklama', 'Bekleyen (henüz tahsil edilmemiş) mevcut taksitler silinip yerine bu plan oluşturulacak.')}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{padding:'20px 28px',borderTop:'1px solid #f3f4f6',background:'rgba(249,250,251,0.5)',display:'flex',justifyContent:'flex-end',gap:12}}>
              <button
                onClick={() => setShowTopluPlanlaModal(false)}
                disabled={saving}
                style={{background:'#fff',border:'1px solid #d1d5db',color:'#374151',borderRadius:8,padding:'8px 16px',fontSize:14,fontWeight:500,cursor:'pointer',transition:'all 0.15s'}}
                onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background='#f9fafb'}
                onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background='#fff'}
              >
                {t('btn.iptal', 'İptal')}
              </button>
              <button
                onClick={handleSaveTopluPlanla}
                disabled={saving || topluPlanlaRows.length === 0}
                style={{background: topluPlanlaRows.length === 0 ? 'rgba(205,105,6,0.4)' : '#cd6906',color:'#fff',border:'none',borderRadius:8,padding:'8px 24px',fontSize:14,fontWeight:500,cursor: topluPlanlaRows.length === 0 ? 'not-allowed' : 'pointer',display:'flex',alignItems:'center',gap:7,boxShadow:'0 1px 3px rgba(205,105,6,0.3)',transition:'all 0.15s'}}
                onMouseEnter={e => { if (topluPlanlaRows.length === 0) return; (e.currentTarget as HTMLButtonElement).style.background='#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow='0 4px 12px rgba(205,105,6,0.4)'; }}
                onMouseLeave={e => { if (topluPlanlaRows.length === 0) return; (e.currentTarget as HTMLButtonElement).style.background='#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow='0 1px 3px rgba(205,105,6,0.3)'; }}
              >
                {saving ? <><span className="sd-spinner-sm"></span> {t('sozlesmeDetay.olusturuluyor', 'Oluşturuluyor...')}</> : <><CheckCircle2 size={15} /> {t('sozlesmeDetay.taksitleriOlustur', 'Taksitleri Oluştur')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sözleşme Bilgilerini Güncelle Modal */}
      {showUpdateModal && (
        <div
          style={{position:'fixed',inset:0,zIndex:1050,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.4)',backdropFilter:'blur(4px)'}}
          onClick={() => setShowUpdateModal(false)}
        >
          <div
            style={{background:'#fff',borderRadius:16,boxShadow:'0 20px 60px rgba(0,0,0,0.2)',width:'100%',maxWidth:820,margin:'0 16px',overflow:'hidden',maxHeight:'90vh',display:'flex',flexDirection:'column'}}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{padding:'24px 28px 16px',borderBottom:'1px solid #f3f4f6',background:'#fff',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexShrink:0}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                <div style={{width:36,height:36,borderRadius:10,background:'#fff7ed',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>
                  <FileEdit size={18} style={{color:'#cd6906'}} />
                </div>
                <div>
                  <div style={{fontSize:17,fontWeight:600,color:'#111827',lineHeight:1.3}}>{t('sozlesmeDetay.sozlesmeBilgileriniGuncelle', 'Sözleşme Bilgilerini Güncelle')}</div>
                  <div style={{fontSize:13,color:'#6b7280',marginTop:3}}>{t('sozlesmeDetay.guncelleAciklama', 'Mevcut sözleşmenin tarih ve finansal koşullarını güncelleyin.')}</div>
                </div>
              </div>
              <button
                onClick={() => setShowUpdateModal(false)}
                style={{flexShrink:0,background:'none',border:'none',cursor:'pointer',padding:'6px',borderRadius:6,color:'#9ca3af',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s'}}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#f3f4f6'; (e.currentTarget as HTMLButtonElement).style.color='#374151'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='none'; (e.currentTarget as HTMLButtonElement).style.color='#9ca3af'; }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div style={{padding:'28px',overflowY:'auto',display:'flex',flexDirection:'column',gap:24}}>
              {/* Row 1: Durum + Fiyat + Liste Fiyatı */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:20}}>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('sozlesmeDetay.sozlesmeDurumu', 'Sözleşme Durumu')} *</label>
                  <select
                    style={{width:'100%',padding:'10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',transition:'all 0.15s',appearance:'auto'}}
                    value={updateFormData.sozlesmeDurumu}
                    onChange={(e) => handleUpdateFormChange('sozlesmeDurumu', e.target.value)}
                    onFocus={(e) => { e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                    onBlur={(e) => { e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                  >
                    <option value="">{t('sozlesmeDetay.durumSecin', 'Durum seçin')}</option>
                    <option value="Rezerve">Rezerve</option>
                    <option value="Sözleşme">Sözleşme</option>
                    <option value="ArsaSahibi">Arsa Sahibi</option>
                    <option value="Taşeron">Taşeron</option>
                  </select>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('sozlesmeDetay.sozlesmeFiyati', 'Sözleşme Fiyatı')} *</label>
                  <div style={{position:'relative'}}>
                    <input
                      type="text"
                      style={{width:'100%',padding:'10px 36px 10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',transition:'all 0.15s'}}
                      placeholder="0"
                      value={updateFormData.sozlesmeFiyati ? formatCurrency(parseFloat(updateFormData.sozlesmeFiyati) || 0) : ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[₺\s.]/g, '').replace(/\D/g, '');
                        handleUpdateFormChange('sozlesmeFiyati', value);
                      }}
                      onFocus={(e) => { e.target.value = updateFormData.sozlesmeFiyati || ''; e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                      onBlur={(e) => { if (updateFormData.sozlesmeFiyati) e.target.value = formatCurrency(parseFloat(updateFormData.sozlesmeFiyati) || 0); e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                    />
                    <span style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'#9ca3af',pointerEvents:'none'}}>₺</span>
                  </div>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('sozlesmeDetay.listeFiyati', 'Liste Fiyatı (Satış Anındaki)')}</label>
                  <div style={{position:'relative'}}>
                    <input
                      type="text"
                      disabled
                      readOnly
                      title={t('sozlesmeDetay.listeFiyatiAciklama', 'Dairenin satış anındaki liste fiyatıdır, değiştirilemez')}
                      style={{width:'100%',padding:'10px 36px 10px 14px',border:'1px solid #e5e7eb',borderRadius:8,fontSize:14,background:'#f3f4f6',color:'#6b7280',outline:'none',boxSizing:'border-box',cursor:'not-allowed'}}
                      value={formatCurrency(sozlesme.listeFiyati || 0)}
                    />
                    <span style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'#9ca3af',pointerEvents:'none'}}>₺</span>
                  </div>
                </div>
              </div>

              {/* Row 2: Tarihler */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('sozlesmeDetay.sozlesmeTarihi', 'Sözleşme Tarihi')} *</label>
                  <input
                    type="text"
                    placeholder="gg.aa.yyyy"
                    maxLength={10}
                    style={{width:'100%',padding:'10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',transition:'all 0.15s'}}
                    value={updateSozlesmeTarihiDisplay}
                    onChange={(e) => {
                      const masked = maskTRDate(e.target.value);
                      setUpdateSozlesmeTarihiDisplay(masked);
                      const iso = fromTRDate(masked);
                      if (iso) handleUpdateFormChange('sozlesmeTarihi', iso);
                    }}
                    onFocus={(e) => { e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                    onBlur={(e) => { e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                  />
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>{t('sozlesmeDetay.rezerveBitisTarihi', 'Rezerve Bitiş Tarihi')}</label>
                  <input
                    type="text"
                    placeholder="gg.aa.yyyy"
                    maxLength={10}
                    style={{width:'100%',padding:'10px 14px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,background:'#fff',outline:'none',boxSizing:'border-box',transition:'all 0.15s'}}
                    value={updateRezerveTarihiDisplay}
                    onChange={(e) => {
                      const masked = maskTRDate(e.target.value);
                      setUpdateRezerveTarihiDisplay(masked);
                      const iso = fromTRDate(masked);
                      handleUpdateFormChange('rezerveBitisTarihi', iso || '');
                    }}
                    onFocus={(e) => { e.target.style.borderColor='#111827'; e.target.style.boxShadow='0 0 0 3px rgba(17,24,39,0.08)'; }}
                    onBlur={(e) => { e.target.style.borderColor='#d1d5db'; e.target.style.boxShadow='none'; }}
                  />
                </div>
              </div>

              {/* Row 3: Sözleşme Maddeleri */}
              <div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.06em'}}>{t('sozlesmeDetay.sozlesmeMaddeleri', 'Sözleşme Maddeleri')}</label>
                  <button type="button" className="sd-sablon-btn" onClick={() => setShowSablonModal(true)}>
                    <i className="fas fa-file-alt"></i> {t('sozlesmeDetay.sablondanGetir', 'Şablondan Getir')}
                  </button>
                </div>
                <div className="sd-quill-wrap">
                  <ReactQuill
                    theme="snow"
                    value={updateFormData.ozelSartlar}
                    onChange={(value) => handleUpdateFormChange('ozelSartlar', value)}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'indent': '-1' }, { 'indent': '+1' }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    formats={['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'link']}
                    placeholder={t('label.ara')}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{padding:'20px 28px',borderTop:'1px solid #f3f4f6',background:'rgba(249,250,251,0.5)',display:'flex',justifyContent:'flex-end',gap:12,flexShrink:0}}>
              <button
                onClick={() => setShowUpdateModal(false)}
                disabled={saving}
                style={{background:'#fff',border:'1px solid #d1d5db',color:'#374151',borderRadius:8,padding:'8px 16px',fontSize:14,fontWeight:500,cursor:'pointer',transition:'all 0.15s'}}
                onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background='#f9fafb'}
                onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background='#fff'}
              >
                {t('btn.iptal', 'İptal')}
              </button>
              <button
                onClick={handleSozlesmeUpdate}
                disabled={saving}
                style={{background:'#cd6906',color:'#fff',border:'none',borderRadius:8,padding:'8px 24px',fontSize:14,fontWeight:500,cursor:'pointer',display:'flex',alignItems:'center',gap:7,boxShadow:'0 1px 3px rgba(205,105,6,0.3)',transition:'all 0.15s'}}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow='0 4px 12px rgba(205,105,6,0.4)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow='0 1px 3px rgba(205,105,6,0.3)'; }}
              >
                {saving ? <><span className="sd-spinner-sm"></span> {t('msg.guncelleniyor', 'Güncelleniyor...')}</> : <><RefreshCw size={15} /> {t('btn.guncelle', 'Güncelle')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sözleşme Belgeleri Modal */}
      {showBelgelerModal && (
        <div className="sd-modal-overlay" onClick={() => setShowBelgelerModal(false)}>
          <div className="sd-modal sd-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="sd-modal-header">
              <div className="sd-modal-title"><i className="fas fa-file-alt"></i> {t('sozlesmeDetay.sozlesmeBelgeleri', 'Sözleşme Belgeleri')}</div>
              <button className="sd-modal-close" onClick={() => setShowBelgelerModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="sd-modal-body">
              {belgelerLoading ? (
                <div style={{textAlign:'center',padding:'32px 0'}}>
                  <span className="sd-spinner-sm" style={{width:'28px',height:'28px',borderWidth:'3px'}}></span>
                  <p style={{marginTop:'12px',color:'#6b7280'}}>{t('sozlesmeDetay.sablonYukleniyor', 'Şablon dosyalar yükleniyor...')}</p>
                </div>
              ) : sablonlar.length === 0 ? (
                <div className="sd-empty">
                  <div className="sd-empty-icon"><i className="fas fa-folder-open"></i></div>
                  <div className="sd-empty-text">{t('sozlesmeDetay.sablonYok', 'Bu proje için henüz şablon dosya eklenmemiş.')}</div>
                </div>
              ) : (
                <div>
                  <p style={{color:'#6b7280',marginBottom:'12px',fontSize:'13px'}}>
                    <strong>{t('label.proje', 'Proje')}:</strong> {sozlesme?.daire?.blok?.proje?.projeAdi}
                  </p>
                  <div className="sd-file-list">
                    {sablonlar.map((sablon) => {
                      const ext = sablon.dosyaAdi.toLowerCase().split('.').pop() || '';
                      const isPdf = ext === 'pdf';
                      const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
                      
                      return (
                        <div key={sablon.dosyaID} className="sd-file-item">
                          <span className="sd-file-name"><i className="fas fa-paperclip"></i> {sablon.aciklama || sablon.dosyaAdi}</span>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {(isPdf || isImage) && (
                              <button 
                                className="sd-file-dl" 
                                onClick={() => handlePreviewSablon(sablon)}
                                style={{ background: '#10b981', borderColor: '#10b981' }}
                              >
                                <i className="fas fa-eye"></i> {t('sozlesmeDetay.onizle', 'Önizle')}
                              </button>
                            )}
                            <button className="sd-file-dl" onClick={() => handleDownloadSablon(sablon)}><i className="fas fa-download"></i> {t('btn.indir', 'İndir')}</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="sd-modal-footer">
              <button className="sd-btn-cancel" onClick={() => setShowBelgelerModal(false)}>{t('btn.kapat')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Dosya Önizleme Modal */}
      {showPreviewModal && (
        <div className="sd-modal-overlay" onClick={handleClosePreview}>
          <div 
            className="sd-modal" 
            style={{ maxWidth: '90vw', width: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="sd-modal-header">
              <div className="sd-modal-title">
                <i className="fas fa-file-alt"></i> {previewFile?.name || 'Dosya Önizleme'}
              </div>
              <button className="sd-modal-close" onClick={handleClosePreview}><i className="fas fa-times"></i></button>
            </div>
            <div 
              className="sd-modal-body" 
              style={{ 
                padding: 0, 
                minHeight: '70vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8fafc'
              }}
            >
              {previewFile?.type === 'pdf' ? (
                <iframe
                  src={previewFile.url}
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none'
                  }}
                  title="PDF Önizleme"
                />
              ) : previewFile?.type === 'image' ? (
                <div style={{
                  width: '100%',
                  height: '70vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1rem'
                }}>
                  <img
                    src={previewFile.url}
                    alt="Önizleme"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              ) : null}
            </div>
            <div className="sd-modal-footer">
              <button className="sd-btn-cancel" onClick={handleClosePreview}>{t('btn.kapat')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Sözleşme İptal Modal */}
      {showIptalModal && (
        <div className="sd-modal-overlay" onClick={() => setShowIptalModal(false)}>
          <div className="sd-modal sd-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="sd-modal-header" style={{borderBottom:'2px solid #fee2e2'}}>
              <div className="sd-modal-title" style={{color:'#dc2626'}}><i className="fas fa-exclamation-triangle"></i> {t('sozlesmeDetay.sozlesmeIptalOnay', 'Sözleşme İptal Onayı')}</div>
              <button className="sd-modal-close" onClick={() => setShowIptalModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="sd-modal-body">
              <div className="sd-warn-box">
                <strong><i className="fas fa-exclamation-triangle"></i> {t('sozlesmeDetay.dikkat', 'DİKKAT!')}</strong>
                <p style={{margin:'6px 0 4px'}}>{t('sozlesmeDetay.iptalUyari', 'Bu işlem geri alınamaz. Sözleşmeyi iptal ettiğinizde:')}</p>
                <ul style={{margin:0,paddingLeft:'18px',fontSize:'13px'}}>
                  <li>{t('sozlesmeDetay.sozlesmePasif', 'Sözleşme pasif duruma geçecek')}</li>
                  <li>{t('sozlesmeDetay.daireSatisaAcik', 'Daire satışa açık duruma dönecek')}</li>
                  <li>{t('sozlesmeDetay.odemePlaniIptal', 'Tüm ödeme planı iptal olacak')}</li>
                </ul>
              </div>
              {sozlesme && (
                <div className="sd-iptal-card">
                  <div style={{fontWeight:600,marginBottom:'8px',color:'#374151'}}>{t('sozlesmeDetay.iptalEdilecekSozlesme', 'İptal Edilecek Sözleşme:')}</div>
                  <div className="sd-row"><span className="sd-label">{t('sozlesmeDetay.takipNo', 'Takip No')}</span><span className="sd-val">{sozlesme.sozlesmeTakipNo}</span></div>
                  <div className="sd-row"><span className="sd-label">{t('label.musteri')}</span><span className="sd-val">{sozlesme.cari?.adiSoyadi}</span></div>
                  <div className="sd-row"><span className="sd-label">{t('sozlesmeDetay.daireSm', 'Daire')}</span><span className="sd-val">{sozlesme.daire?.blok?.proje?.projeAdi} – {sozlesme.daire?.blok?.blokAdi} – {sozlesme.daire?.daireNo}</span></div>
                  <div className="sd-row"><span className="sd-label">{t('label.tutar', 'Tutar')}</span><span className="sd-val">{formatCurrency(sozlesme.sozlesmeFiyati)}</span></div>
                </div>
              )}
              <p style={{textAlign:'center',fontWeight:600,margin:'12px 0 0',fontSize:'14px'}}>{t('sozlesmeDetay.iptalEminMisiniz', 'Bu sözleşmeyi iptal etmek istediğinizden emin misiniz?')}</p>
            </div>
            <div className="sd-modal-footer sd-modal-footer-split">
              <button className="sd-btn-cancel" onClick={() => setShowIptalModal(false)} disabled={saving}>{t('btn.iptal')}</button>
              <button className="sd-btn-danger" onClick={handleSozlesmeIptal} disabled={saving}>
                {saving ? <><span className="sd-spinner-sm"></span> {t('sozlesmeDetay.iptalEdiliyor', 'İptal Ediliyor...')}</> : <><i className="fas fa-check"></i> {t('sozlesmeDetay.evetIptalEt', 'Evet, İptal Et')}</>}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Daire Detay Modal */}
      {showDaireDetayModal && sozlesme?.daire && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setShowDaireDetayModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }} />
          <div style={{ position: 'relative', width: '96%', maxWidth: 1060, height: '88vh', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff' }}>
              <div>
                {sozlesme.daire.blok?.proje?.projeAdi && (
                  <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, marginBottom: 2 }}>{sozlesme.daire.blok.proje.projeAdi}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>
                    {t('sozlesmeDetay.daireSm', 'Daire')} {sozlesme.daire.daireNo}
                  </h2>
                  {sozlesme.daire.daireTip?.tip && (
                    <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: '#f0fdf4', color: '#15803d', border: '1px solid #22c55e55' }}>
                      {sozlesme.daire.daireTip.tip}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: '#64748b' }}>{sozlesme.daire.takipNo}</span>
                </div>
              </div>
              <button onClick={() => setShowDaireDetayModal(false)}
                style={{ background: 'transparent', border: '1px solid #e2e8f0', cursor: 'pointer', padding: '5px 8px', color: '#6b7280', borderRadius: 8, display: 'flex', alignItems: 'center', fontSize: 16 }}>
                <i className="fas fa-times" />
              </button>
            </div>

            {/* Body: 3fr left (gallery) | 2fr right (details) */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '3fr 2fr', minHeight: 0, overflow: 'hidden' }}>

              {/* LEFT — Gallery */}
              <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #e2e8f0', background: '#0f172a' }}>
                {galeriGorseller.length > 0 ? (
                  <div style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <img
                      src={`${API_BASE_URL}/api/SunumBelgeleri/download/${galeriGorseller[galeriSlideIndex]?.sunumBelgeId}`}
                      alt={galeriGorseller[galeriSlideIndex]?.aciklama || ''}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                    {galeriGorseller[galeriSlideIndex]?.aciklama && (
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.65))', color: '#fff', padding: '28px 16px 12px', fontSize: 12.5, fontWeight: 600 }}>
                        {galeriGorseller[galeriSlideIndex].aciklama}
                      </div>
                    )}
                    {galeriGorseller.length > 1 && (
                      <>
                        <button onClick={e => { e.stopPropagation(); setGaleriSlideIndex(i => i <= 0 ? galeriGorseller.length - 1 : i - 1); }}
                          style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', fontSize: 18, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>‹</button>
                        <button onClick={e => { e.stopPropagation(); setGaleriSlideIndex(i => i >= galeriGorseller.length - 1 ? 0 : i + 1); }}
                          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', fontSize: 18, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>›</button>
                        <div style={{ position: 'absolute', bottom: galeriGorseller[galeriSlideIndex]?.aciklama ? 36 : 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5 }}>
                          {galeriGorseller.map((_, i) => (
                            <span key={i} onClick={e => { e.stopPropagation(); setGaleriSlideIndex(i); }}
                              style={{ width: 7, height: 7, borderRadius: '50%', background: i === galeriSlideIndex ? '#fff' : 'rgba(255,255,255,0.45)', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.7)', transition: 'background 0.15s' }} />
                          ))}
                        </div>
                        <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 20 }}>
                          {galeriSlideIndex + 1} / {galeriGorseller.length}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', gap: 10 }}>
                    <i className="fas fa-image" style={{ fontSize: 48, opacity: 0.3 }} />
                    <span style={{ fontSize: 13, opacity: 0.5 }}>{t('sozlesmeDetay.gorselYok', 'Görsel yok')}</span>
                  </div>
                )}
                {/* Thumbnail strip */}
                {galeriGorseller.length > 1 && (
                  <div style={{ display: 'flex', gap: 4, padding: '8px 10px', background: '#1e293b', overflowX: 'auto', flexShrink: 0 }}>
                    {galeriGorseller.map((g, i) => (
                      <img key={g.sunumBelgeId}
                        src={`${API_BASE_URL}/api/SunumBelgeleri/download/${g.sunumBelgeId}`}
                        alt=""
                        onClick={e => { e.stopPropagation(); setGaleriSlideIndex(i); }}
                        style={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 5, cursor: 'pointer', flexShrink: 0, border: i === galeriSlideIndex ? '2px solid #fff' : '2px solid transparent', opacity: i === galeriSlideIndex ? 1 : 0.55, transition: 'all 0.15s' }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* RIGHT — Details */}
              <div style={{ overflowY: 'auto', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>

                {/* Temel bilgiler */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>{t('sozlesmeDetay.daireBilgileri', 'Daire Bilgileri')}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {[
                      { label: t('label.proje', 'Proje'), value: sozlesme.daire.blok?.proje?.projeAdi },
                      { label: t('sozlesmeDetay.blok', 'Blok'), value: sozlesme.daire.blok?.blokAdi },
                      { label: t('sozlesmeDetay.daireNo', 'Daire No'), value: sozlesme.daire.daireNo },
                      { label: t('sozlesmeDetay.kat', 'Kat'), value: sozlesme.daire.kat?.katAdi },
                      { label: t('sozlesmeDetay.cephe', 'Cephe'), value: sozlesme.daire.cephe?.cepheAdi },
                      { label: t('sozlesmeDetay.manzara', 'Manzara'), value: sozlesme.daire.manzara?.manzaraAdi },
                      { label: t('sozlesmeDetay.netAlan', 'Net Alan'), value: sozlesme.daire.netM2 ? `${sozlesme.daire.netM2} m²` : undefined },
                      { label: t('sozlesmeDetay.brutAlan', 'Brüt Alan'), value: sozlesme.daire.brutM2 ? `${sozlesme.daire.brutM2} m²` : undefined },
                      { label: t('label.tip', 'Tip'), value: sozlesme.daire.daireTip?.tip },
                      { label: t('sozlesmeDetay.listeFiyati', 'Liste Fiyatı'), value: sozlesme.daire.listeFiyati ? formatCurrency(sozlesme.daire.listeFiyati) : undefined },
                    ].filter(r => r.value).map(r => (
                      <div key={r.label} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, minWidth: 80, flexShrink: 0 }}>{r.label}</span>
                        <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{r.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Daire Özellikleri */}
                {daireOzellikler.length > 0 && (
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>{t('sozlesmeDetay.daireOzellikleri', 'Daire Özellikleri')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {daireOzellikler.map((o, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, minWidth: 80, flexShrink: 0 }}>{o.ozellik}</span>
                          <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{o.deger}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Proje Özellikleri */}
                {projeOzellikler.length > 0 && (
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>{t('sozlesmeDetay.projeOzellikleri', 'Proje Özellikleri')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {projeOzellikler.map((o, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, minWidth: 80, flexShrink: 0 }}>{o.ozellik}</span>
                          <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{o.deger}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Belgeler */}
                {galeriBelgeler.length > 0 && (
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}><i className="fas fa-file-alt" style={{ marginRight: 5 }} />{t('sozlesmeDetay.belgeler', 'Belgeler')}</div>
                    {galeriBelgeler.map(b => (
                      <div key={b.sunumBelgeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
                        <i className="fas fa-file-alt" style={{ color: '#3b82f6', fontSize: 13 }} />
                        <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{b.aciklama || b.sunumBelgeAdi || 'Belge'}</span>
                        <a href={`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>
                          <i className="fas fa-download" style={{ marginRight: 3 }} />{t('btn.indir', 'İndir')}
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {/* Videolar */}
                {galeriVideolar.length > 0 && (
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}><i className="fas fa-film" style={{ marginRight: 5 }} />{t('sozlesmeDetay.videolar', 'Videolar')}</div>
                    {galeriVideolar.map(v => (
                      <div key={v.sunumBelgeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                        onClick={() => setShowVideoModal(v)}>
                        <i className="fas fa-play-circle" style={{ color: '#dc2626', fontSize: 15 }} />
                        <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{v.aciklama || 'Video'}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sozlesmeDetay.oynat', 'Oynat')} →</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Açıklama */}
                {sozlesme.daire.aciklama && (
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{t('label.aciklama')}</div>
                    <p style={{ color: '#475569', fontSize: 13, margin: 0, lineHeight: 1.6 }}>{sozlesme.daire.aciklama}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="sd-modal-overlay" onClick={() => setShowVideoModal(null)} style={{ zIndex: 1060 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#000', borderRadius: 12, maxWidth: 720, width: '90%', overflow: 'hidden', position: 'relative' }}>
            <button onClick={() => setShowVideoModal(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>×</button>
            {showVideoModal.sunumBelgeAdi && (
              <div style={{ width: '100%' }}
                dangerouslySetInnerHTML={{ __html: showVideoModal.sunumBelgeAdi }} />
            )}
            {showVideoModal.aciklama && (
              <div style={{ padding: '8px 14px', color: '#fff', fontSize: 13, fontWeight: 600 }}>{showVideoModal.aciklama}</div>
            )}
          </div>
        </div>
      )}

      {/* Şablon Seç Modal */}
      {showSablonModal && (
        <div className="sd-modal-overlay" onClick={() => setShowSablonModal(false)} style={{ zIndex: 1070 }}>
          <div className="sd-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
            <div className="sd-modal-header">
              <div className="sd-modal-title">
                <i className="fas fa-file-alt" style={{ color: '#3b82f6', marginRight: 8 }}></i>
                {t('sozlesmeDetay.dokumanSablonuSec', 'Döküman Şablonu Seç')}
              </div>
              <button className="sd-modal-close" onClick={() => setShowSablonModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="sd-modal-body" style={{ padding: 0 }}>
              {dokumanSablonlari.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                  <i className="fas fa-inbox" style={{ fontSize: 40, marginBottom: 14, opacity: 0.4 }}></i>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>{t('sozlesmeDetay.sablonEklenmemis', 'Henüz şablon eklenmemiş')}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>{t('sozlesmeDetay.sablonEkleTanim', 'Tanımlamalar › Döküman Şablonları menüsünden şablon ekleyebilirsiniz.')}</p>
                </div>
              ) : (
                <div className="sd-sablon-list">
                  {dokumanSablonlari.map((sablon: any) => (
                    <div key={sablon.dokumanSablonID} className="sd-sablon-item" onClick={() => handleSablonSec(sablon)}>
                      <div className="sd-sablon-title">
                        <i className="fas fa-file-alt" style={{ color: '#3b82f6', fontSize: 12, marginRight: 6 }}></i>
                        {sablon.sablonBasligi}
                      </div>
                      {sablon.icerik && (
                        <div className="sd-sablon-preview"
                          dangerouslySetInnerHTML={{ __html: sablon.icerik.replace(/<[^>]*>/g, '').substring(0, 100) + '...' }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="sd-modal-footer">
              <button className="sd-btn-cancel" onClick={() => setShowSablonModal(false)}>{t('btn.kapat')}</button>
            </div>
          </div>
        </div>
      )}
      {/* Taksit Detay / Düzenleme Modal */}
      {showTaksitDetayModal && selectedTaksitForDetay && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowTaksitDetayModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-edit" style={{ color: '#1d4ed8', fontSize: 16 }}></i>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{t('sozlesmeDetay.taksitDetayi', 'Taksit Detayı')}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>#{selectedTaksitForDetay.siraNo} · {selectedTaksitForDetay.odemeTuruDetay || selectedTaksitForDetay.odemeTuru || 'Taksit'}</div>
                </div>
              </div>
              <button onClick={() => setShowTaksitDetayModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#64748b', fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Miktar - binlik ayraçlı */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('sozlesmeDetay.miktarTL', 'Miktar (₺)')} *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={taksitDetayForm.miktarDisplay}
                  onChange={e => {
                    const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                    if (raw === '' || /^\d*\.?\d{0,2}$/.test(raw)) {
                      const num = parseFloat(raw);
                      setTaksitDetayForm(f => ({
                        ...f,
                        miktar: raw,
                        miktarDisplay: raw === '' ? '' : isNaN(num) ? raw : num.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      }));
                    }
                  }}
                  onBlur={() => {
                    const num = parseFloat(taksitDetayForm.miktar);
                    if (!isNaN(num)) {
                      setTaksitDetayForm(f => ({ ...f, miktarDisplay: num.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }));
                    }
                  }}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                  placeholder="0"
                />
              </div>
              {/* Vade Tarihi */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('sozlesmeDetay.vadeTarihi', 'Vade Tarihi')} *</label>
                <TrDateInput
                  value={taksitDetayForm.vadeTarihi}
                  onChange={(e) => setTaksitDetayForm(f => ({ ...f, vadeTarihi: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                />
              </div>
              {/* Ödeme Durumu */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('sozlesmeDetay.odemeDurumu', 'Ödeme Durumu')}</label>
                <select
                  value={taksitDetayForm.odemeDurumu}
                  onChange={e => setTaksitDetayForm(f => ({ ...f, odemeDurumu: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, background: '#fff' }}
                >
                  <option value="Bekliyor">{t('durum.bekliyor', 'Bekliyor')}</option>
                  <option value="Odendi">{t('durum.odendi', 'Ödendi')}</option>
                  <option value="Iptal">{t('durum.iptal')}</option>
                </select>
              </div>
              {/* Ödendi seçilince: tahsilat tarihi + ödeme türü + kasa */}
              {taksitDetayForm.odemeDurumu === 'Odendi' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('sozlesmeDetay.tahsilatTarihi', 'Tahsilat Tarihi')} <span style={{ color: '#f43f5e' }}>*</span></label>
                      <TrDateInput
                        value={taksitDetayForm.tahsilatTarihi}
                        onChange={e => setTaksitDetayForm(f => ({ ...f, tahsilatTarihi: e.target.value }))}
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('label.odemeTuru', 'Ödeme Türü')}</label>
                      <select
                        value={taksitDetayForm.odemeTuru}
                        onChange={e => setTaksitDetayForm(f => ({ ...f, odemeTuru: e.target.value }))}
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}
                      >
                        <option value="Nakit">{t('odeme.nakit')}</option>
                        <option value="Senet">{t('odeme.senet')}</option>
                        <option value="Cek">{t('odeme.cek')}</option>
                        <option value="Takas">{t('odeme.takas', 'Takas')}</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('label.kasa', 'Kasa')} <span style={{ color: '#f43f5e' }}>*</span></label>
                    <select
                      value={taksitDetayForm.kasaID}
                      onChange={e => setTaksitDetayForm(f => ({ ...f, kasaID: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}
                    >
                      <option value="">— {t('sozlesmeDetay.kasaSecin', 'Kasa seçin')} —</option>
                      {kasalar.map(k => <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi} ({k.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>)}
                    </select>
                  </div>
                </>
              )}
              {/* Açıklama */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  <span>{t('label.aciklama')}</span>
                  <AciklamaTurButonlari value={taksitDetayForm.aciklama} onChange={deger => setTaksitDetayForm(f => ({ ...f, aciklama: deger }))} />
                </label>
                <input
                  type="text"
                  value={taksitDetayForm.aciklama}
                  onChange={e => setTaksitDetayForm(f => ({ ...f, aciklama: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                  placeholder={t('label.aciklama')}
                />
              </div>
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowTaksitDetayModal(false)} style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                {t('btn.iptal', 'Vazgeç')}
              </button>
              <button
                onClick={handleTaksitDetaySave}
                disabled={taksitDetaySaving}
                style={{ padding: '9px 20px', background: taksitDetayForm.odemeDurumu === 'Odendi' ? '#16a34a' : '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: taksitDetaySaving ? 'not-allowed' : 'pointer', opacity: taksitDetaySaving ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {taksitDetaySaving
                  ? <><i className="fas fa-spinner fa-spin"></i> {t('msg.kaydediliyor', 'Kaydediliyor...')}</>
                  : taksitDetayForm.odemeDurumu === 'Odendi'
                    ? <><i className="fas fa-check"></i> {t('sozlesmeDetay.tahsilatıKaydet', 'Tahsilatı Kaydet')}</>
                    : <><i className="fas fa-save"></i> {t('btn.kaydet', 'Kaydet')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tahsilat Yap Modal */}
      {showTahsilatYapModal && tahsilatYapTaksit && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowTahsilatYapModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-check" style={{ color: '#16a34a', fontSize: 14 }}></i>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{t('btn.tahsilatYap')}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    <strong>{tahsilatYapTaksit.aciklama}</strong> — {formatCurrency(tahsilatYapTaksit.miktar)}
                  </div>
                </div>
              </div>
              <button onClick={() => setShowTahsilatYapModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#9ca3af' }}>
                <i className="fas fa-times" style={{ fontSize: 16 }}></i>
              </button>
            </div>
            <form onSubmit={e => { e.preventDefault(); handleSaveTahsilatYap(); }}>
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('sozlesmeDetay.tahsilatTarihi', 'Tahsilat Tarihi')} <span style={{ color: '#f43f5e' }}>*</span></label>
                    <TrDateInput
                      required
                      value={tahsilatYapFormData.tarih}
                      onChange={e => setTahsilatYapFormData(f => ({ ...f, tarih: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('label.odemeTuru', 'Ödeme Türü')}</label>
                    <select
                      value={tahsilatYapFormData.odemeTuru}
                      onChange={e => setTahsilatYapFormData(f => ({ ...f, odemeTuru: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}
                    >
                      <option value="Nakit">{t('odeme.nakit')}</option>
                      <option value="Senet">{t('odeme.senet')}</option>
                      <option value="Cek">{t('odeme.cek')}</option>
                      <option value="Takas">{t('odeme.takas', 'Takas')}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('label.kasa', 'Kasa')} <span style={{ color: '#f43f5e' }}>*</span></label>
                  <select
                    required
                    value={tahsilatYapFormData.kasaID}
                    onChange={e => setTahsilatYapFormData(f => ({ ...f, kasaID: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}
                  >
                    <option value="">— {t('sozlesmeDetay.kasaSecin', 'Kasa seçin')} —</option>
                    {kasalar.map(k => <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi} ({k.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                    <span>{t('label.aciklama')}</span>
                    <AciklamaTurButonlari value={tahsilatYapFormData.aciklama} onChange={deger => setTahsilatYapFormData(f => ({ ...f, aciklama: deger }))} />
                  </label>
                  <textarea
                    rows={3}
                    value={tahsilatYapFormData.aciklama}
                    onChange={e => setTahsilatYapFormData(f => ({ ...f, aciklama: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                  />
                </div>
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => setShowTahsilatYapModal(false)}
                  style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  {t('btn.iptal', 'İptal')}
                </button>
                <button type="submit" disabled={saving}
                  style={{ padding: '9px 22px', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#16a34a', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <i className="fas fa-check" style={{ fontSize: 13 }}></i>
                  {saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('sozlesmeDetay.tahsilatıKaydet', 'Tahsilatı Kaydet')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Hazır Ödeme Planı Modal ─────────────────────────────────────────── */}
      {showHazirPlanModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 860, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>
            {/* Header */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: '#fef3e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-file-invoice-dollar" style={{ color: 'rgb(205,105,6)', fontSize: 15 }}></i>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{t('sozlesmeDetay.hazirOdemePlani', 'Hazır Ödeme Planı')}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{t('sozlesmeDetay.hazirPlanAciklama', 'Şablon seçerek otomatik ödeme takvimi oluşturun')}</div>
                </div>
              </div>
              <button onClick={() => setShowHazirPlanModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {/* Row 1: Miktar + Ödeme Türü */}
              <div style={{ padding: '18px 24px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                      {t('sozlesmeDetay.planlananMiktar', 'Planlanacak Miktar (₺)')}
                    </label>
                    <button
                      type="button"
                      onClick={() => handleHazirPlanMiktarChange(String(Math.max(0, Math.round(calculatePlanlanmamisTutar()))))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cd6906', fontSize: 11, fontWeight: 600, padding: 0 }}
                    >
                      {t('sozlesmeDetay.kalanTutariGetir', 'Kalan Tutarı Getir')}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={hazirPlanMiktar === '' ? '' : Number(hazirPlanMiktar).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                    onChange={e => {
                      const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                      if (raw === '' || /^\d*$/.test(raw)) handleHazirPlanMiktarChange(raw);
                    }}
                    placeholder="Tutar giriniz"
                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    {t('label.odemeTuru', 'Ödeme Türü')}
                  </label>
                  <select
                    value={hazirPlanOdemeTuru}
                    onChange={e => setHazirPlanOdemeTuru(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}
                  >
                    {['Nakit', 'BankaHavale', 'Cek', 'Senet', 'Takas', 'Mahsup'].map(item => <option key={item} value={item}>{item === 'Cek' ? 'Çek' : item === 'BankaHavale' ? 'Banka Havale/EFT' : item === 'Mahsup' ? 'Mahsup (Daire Karşılığı)' : item}</option>)}
                  </select>
                </div>
              </div>

              {/* Şablonlar listesi */}
              <div style={{ padding: '16px 24px 0' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>{t('sozlesmeDetay.odemeSablonuSecin', 'Ödeme Şablonu Seçin')}</div>
                {hazirPlanLoading ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>{t('sozlesmeDetay.sablonlarYukleniyor', 'Şablonlar yükleniyor...')}
                  </div>
                ) : hazirPlanSablonlar.length === 0 ? (
                  <div style={{ padding: '16px', background: '#fef9c3', borderRadius: 8, fontSize: 13, color: '#854d0e', border: '1px solid #fde68a' }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>{t('sozlesmeDetay.aktifSablonYok', 'Aktif ödeme planı şablonu bulunamadı. Önce Ayarlar > Ödeme Planı Şablonları bölümünden şablon oluşturun.')}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                    {hazirPlanSablonlar.map(s => {
                      const isSelected = selectedHazirSablon?.sablonId === s.sablonId;
                      return (
                        <div key={s.sablonId} onClick={() => handleHazirSablonSelect(s)} style={{ border: `2px solid ${isSelected ? 'rgb(205,105,6)' : '#e2e8f0'}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', background: isSelected ? '#fff7ed' : '#fff', transition: 'all 0.15s' }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: isSelected ? 'rgb(205,105,6)' : '#1e293b', marginBottom: 6 }}>{s.sablonAdi}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, background: '#fef3e2', color: 'rgb(205,105,6)' }}>
                              {t('sozlesmeDetay.pesinat', 'Peşinat')}: {s.pesinatTipi === 'Oran' ? `%${s.pesinatDegeri}` : `₺${s.pesinatDegeri.toLocaleString('tr-TR')}`}
                            </span>
                            <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, background: '#e0f2fe', color: '#0369a1' }}>
                              {s.vadeTipi === 'EsitTaksit' ? `${s.maksimumTaksitSayisi} ${t('sozlesmeDetay.esitTaksit', 'Eşit Taksit')}` : s.vadeTipi === 'AraOdemeli' ? t('sozlesmeDetay.araOdemeli', 'Ara Ödemeli') : t('sozlesmeDetay.serbest', 'Serbest')}
                            </span>
                            {s.detaylar?.length > 0 && (
                              <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, background: '#f0fdf4', color: '#166534' }}>{s.detaylar.length} {t('sozlesmeDetay.kalem', 'kalem')}</span>
                            )}
                          </div>
                          {s.aciklama && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>{s.aciklama}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Generated rows table */}
              {hazirPlanRows.length > 0 && (
                <div style={{ padding: '16px 24px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t('sozlesmeDetay.odemeTakvimi', 'Ödeme Takvimi')} ({hazirPlanRows.length} {t('sozlesmeDetay.kalem', 'kalem')})</div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                      <span style={{ padding: '4px 12px', background: '#f0f9ff', borderRadius: 20, color: '#0369a1', fontWeight: 500 }}>
                        <i className="fas fa-calendar-alt" style={{ marginRight: 5 }}></i>{t('sozlesmeDetay.ortVade', 'Ort. Vade')}: {calcHazirPlanAvgVade()} {t('sozlesmeDetay.gun', 'gün')}
                      </span>
                      <span style={{ padding: '4px 12px', background: '#f0fdf4', borderRadius: 20, color: '#166534', fontWeight: 500 }}>
                        {t('sozlesmeDetay.toplamHP', 'Toplam')}: ₺{hazirPlanRows.reduce((s, r) => s + r.miktar, 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', width: 40 }}>#</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('label.tur')}</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('sozlesmeDetay.vadeTarihi', 'Vade Tarihi')}</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('sozlesmeDetay.miktarTL', 'Miktar (₺)')}</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{t('label.aciklama')}</th>
                          <th style={{ padding: '10px 12px', width: 34, borderBottom: '1px solid #e2e8f0' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {hazirPlanRows.map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: idx < hazirPlanRows.length - 1 ? '1px solid #f1f5f9' : 'none', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '8px 12px', color: '#94a3b8', fontWeight: 500 }}>{idx + 1}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: row.odemeTuru === 'Pesinat' ? '#fef3c7' : row.odemeTuru === 'Taksit' ? '#e0f2fe' : '#f0fdf4', color: row.odemeTuru === 'Pesinat' ? '#92400e' : row.odemeTuru === 'Taksit' ? '#0369a1' : '#166534', fontWeight: 600 }}>
                                {HAZIR_PLAN_ODEME_TURU_LABEL[row.odemeTuru] || row.odemeTuru}
                              </span>
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <TrDateInput
                                value={(() => { const [d, m, y] = row.vadeTarihiStr.split('.'); return `${y}-${m}-${d}`; })()}
                                onChange={e => {
                                  const [y, m, d] = e.target.value.split('-');
                                  const newRows = [...hazirPlanRows];
                                  newRows[idx] = { ...newRows[idx], vadeTarihiStr: `${d}.${m}.${y}` };
                                  setHazirPlanRows(newRows);
                                }}
                                style={{ width: 140, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none' }}
                              />
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                <button
                                  type="button"
                                  title={row.kilitli ? t('sozlesmeDetay.kilitliAciklama', 'Bu tutar kilitli, sistem otomatik dağıtımda değiştirmez') : t('sozlesmeDetay.kilitsizAciklama', 'Bu tutar sistem tarafından otomatik dağıtılır')}
                                  onClick={() => {
                                    const newRows = [...hazirPlanRows];
                                    newRows[idx] = { ...newRows[idx], kilitli: !newRows[idx].kilitli };
                                    const toplam = parseFloat(hazirPlanMiktar) || 0;
                                    setHazirPlanRows(redistributeHazirRows(newRows, toplam));
                                  }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: row.kilitli ? '#cd6906' : '#cbd5e1' }}
                                >
                                  {row.kilitli ? <Lock size={14} /> : <Unlock size={14} />}
                                </button>
                                <input
                                  type="text"
                                  value={row.miktar.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                                  onChange={e => {
                                    const raw = e.target.value.replace(/\./g, '');
                                    const val = parseInt(raw, 10) || 0;
                                    const toplam = parseFloat(hazirPlanMiktar) || 0;
                                    const digerKilitliToplam = hazirPlanRows.reduce((s, r, i) => i !== idx && r.kilitli ? s + r.miktar : s, 0);
                                    const maxDeger = Math.max(0, toplam - digerKilitliToplam);
                                    if (val > maxDeger) {
                                      toast.error(t('sozlesmeDetay.tutarAsimUyarisi', `Girilen tutar toplam tutarı aşamaz. En fazla ₺${maxDeger.toLocaleString('tr-TR')} girilebilir.`));
                                    }
                                    const newRows = [...hazirPlanRows];
                                    newRows[idx] = { ...newRows[idx], miktar: Math.min(val, maxDeger), kilitli: true };
                                    setHazirPlanRows(redistributeHazirRows(newRows, toplam));
                                  }}
                                  style={{ width: 120, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', textAlign: 'right' as const }}
                                />
                              </div>
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input
                                  type="text"
                                  value={row.aciklama}
                                  onChange={e => {
                                    const newRows = [...hazirPlanRows];
                                    newRows[idx] = { ...newRows[idx], aciklama: e.target.value };
                                    setHazirPlanRows(newRows);
                                  }}
                                  style={{ flex: 1, minWidth: 0, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none' }}
                                />
                                <AciklamaTurButonlari
                                  value={row.aciklama}
                                  onChange={deger => {
                                    const newRows = [...hazirPlanRows];
                                    newRows[idx] = { ...newRows[idx], aciklama: deger };
                                    setHazirPlanRows(newRows);
                                  }}
                                />
                              </div>
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              <button
                                type="button"
                                title={t('sozlesmeDetay.satiriSil', 'Satırı sil')}
                                onClick={() => handleDeleteHazirRow(idx)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}
                              >
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div style={{ padding: '4px 24px 0' }}>
                <button
                  type="button"
                  onClick={handleAddHazirRow}
                  style={{ background: 'none', border: '1px dashed #d1d5db', color: '#6b7280', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center' }}
                >
                  <i className="fas fa-plus"></i> {t('sozlesmeDetay.satirEkle', 'Satır Ekle')}
                </button>
              </div>
              <div style={{ padding: '4px 24px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#b91c1c', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={hazirPlanTemizleMevcut} onChange={e => setHazirPlanTemizleMevcut(e.target.checked)} style={{ width: 15, height: 15, accentColor: '#dc2626' }} />
                  {t('sozlesmeDetay.guncelPlaniTemizle', 'Güncel ödeme planını temizle')}
                </label>
                {hazirPlanTemizleMevcut && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginLeft: 23 }}>
                    {t('sozlesmeDetay.guncelPlaniTemizleAciklama', 'Bekleyen (henüz tahsil edilmemiş) mevcut taksitler silinip yerine bu plan oluşturulacak.')}
                  </div>
                )}
              </div>
              <div style={{ height: 20 }} />
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                {hazirPlanRows.length > 0 && `${hazirPlanRows.length} ${t('sozlesmeDetay.odeme', 'ödeme')} • ${t('sozlesmeDetay.bekliyorKaydedilecek', 'Bekliyor durumunda kaydedilecek')}`}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowHazirPlanModal(false)} style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  {t('btn.iptal', 'İptal')}
                </button>
                <button
                  onClick={handleHazirPlanSave}
                  disabled={hazirPlanSaving || hazirPlanRows.length === 0}
                  style={{ padding: '9px 22px', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: hazirPlanRows.length === 0 ? 'not-allowed' : 'pointer', background: hazirPlanRows.length === 0 ? 'rgba(205,105,6,0.4)' : 'rgb(205,105,6)', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <i className="fas fa-check" style={{ fontSize: 13 }}></i>
                  {hazirPlanSaving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('sozlesmeDetay.odemeleriOlustur', 'Ödemeleri Oluştur')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showImalatNotlariModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowImalatNotlariModal(false); setImalatNotForm(null); setImalatNotEditID(null); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: 700, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px 14px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontWeight: 700, fontSize: 17, color: '#1e293b' }}><i className="fas fa-tools" style={{ marginRight: 8, color: '#d97706' }}></i>{t('sozlesmeDetay.imalatNotları', 'İmalat Notları')}</span>
              <button onClick={() => { setShowImalatNotlariModal(false); setImalatNotForm(null); setImalatNotEditID(null); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {imalatNotlariLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{t('msg.yukleniyor')}</div>
              ) : imalatKategorilerSD.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>{t('sozlesmeDetay.imalatKatYok', 'Bu proje için henüz imalat kategorisi tanımlanmamış.')}<br />{t('sozlesmeDetay.imalatKatEkle', 'Proje Detayı → İmalat Kategorileri bölümünden ekleyebilirsiniz.')}</div>
              ) : (
                <>
                  {imalatKategorilerSD.map(kat => {
                    const notlar = imalatNotlariSD.filter(n => n.imalatKatID === kat.imalatKatID);
                    return (
                      <div key={kat.imalatKatID} style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                            {kat.kategoriAdi}
                            <span style={{ marginLeft: 8, fontSize: 11, background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '2px 7px' }}>{kat.alanTipi}</span>
                          </div>
                          <button onClick={() => { setImalatNotEditID(null); setImalatNotForm({ katID: kat.imalatKatID, deger: '', secimler: [], aciklama: '' }); }} style={{ border: 'none', background: '#3b82f6', color: '#fff', borderRadius: 8, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>
                            + {t('sozlesmeDetay.notEkle', 'Not Ekle')}
                          </button>
                        </div>
                        {notlar.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', paddingLeft: 4 }}>{t('sozlesmeDetay.notYok', 'Henüz not eklenmemiş.')}</div>}
                        {notlar.map(not => {
                          let degerGoster = not.deger;
                          if (['TekSecim','CokSecim'].includes(kat.alanTipi)) {
                            try { degerGoster = JSON.parse(not.deger).join(', '); } catch { degerGoster = not.deger; }
                          }
                          return (
                            <div key={not.imalatNotID} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, border: '1px solid #f1f5f9', background: '#fafafa', marginBottom: 6 }}>
                              <div>
                                <div style={{ fontWeight: 500, fontSize: 13, color: '#374151' }}>{degerGoster}</div>
                                {not.aciklama && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{not.aciklama}</div>}
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{not.ekleyenAdi || ''} · {new Date(not.eklenmeTarihi).toLocaleDateString('tr-TR')}</div>
                              </div>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={() => {
                                  let secimler: string[] = [];
                                  let deger = not.deger;
                                  if (['TekSecim','CokSecim'].includes(kat.alanTipi)) { try { secimler = JSON.parse(not.deger); deger = ''; } catch {} }
                                  setImalatNotEditID(not.imalatNotID);
                                  setImalatNotForm({ katID: kat.imalatKatID, deger, secimler, aciklama: not.aciklama || '' });
                                }} style={{ border: 'none', background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>{t('btn.duzenle')}</button>
                                <button onClick={() => handleImalatNotDelete(not.imalatNotID)} style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>{t('btn.sil')}</button>
                              </div>
                            </div>
                          );
                        })}
                        {imalatNotForm && imalatNotForm.katID === kat.imalatKatID && (
                          <div style={{ border: '1px solid #bfdbfe', borderRadius: 10, padding: 14, background: '#f0f7ff', marginTop: 8 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#1e293b' }}>{imalatNotEditID ? t('sozlesmeDetay.notuDuzenle', 'Notu Düzenle') : t('sozlesmeDetay.yeniNot', 'Yeni Not')}</div>
                            {['TekSecim','CokSecim'].includes(kat.alanTipi) && kat.secenekler ? (
                              <div style={{ marginBottom: 10 }}>
                                <label style={{ fontSize: 12, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>{t('sozlesmeDetay.secim', 'Seçim')} {kat.alanTipi === 'TekSecim' ? `(${t('sozlesmeDetay.tek', 'Tek')})` : `(${t('sozlesmeDetay.coklu', 'Çoklu')})`}</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 6 }}>
                                  {JSON.parse(kat.secenekler).map((s: string) => {
                                    const selected = imalatNotForm.secimler.includes(s);
                                    return (
                                      <button key={s} onClick={() => {
                                        if (kat.alanTipi === 'TekSecim') {
                                          setImalatNotForm({ ...imalatNotForm, secimler: selected ? [] : [s] });
                                        } else {
                                          setImalatNotForm({ ...imalatNotForm, secimler: selected ? imalatNotForm.secimler.filter(x => x !== s) : [...imalatNotForm.secimler, s] });
                                        }
                                      }} style={{ border: `1.5px solid ${selected ? '#3b82f6' : '#e2e8f0'}`, background: selected ? '#eff6ff' : '#fff', color: selected ? '#1d4ed8' : '#374151', borderRadius: 20, padding: '4px 14px', cursor: 'pointer', fontSize: 13, fontWeight: selected ? 600 : 400 }}>
                                        {s}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <div style={{ marginBottom: 10 }}>
                                <label style={{ fontSize: 12, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 4 }}>{t('sozlesmeDetay.deger', 'Değer')}</label>
                                <input value={imalatNotForm.deger} onChange={e => setImalatNotForm({ ...imalatNotForm, deger: e.target.value })} type={kat.alanTipi === 'Sayi' ? 'number' : kat.alanTipi === 'Tarih' ? 'date' : 'text'} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 14, boxSizing: 'border-box' as const }} />
                              </div>
                            )}
                            <div style={{ marginBottom: 12 }}>
                              <label style={{ fontSize: 12, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 4 }}>{t('label.aciklama')}</label>
                              <input value={imalatNotForm.aciklama} onChange={e => setImalatNotForm({ ...imalatNotForm, aciklama: e.target.value })} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 14, boxSizing: 'border-box' as const }} placeholder={t('label.aciklama')} />
                            </div>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                              <button onClick={() => { setImalatNotForm(null); setImalatNotEditID(null); }} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 13 }}>{t('btn.iptal')}</button>
                              <button onClick={handleImalatNotKaydet} style={{ border: 'none', background: '#3b82f6', color: '#fff', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('btn.kaydet')}</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Sözleşme Hareketleri Modal ── */}
      {showHareketlerModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowHareketlerModal(false)}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: 720, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.28)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px 14px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-history" style={{ color: '#16a34a', fontSize: 15 }}></i>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{t('sozlesmeDetay.sozlesmeHareketleri', 'Sözleşme Hareketleri')}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{sozlesme?.sozlesmeTakipNo}</div>
                </div>
              </div>
              <button onClick={() => setShowHareketlerModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}>×</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {hareketlerLoading ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, marginBottom: 12, display: 'block' }}></i>
                  {t('msg.yukleniyor', 'Yükleniyor...')}
                </div>
              ) : hareketler.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
                  <i className="fas fa-history" style={{ fontSize: 32, marginBottom: 12, display: 'block', opacity: 0.3 }}></i>
                  {t('sozlesmeDetay.hareketYok', 'Bu sözleşme için kayıt bulunamadı')}
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* Vertical line */}
                  <div style={{ position: 'absolute', left: 17, top: 0, bottom: 0, width: 2, background: '#e5e7eb', zIndex: 0 }} />

                  {hareketler.map((h: any, idx: number) => {
                    const tipi = (h.islemTipi || '').toLowerCase();
                    const tablo = h.tabloDegisiklik || '';

                    const islemRenk = tipi === 'create' ? '#16a34a'
                      : tipi === 'update' ? '#2563eb'
                      : tipi === 'delete' ? '#dc2626'
                      : '#6b7280';
                    const islemBg = tipi === 'create' ? '#f0fdf4'
                      : tipi === 'update' ? '#eff6ff'
                      : tipi === 'delete' ? '#fef2f2'
                      : '#f9fafb';
                    const islemIkon = tipi === 'create' ? 'fa-plus'
                      : tipi === 'update' ? 'fa-pencil-alt'
                      : tipi === 'delete' ? 'fa-trash'
                      : 'fa-circle';
                    const islemEtiket = tipi === 'create' ? t('hareket.olusturuldu', 'Oluşturuldu')
                      : tipi === 'update' ? t('hareket.guncellendi', 'Güncellendi')
                      : tipi === 'delete' ? t('hareket.silindi', 'Silindi')
                      : h.islemTipi || t('hareket.islem', 'İşlem');

                    const tabloEtiket: Record<string, string> = {
                      'Sozlesmeler': 'Sözleşme',
                      'OdemePlanTaksitleri': 'Ödeme Planı',
                      'CariOdemePlanlari': 'Ödeme Planı',
                      'Odemeler': 'Tahsilat',
                      'Cariler': 'Müşteri',
                    };

                    const tarih = h.islemTarihi ? new Date(h.islemTarihi) : null;
                    const tarihStr = tarih
                      ? tarih.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })
                      : '—';
                    const saatStr = tarih
                      ? tarih.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                      : '';

                    return (
                      <div key={h.logID ?? idx} style={{ display: 'flex', gap: 14, marginBottom: 18, position: 'relative', zIndex: 1 }}>
                        {/* Icon dot */}
                        <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: '50%', background: islemBg, border: `2px solid ${islemRenk}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={`fas ${islemIkon}`} style={{ color: islemRenk, fontSize: 13 }}></i>
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, background: '#fafafa', border: '1px solid #f1f5f9', borderRadius: 10, padding: '10px 14px', minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{h.islemDetayi || '—'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                              {tablo && tabloEtiket[tablo] && (
                                <span style={{ fontSize: 10, fontWeight: 600, background: '#f1f5f9', color: '#475569', borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                                  {tabloEtiket[tablo]}
                                </span>
                              )}
                              <span style={{ fontSize: 10, fontWeight: 700, background: islemBg, color: islemRenk, border: `1px solid ${islemRenk}22`, borderRadius: 5, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                                {islemEtiket}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <i className="fas fa-clock" style={{ fontSize: 10 }}></i>
                              {tarihStr}{saatStr ? ` — ${saatStr}` : ''}
                            </span>
                            {(h.kullanici?.adSoyad || h.kullanici?.adiSoyadi) && (
                              <span style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <i className="fas fa-user" style={{ fontSize: 10 }}></i>
                                {h.kullanici.adSoyad || h.kullanici.adiSoyadi}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{hareketler.length > 0 ? `${hareketler.length} ${t('sozlesmeDetay.kayit', 'kayıt')}` : ''}</span>
              <button onClick={() => setShowHareketlerModal(false)} style={{ padding: '7px 20px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {t('btn.kapat', 'Kapat')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SozlesmeDetay;